// Estimating Module — Phase 1
// Admin/leadership only. Gated by app_settings.estimating_enabled = 'true'.
// Matches Projects.jsx patterns: inline edit forms, styles.* from UI.jsx, fmtMoney.

import { useEffect, useState, useCallback, useRef, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth, isAdminLike } from '../context/AuthContext';
import { styles, colors, fmtMoney, fmtDate, fmtDateTime, reqDisplayTitle } from '../components/UI';
import { LastQuoted, PriceHistoryModal, usePriceHints } from '../components/PriceHistory';
import RevisionDrawer from '../components/RevisionDrawer';
import BlurDateInput from '../components/BlurDateInput';
import { buildCrewSlots, SECTION_ORDER, WD_LABEL, mdDate } from '../components/CrewEditor';
import DropZone from '../components/DropZone';
import MsaSheetEditor from '../components/MsaSheetEditor';
import { msaFuzzyMatch } from '../utils/msaMatch';
import EstimatingGuide from '../components/EstimatingGuide';
import { WORK_DAYS_OPTIONS, phaseHoursPreview, craftRollup, dayCountISO, endFromDays, peakHeadcount, positionsFor } from '../components/laborHours';
import { csvSafeCell } from '../utils/csvSafe';

const ORANGE = '#E36F2C';
const TIERS  = [50, 70, 100];

// Estimate status colors — the Status control was easy to lose in the header
// (George 2026-07-02), so the select + the v-tag render in status color.
// Draft is amber (action pending), not gray, so it's never invisible.
const EST_STATUS_COLOR = {
  Draft:     { bg: '#FEF3C7', fg: '#92400E', border: '#F59E0B' },
  Submitted: { bg: '#DBEAFE', fg: '#1E40AF', border: '#3B82F6' },
  Approved:  { bg: '#D1FAE5', fg: '#065F46', border: '#10B981' },
  Rejected:  { bg: '#FEE2E2', fg: '#991B1B', border: '#EF4444' },
};
const EST_STATUSES = ['Draft', 'Submitted', 'Approved', 'Rejected'];
// Sentinel value in the project picker: a deal with no Project Tracker
// opportunity yet. Everything downstream (quote PDF push, audit trail, ops
// handoff) anchors on the opportunity, so standalone CREATES one rather than
// running an estimate without it.
const STANDALONE = '__standalone__';
// Win % is the salesperson's number and is never defaulted (George 2026-09-23:
// "sales sets the win chance"); the standalone form requires a whole 0-100,
// the same rule the server enforces (server/utils/winPct.js winPctError).
const isWinPct = (v) => /^\d{1,3}$/.test(String(v ?? '').trim()) && Number(v) <= 100;
// SSS standard mileage rate — paid AND billed (a wash). New labor lines
// default to this; the per-line field overrides it for one-offs.
// Tracks the IRS standard mileage rate: 0.725 → 0.76 (George 2026-08-11).
// It moves roughly yearly, so this is the ONE place it is written — every
// button label below reads it, and an estimate stores its own rate per line,
// so a bump here never touches pricing that is already out the door.
const STANDARD_MILEAGE_RATE = 0.76;
const STANDARD_MILEAGE_LABEL = `$${STANDARD_MILEAGE_RATE}`;
const HPD_PRESETS = [8, 10, 12];
const OT_RULES = [
  { value: 'daily_over_8',   label: 'Daily >8 hrs' },
  { value: 'weekly_over_40', label: 'Weekly >40 hrs' },
  { value: 'all_st',         label: 'All Straight Time' },
];
const LABOR_TYPES = ['Standard', 'Fixed', 'Subcontractor'];
const EQUIP_TYPES = ['Utilization', 'Non-Consignment', 'Third-Party'];
const PHASES      = ['Pre-TA', 'Mechanical', 'Post-TA'];

const pct = (v) => `${(Number(v) * 100).toFixed(2)}%`;
const mult = (v) => `(×${(1 + (Number(v) || 0)).toFixed(4)})`;
// Small (?) help glyph — matches the Dashboard/Summary hover-help pattern.
const HelpGlyph = ({ help }) => (
  <span title={help} aria-label="help" style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 14, height: 14, borderRadius: '50%', border: '1px solid #9CA3AF',
    color: '#6B7280', fontSize: 9, fontWeight: 700, cursor: 'help',
    marginLeft: 3, verticalAlign: 'middle' }}>?</span>
);
const status_color = (s) => s === 'OK' ? colors.green : ORANGE;
// Gray "unburdened" companion under a burdened Margin/Markup cell — the same
// bill measured against the PAY rate instead of our burdened cost (the
// wage-multiplier view). Renders nothing when both values are null, which is
// how Subcontractor lines show a single figure: the sub's rate already IS our
// true cost, so there is no second view to draw (calcEstimate nulls them).
const Unburdened = ({ st, ot, title }) => (
  (st == null && ot == null) ? null : (
    <div title={title} style={{ marginTop: 3, paddingTop: 2, borderTop: '1px dotted #E5E7EB',
      fontWeight: 400, fontSize: 10, color: '#9CA3AF' }}>
      <div style={{ fontSize: 9 }}>unburdened</div>
      {st != null ? <div>ST {pct(st)}</div> : null}
      {ot != null ? <div>OT {pct(ot)}</div> : null}
    </div>
  )
);

// Subcontractor markup the line is HOLDING: the % typed in the Markup column
// (`subcontractor_markup`), else the markup implied by an existing bill over
// the sub cost. Null when neither exists (nothing to hold). A hand-typed bill
// rate nulls the stored value so the implied figure takes over — the same
// cost/bill/markup triangle the Consumables tab uses.
const subMarkupOf = (line) => {
  if (line.subcontractor_markup != null && line.subcontractor_markup !== '') {
    const m = Number(line.subcontractor_markup);
    if (Number.isFinite(m) && m >= 0) return m;
  }
  const pay = Number(line.pay_st) || 0, bill = Number(line.bill_st) || 0;
  return pay > 0 && bill > pay ? Math.round(((bill - pay) / pay) * 10000) / 10000 : null;
};
// OT side of the same triangle. The OT rate can carry its OWN markup (typed
// in the OT % box); otherwise it follows the ST markup. Implied first —
// (bill_ot − pay_ot) ÷ pay_ot — so an OT override survives a Pay OT edit.
const subOtMarkupOf = (line) => {
  const pay = Number(line.pay_ot) || 0, bill = Number(line.bill_ot) || 0;
  if (pay > 0 && bill > pay) return Math.round(((bill - pay) / pay) * 10000) / 10000;
  return subMarkupOf(line);
};
// Sub Pay OT defaults to 1.5× Pay ST (the usual sub agreement) and keeps
// FOLLOWING ST while it still equals that, so one cost entry configures the
// whole line. A hand-typed OT rate that differs from 1.5× is left alone.
const subOtFollowsSt = (line) => {
  const st = Number(line.pay_st) || 0, ot = Number(line.pay_ot) || 0;
  return !(ot > 0) || Math.abs(ot - Math.round(st * 150) / 100) < 0.005;
};

export default function Estimating() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects,    setProjects]    = useState([]);
  const [estimates,   setEstimates]   = useState([]);
  const [current,     setCurrent]     = useState(null);   // loaded estimate with computed
  const [error,       setError]       = useState('');
  const [disabled,    setDisabled]    = useState(false);  // module disabled flag

  // Pickers
  const [selProjectId, setSelProjectId] = useState('');
  const [catalog,       setCatalog]     = useState([]);
  const [catalogQ,      setCatalogQ]    = useState('');
  const [crafts,        setCrafts]      = useState([]);   // active crafts → labor craft picker

  // Expanded rows
  const [expandedPhase, setExpandedPhase] = useState(null); // labor line id
  const [expandedEquipPhase, setExpandedEquipPhase] = useState(null); // equipment line id
  const [showRollup, setShowRollup] = useState(false); // craft rollup card on Labor tab (collapsed by default)
  const [selectedLabor, setSelectedLabor] = useState(() => new Set()); // bulk-edit selection
  const [copyPanel, setCopyPanel] = useState(null); // { sourceId } when the copy-to-selected panel is open

  // Active section
  const [section, setSection] = useState('labor'); // labor | equipment | consumables | summary

  // Loading flag
  const [saving, setSaving] = useState(false);
  // Auto-save indicator — flashes briefly after every successful save
  const [savedAt, setSavedAt] = useState(null);
  // Revision history drawer
  const [showRevisions, setShowRevisions] = useState(false);
  // Numbered revisions / quotes / audit history
  const [showSuperseded, setShowSuperseded] = useState(false);
  // All Estimates list filters
  const [estFilterName, setEstFilterName] = useState('');
  const [estFilterStatus, setEstFilterStatus] = useState('');
  const [estFilterSalesperson, setEstFilterSalesperson] = useState('');
  const [estFilterStart, setEstFilterStart] = useState(''); // start date on/after
  const [quotes, setQuotes] = useState([]);
  const [estHistory, setEstHistory] = useState([]);
  // MSA rate sheet (leadership verify panel)
  const [msaSheet, setMsaSheet] = useState(null);
  // Bumped to force the consumable Markup % cells to re-seed from data (e.g. after
  // "Apply to all"). A per-line markup edit does NOT bump it, so the value you
  // type stays put instead of snapping to the rounding-derived markup.
  const [consNonce, setConsNonce] = useState(0);
  // Submitted requests waiting for an estimate (queue surfaced here)
  const [waiting, setWaiting] = useState([]);
  // Manual "push to opportunity" backfill (estimate value + quote PDF)
  const [pushing, setPushing] = useState(false);
  const [pushMsg, setPushMsg] = useState(null); // { ok, text } | null
  const [resyncing, setResyncing] = useState(false);
  const [reserving, setReserving] = useState(false);
  const [reserveMsg, setReserveMsg] = useState(null); // { ok, text } | null
  const [pulling, setPulling] = useState(false);
  const [pullMsg, setPullMsg] = useState(null); // { ok, text } | null
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null); // { ok, text } | null
  const [showImport, setShowImport] = useState(false); // workbook import modal
  // Duplicate this estimate into a new one (own chain, new dates, any opportunity).
  const [dupOpen, setDupOpen] = useState(false);
  const [dupMsg, setDupMsg] = useState(null);
  // Request from Sales: { flags, owner_id, note, catalog, saving, error } | null
  const [reqAsk, setReqAsk] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);   // full audit-trail modal
  const [auditData, setAuditData] = useState(null);    // { jobLabel, customer, opportunity, rows } | null
  // Standalone estimate (no request, no opportunity yet) — creates the
  // Project Tracker opportunity on the fly, then the estimate against it.
  const [relinking, setRelinking] = useState(false);
  const [standalone, setStandalone] = useState(null);  // { site, branch_id, account_owner_id, date_planned, win_pct } | null
  const [branches, setBranches] = useState([]);
  const [reps, setReps] = useState([]);
  const waitingRef = useRef(null);   // ⏳ Waiting for Estimate panel — the New requests tile jumps here
  // Price memory: what we quoted this craft / CAT-CLASS / part before, and
  // whether we WON with it. ONE request per estimate (not per line); advisory
  // only, so a failure never blocks pricing. The line-count nonce refetches
  // after you add or remove lines.
  const [priceModal, setPriceModal] = useState(null);   // { kind, key, label } | null
  const priceHints = usePriceHints(current?.id,
    (current?.labor_lines?.length || 0) + (current?.equipment_lines?.length || 0) + (current?.consumable_lines?.length || 0));
  const openPrice = (kind, lineId, label) => {
    const h = priceHints?.[kind]?.[lineId];
    if (h) setPriceModal({ kind, key: h.key, label });
  };

  // ---- Boot ----------------------------------------------------------------
  useEffect(() => {
    api.lookupCrafts().then(setCrafts).catch(() => {});
    // Standalone-opportunity form pickers.
    api.listBranches().then(setBranches).catch(() => {});
    api.listReps().then(setReps).catch(() => {});
  }, []);

  // Project picker: only opportunities that can still need an estimate
  // (Lost/Closed/Complete are done), alphabetical so they're findable.
  const loadProjects = useCallback(() => {
    api.listProjects({})
      .then(rows => setProjects(
        (rows || [])
          .filter(p => !['Lost', 'Closed', 'Complete'].includes(p.status))
          .sort((a, b) => String(a.site || '').localeCompare(String(b.site || '')))
      ))
      .catch(() => {});
  }, []);
  useEffect(() => { loadProjects(); }, [loadProjects]);

  const loadEstimates = useCallback(() => {
    api.listEstimates(showSuperseded ? { all_revisions: 'true' } : {}).then(setEstimates).catch(err => {
      if (err.message.includes('not enabled') || err.message.includes('403')) {
        setDisabled(true);
      } else setError(err.message);
    });
    // Submitted requests with no estimate yet — the work queue. Ops users
    // are denied the requests routes; hide the panel for them quietly.
    api.listRequests({ status: 'Submitted' }).then(setWaiting).catch(() => setWaiting([]));
  }, [showSuperseded]);
  useEffect(() => { loadEstimates(); }, [loadEstimates]);

  // Distinct salespeople present in the current estimates list (for the filter dropdown).
  const salespeople = useMemo(
    () => [...new Set(estimates.map(e => e.salesperson).filter(Boolean))].sort(),
    [estimates]
  );
  // Every filter EXCEPT status. The status tiles count off this set, so clicking
  // a tile never changes the numbers you just clicked.
  const baseEstimates = useMemo(() => {
    const name = estFilterName.trim().toLowerCase();
    return estimates.filter(e => {
      if (name && !String(e.project_site || '').toLowerCase().includes(name)) return false;
      if (estFilterSalesperson && e.salesperson !== estFilterSalesperson) return false;
      if (estFilterStart && !(e.start_date && e.start_date >= estFilterStart)) return false;
      return true;
    });
  }, [estimates, estFilterName, estFilterSalesperson, estFilterStart]);
  // Client-side filtered estimates for the All Estimates list.
  const filteredEstimates = useMemo(
    () => baseEstimates.filter(e => !estFilterStatus || e.status === estFilterStatus),
    [baseEstimates, estFilterStatus]
  );
  const statusCounts = useMemo(() => {
    const counts = Object.fromEntries(EST_STATUSES.map(s => [s, 0]));
    baseEstimates.forEach(e => { if (counts[e.status] != null) counts[e.status]++; });
    return counts;
  }, [baseEstimates]);
  const estFiltersActive = estFilterName || estFilterStatus || estFilterSalesperson || estFilterStart;

  // Deep link: /estimating?open=<id> (e.g. View Estimate from the Requests page)
  useEffect(() => {
    const open = Number(new URLSearchParams(window.location.search).get('open'));
    if (open) reload(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handoff: create the estimate from a waiting request (copies lines +
  // target rates, links the chain) and open it.
  const estimateFromRequest = (r) => {
    doSave(async () => {
      const result = await api.createEstimateFromRequest(r.id);
      await reload(result.estimate_id);
      setSection('labor');
    });
  };

  // Lazy-load quotes / audit history when their tab opens.
  useEffect(() => {
    if (!current) return;
    if (section === 'quote') {
      api.listQuotes({ estimate_root: current.root_id || current.id, all_revisions: 'true' })
        .then(setQuotes).catch(() => setQuotes([]));
    }
    if (section === 'history') {
      api.getEstimateHistory(current.id).then(setEstHistory).catch(() => setEstHistory([]));
    }
    // Labor/equipment grids use the sheet too (MSA wording picker), not just the MSA tab.
    if (['msa', 'labor', 'equipment', 'thirdparty'].includes(section)) {
      api.getMsaSheetForEstimate(current.id).then(setMsaSheet).catch(() => setMsaSheet(null));
    }
  // status too: Submitting auto-creates the Draft quote server-side, so a
  // Quote tab opened before the submit showed "No quote yet" and Create then
  // 400'd "already exists" (EST-102, 2026-09-24).
  }, [section, current?.id, current?.status]);

  const loadCatalog = useCallback(() => {
    api.listEquipmentCatalog(catalogQ).then(setCatalog).catch(() => {});
  }, [catalogQ]);
  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // ---- Helpers -------------------------------------------------------------
  const reload = async (id) => {
    try {
      const est = await api.getEstimate(id);
      setCurrent(est);
      loadEstimates();
    } catch (err) { setError(err.message); }
  };

  // An opportunity's site name is NOT unique -- the same customer routinely
  // has two open bids on the same site. Every opportunity picker and the
  // estimate header use this label so the row is identifiable at a glance.
  const oppLabel = (p) => p ? [
    `#${p.id}`, p.site,
    p.branch_name || p.branch_code, fmtDate(p.date_planned), p.status
  ].filter(Boolean).join(' · ') : '';

  // Relink the whole estimate chain (revisions + its change orders) to a
  // different opportunity, then revalue both sides.
  const relink = async (projectId) => {
    if (!current || !projectId) return;
    const target = projects.find(p => String(p.id) === String(projectId));
    if (!confirm(`Move EST-${current.root_id || current.id}-${current.revision || 0} — and every revision and change order in its chain — to:

${oppLabel(target)}

Both opportunities are revalued from their approved estimates.`)) return;
    setRelinking(false);
    try {
      const r = await api.relinkEstimate(current.id, Number(projectId));
      await reload(current.id);
      loadProjects();
      if (r.from_stale && r.from_project_id) {
        alert(`Moved ${r.moved} estimate row(s). Opportunity #${r.from_project_id} has no approved estimate left, so its forecast value was left as-is — check it in the Project Tracker.`);
      }
    } catch (err) { setError(err.message); }
  };

  const doSave = async (fn) => {
    setSaving(true);
    setError('');
    try {
      await fn();
      setSavedAt(new Date());
    }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ---- CSVM vendor response (lives on the request chain) --------------------
  // PO # and vendor quote edits go through the requests router; the estimate
  // just displays the chain's current values (current.csvm_vendor).
  const saveCsvmVendor = (patch) => {
    if (!current?.csvm_vendor) return;
    doSave(async () => {
      await api.updateRequestCsvmVendor(current.csvm_vendor.request_id, patch);
      await reload(current.id);
    });
  };
  const uploadCsvmQuoteFile = (file) => {
    if (!file || !current?.csvm_vendor) return;
    doSave(async () => {
      const up = await api.uploadRequestFile(current.csvm_vendor.request_id, file);
      await api.updateRequestCsvmVendor(current.csvm_vendor.request_id, { quote_attachment_id: up.id });
      await reload(current.id);
    });
  };

  // ---- Change-order link (2026-08-13, George — Cenovus Lima CO) -------------
  // A CO is a separate estimate chain on the same opportunity billing ON TOP of
  // the base: approval ADDS its total to the opportunity value instead of
  // replacing it, and the FS forecast feed ships base + approved COs. Draft-only
  // link/unlink; the server stores the base chain's ROOT id.
  const coBaseOptions = useMemo(() => {
    if (!current) return [];
    const selfRoot = current.root_id || current.id;
    return estimates.filter(e =>
      e.project_id === current.project_id
      && e.is_current !== false
      && e.change_order_of_estimate_id == null
      && (e.root_id || e.id) !== selfRoot
    );
  }, [estimates, current?.id, current?.project_id, current?.root_id]);

  const setChangeOrderLink = (targetId) => {
    if (!current) return;
    doSave(async () => {
      await api.setEstimateChangeOrder(current.id, targetId);
      await reload(current.id);
    });
  };

  // ---- Delete estimate ------------------------------------------------------
  // Deleting an estimate never takes the opportunity with it by default — the
  // opportunity usually pre-dates the estimate and anchors the request chain, the
  // AM's activity, files, and crew. When nothing else is attached (the standalone
  // case: the opportunity exists only to host this estimate) we offer to delete it
  // too; otherwise the first confirm says why it's staying.
  const deleteEstimate = async (id, label) => {
    let usage = null;
    try { usage = await api.estimateOpportunityUsage(id); } catch { /* advisory only */ }
    const tail = !usage ? ''
      : usage.bare ? `\n\nThe opportunity "${usage.site}" has nothing else attached to it.`
      : `\n\nThe opportunity "${usage.site}" will be kept — ${usage.blockers.join(', ')} still reference it.`;
    if (!confirm(`Delete estimate "${label}"? This cannot be undone.${tail}`)) return;
    const alsoOpportunity = !!(usage && usage.bare) &&
      confirm(`Also delete the opportunity "${usage.site}" from the Project Tracker?\n\nOK = delete both.  Cancel = keep the opportunity.`);
    doSave(async () => {
      const r = await api.deleteEstimate(id, alsoOpportunity);
      if (current?.id === id) setCurrent(null);
      if (alsoOpportunity && r?.opportunity && !r.opportunity.deleted) {
        alert(`Estimate deleted. The opportunity "${r.opportunity.site}" was kept — ${r.opportunity.blockers.join(', ')} reference it.`);
      }
      if (alsoOpportunity) {
        api.listProjects({})
          .then(rows => setProjects((rows || [])
            .filter(p => !['Lost', 'Closed', 'Complete'].includes(p.status))
            .sort((a, b) => String(a.site || '').localeCompare(String(b.site || '')))))
          .catch(() => {});
      }
      loadEstimates();
    });
  };

  // ---- Full audit trail (request -> estimate -> quote -> PO) ----------------
  const openAudit = async () => {
    if (!current) return;
    setAuditOpen(true); setAuditData(null);
    try { setAuditData(await api.getEstimateAuditTrail(current.id)); }
    catch (e) { setError(e.message); setAuditOpen(false); }
  };

  // ---- Workbook export (4-sheet .xlsx of the current estimate's lines) ------
  const exportWorkbook = async () => {
    if (!current) return;
    try { await api.exportEstimateWorkbook(current.id); }
    catch (e) { setError(e.message); }
  };

  // ---- Billed summary export (consolidated bill-side .xlsx) ------------------
  const exportBilledWorkbook = async () => {
    if (!current) return;
    try { await api.exportBilledWorkbook(current.id); }
    catch (e) { setError(e.message); }
  };

  // ---- Personnel (submittal manpower Gantt) ---------------------------------
  const savePersonnel = (personnel) =>
    doSave(async () => { const est = await api.updatePersonnel(current.id, personnel); setCurrent(est); });
  const exportPersonnelGantt = async () => {
    if (!current) return;
    try { await api.exportPersonnelGantt(current.id); }
    catch (e) { setError(e.message); }
  };
  const exportOrgChart = async () => {
    if (!current) return;
    try { await api.exportOrgChart(current.id); }
    catch (e) { setError(e.message); }
  };

  // ---- Resync the linked job's Operations staffing plan from this estimate ---
  const resyncCrewPlan = async () => {
    if (!current || resyncing) return;
    setResyncing(true); setPushMsg(null); setError('');
    try {
      const r = await api.resyncCrewPlan(current.id);
      setPushMsg({ ok: true, text: `Crew plan synced to Operations — ${r.crafts} craft requirement(s)` +
        (r.assigned ? `, ${r.assigned} crew assigned from Personnel.` : '.') });
    } catch (e) {
      setPushMsg({ ok: false, text: e.message });
    } finally { setResyncing(false); }
  };

  // ---- Reserve this estimate's equipment in Inventory (pre-finalization) -----
  // Soft hold for warehouse planning — not a pull. Converts to a real pull list
  // when the deal is won and pushed from Operations.
  const reserveEquipment = async () => {
    if (!current || reserving) return;
    if (!window.confirm(
      "Reserve this estimate's equipment in the Inventory app?\n\n" +
      'This is a SOFT HOLD for warehouse availability planning — it does NOT pull or stage gear. ' +
      'It converts to a real pull list automatically when the deal is won and pushed from Operations.'
    )) return;
    setReserving(true); setReserveMsg(null); setError('');
    try {
      const r = await api.reserveEstimateEquipment(current.id);
      setReserveMsg({ ok: true, text: `Reserved ${r.line_count} equipment line(s) in Inventory as ${r.reservation_key} (rev ${r.push_seq}).` });
    } catch (e) {
      setReserveMsg({ ok: false, text: e.message });
    } finally { setReserving(false); }
  };

  // ---- Push a REAL pull list (bid won, PO/scope still pending) --------------
  // Pullable in Inventory immediately; later pushes supersede-with-delta and
  // never erase already-pulled barcodes. Needs a job number on the opportunity.
  const pushPullList = async () => {
    if (!current || pulling) return;
    if (!window.confirm(
      "Push this estimate's equipment to Inventory as a REAL PULL LIST?\n\n" +
      'The warehouse can pull and scan barcodes immediately — use this once the bid is won but before you flip the deal to Won. ' +
      'The opportunity must have a job number. Later changes re-push and update the SAME list without erasing already-pulled barcodes.'
    )) return;
    setPulling(true); setPullMsg(null); setError('');
    try {
      const r = await api.pushEstimatePullList(current.id);
      setPullMsg({ ok: true, text: `Pushed ${r.line_count} equipment line(s) to Inventory as pull list ${r.job_number} (rev ${r.push_seq}). Pull + scan now in Inventory.` });
    } catch (e) {
      setPullMsg({ ok: false, text: e.message });
    } finally { setPulling(false); }
  };

  // ---- Manual status email (automatic estimate emails are switched OFF) -----
  // Submitted → ready-for-approval email to the notify list; Approved/Rejected
  // → decision email to the AM. Previews the recipients, confirms, then sends.
  const sendStatusEmail = async () => {
    if (!current || sendingEmail) return;
    setSendingEmail(true); setEmailMsg(null); setError('');
    try {
      const p = await api.sendEstimateStatusEmail(current.id, { preview: true });
      const label = p.kind === 'approval_request' ? 'ready-for-approval' : `${current.status} decision`;
      if (!p.to) { setEmailMsg({ ok: false, text: p.reason || 'No recipients configured.' }); return; }
      if (!window.confirm(
        `Send the ${label} email for EST-${current.root_id || current.id}-${current.revision || 0} to:\n\n${p.to}\n\nYou are CC'd on the send.`
      )) return;
      const r = await api.sendEstimateStatusEmail(current.id);
      setEmailMsg(r.sent
        ? { ok: true, text: `${label} email sent to ${r.to}.` }
        : { ok: false, text: r.reason || 'Not sent.' });
    } catch (e) {
      setEmailMsg({ ok: false, text: e.message });
    } finally { setSendingEmail(false); }
  };

  // ---- Revise (clone to revision N+1) ---------------------------------------
  const reviseEstimate = () => {
    if (!current) return;
    if (!confirm(`Create revision -${Number(current.revision || 0) + 1} of this estimate?\n\nThe current revision freezes as the record of what was priced; the new one opens as an editable Draft.`)) return;
    doSave(async () => {
      const est = await api.reviseEstimate(current.id);
      setCurrent(est);
      loadEstimates();
    });
  };

  // ---- Push approved estimate value + quote PDF → opportunity ---------------
  // Backfill for estimates approved before the auto-sync shipped (2026-06-15).
  // Idempotent server-side; safe to click again.
  const pushToOpportunity = async () => {
    if (!current || pushing) return;
    setPushing(true);
    setPushMsg(null);
    setError('');
    try {
      const r = await api.pushEstimateToOpportunity(current.id);
      const parts = [];
      let ok = true;
      if (r.value_updated) parts.push(`Opportunity value set to ${fmtMoney(r.total)}`);
      if (r.quote_attached) parts.push('quote PDF attached');
      else if (r.quote_reason === 'already_attached') parts.push('quote PDF already on file');
      else if (r.quote_reason === 'newer_revision_filed') parts.push('a newer quote revision is already on file');
      else if (r.quote_reason === 'no_quote') parts.push('no quote yet — issue one from the Quote tab');
      else if (r.quote_reason === 'r2_unconfigured') parts.push('file storage off — PDF skipped');
      // Genuine failures: surface them and turn the banner red so leadership
      // doesn't read a partial failure as full success.
      else if (r.quote_reason === 'error') { parts.push('quote PDF failed to attach — try again'); ok = false; }
      else if (r.quote_reason === 'no_opportunity') { parts.push('quote not linked to this opportunity — PDF skipped'); ok = false; }
      setPushMsg({ ok, text: parts.length ? parts.join(' · ') : 'Nothing to push.' });
    } catch (err) {
      setPushMsg({ ok: false, text: err.message });
    } finally {
      setPushing(false);
    }
  };

  // ---- Create estimate ----------------------------------------------------
  const createEstimate = async () => {
    if (!selProjectId) return;
    if (selProjectId === STANDALONE) {
      setError('');   // the modal shows the page error; don't carry a stale one in
      setStandalone({ site: '', branch_id: '', account_owner_id: '', date_planned: '', win_pct: '', revenue_type: '' });
      return;
    }
    doSave(async () => {
      const est = await api.createEstimate({ project_id: Number(selProjectId) });
      setCurrent(est);
      // Refresh the list so a just-created base shows up in the CO picker's
      // coBaseOptions (built off `estimates`) without a page reload.
      loadEstimates();
    });
  };

  // Standalone deal: create the opportunity first (status Bidding), then the
  // estimate on it, so the quote PDF / audit trail / ops handoff all still work.
  const createStandalone = () => {
    const site = (standalone?.site || '').trim();
    if (!site || !isWinPct(standalone.win_pct) || !standalone.revenue_type) return;
    doSave(async () => {
      const proj = await api.createProject({
        site,
        branch_id: standalone.branch_id || null,
        account_owner_id: standalone.account_owner_id || null,
        date_planned: standalone.date_planned || null,
        status: 'Bidding',
        win_pct: Number(standalone.win_pct),
        revenue_type: standalone.revenue_type,
      });
      const est = await api.createEstimate({ project_id: proj.id });
      setProjects(ps => [...ps, proj].sort((a, b) => String(a.site || '').localeCompare(String(b.site || ''))));
      setSelProjectId(String(proj.id));
      setStandalone(null);
      setCurrent(est);
      setSection('labor');
      loadEstimates();
    });
  };

  // ---- Header edits -------------------------------------------------------
  const updateHeader = (field, value) => {
    doSave(async () => { await reload((await api.updateEstimate(current.id, { [field]: value })).id); });
  };

  // ---- Markup -------------------------------------------------------------
  const saveMarkup = (lines) => {
    doSave(async () => {
      const est = await api.updateEstimateMarkup(current.id, lines);
      setCurrent(est);
    });
  };

  // ---- Labor --------------------------------------------------------------
  const addLaborLine = () => {
    doSave(async () => {
      const est = await api.addLaborLine(current.id, { craft: '', labor_type: 'Standard', qty_positions: 1,
        mob_cost_rate: STANDARD_MILEAGE_RATE, mob_bill_rate: STANDARD_MILEAGE_RATE });
      setCurrent(est);
    });
  };

  const updateLabor = (lineId, body) => {
    doSave(async () => {
      const est = await api.updateLaborLine(lineId, body);
      setCurrent(est);
    });
  };

  const deleteLabor = (lineId) => {
    if (!confirm('Delete labor line?')) return;
    doSave(async () => {
      const est = await api.deleteLaborLine(lineId);
      setCurrent(est);
    });
  };

  const savePhases = (lineId, phases) => {
    doSave(async () => {
      const est = await api.updateLaborPhases(lineId, phases);
      setCurrent(est);
      setExpandedPhase(null);
    });
  };
  const saveEquipPhases = (lineId, phases) => {
    doSave(async () => {
      const est = await api.updateEquipmentPhases(lineId, phases);
      setCurrent(est);
      setExpandedEquipPhase(null);
    });
  };

  // ---- Bulk labor edit (copy-to-selected + standard mileage) --------------
  const toggleLaborSel = (lineId) => setSelectedLabor(prev => {
    const next = new Set(prev);
    next.has(lineId) ? next.delete(lineId) : next.add(lineId);
    return next;
  });
  const applyBulkLabor = (body) => {
    doSave(async () => {
      const est = await api.bulkApplyLabor(current.id, body);
      setCurrent(est);
      setCopyPanel(null);
    });
  };

  // ---- Equipment ----------------------------------------------------------
  const addEquipment = (catalogItem) => {
    doSave(async () => {
      const est = await api.addEquipmentLine(current.id, {
        description: catalogItem.description,
        category: catalogItem.category,
        cat_class: catalogItem.cat_class,
        equip_type: catalogItem.default_type || 'Utilization',
        catalog_id: catalogItem.id,
        qty: 1, num_days: 1,
        day_rate: catalogItem.default_day_rate || 0,
      });
      setCurrent(est);
    });
  };

  const updateEquip = (lineId, body) =>
    doSave(async () => { const est = await api.updateEquipmentLine(lineId, body); setCurrent(est); });
  // Carry one line's Start/End (and derived Days) down to every equipment line.
  const applyEquipDatesToAll = (srcLine) => {
    const start = srcLine.start_date ? String(srcLine.start_date).slice(0, 10) : '';
    const end = srcLine.end_date ? String(srcLine.end_date).slice(0, 10) : '';
    if (!start || !end) { alert("Set this line's Start and End dates first."); return; }
    const ids = (current.equipment_lines || []).filter(l => l.equip_type !== 'Third-Party').map(l => l.id);
    if (!ids.length) return;
    if (!confirm(`Apply ${start} → ${end} to all ${ids.length} equipment line(s)?`)) return;
    doSave(async () => {
      const est = await api.applyEquipmentDates(current.id, { start_date: start, end_date: end, line_ids: ids });
      setCurrent(est);
    });
  };

  const deleteEquip = (lineId) => {
    if (!confirm('Delete equipment line?')) return;
    doSave(async () => { const est = await api.deleteEquipmentLine(lineId); setCurrent(est); });
  };

  // Vendor quote-request email — no generated PDF (unlike CSVM), just the
  // line's description/qty/dates as plain text. Sender is CC'd server-side.
  const emailEquipVendor = (line) => {
    const to = prompt(`Vendor email address for "${line.description}" (comma-separate multiple).\nYou will be CC'd on the send:`);
    if (!to || !to.trim()) return;
    doSave(async () => {
      const r = await api.emailEquipmentVendor(line.id, to.trim());
      alert(`Quote request sent to ${r.to}${r.cc ? `\nCC: ${r.cc}` : ''}`);
    });
  };

  // Clone a line (same description / CAT-CLASS / rate) so a qty ramp up/down is
  // a new line with its own dates — keeps the CAT-CLASS intact for the Gantt.
  const duplicateEquip = (line) => {
    doSave(async () => {
      const est = await api.addEquipmentLine(current.id, {
        description: line.description, category: line.category, cat_class: line.cat_class,
        equip_type: line.equip_type, catalog_id: line.catalog_id || undefined,
        qty: line.qty, num_days: line.num_days,
        start_date: line.start_date || null, end_date: line.end_date || null,
        day_rate: line.day_rate, rate_unit: line.rate_unit || 'day',
        msa_description: line.msa_description || null, notes: line.notes || null,
      });
      setCurrent(est);
    });
  };

  // ---- Consumables --------------------------------------------------------
  const addConsumable = () => {
    doSave(async () => {
      const est = await api.addConsumableLine(current.id, { description: 'New item', qty: 1, bill_rate: 0, cost: 0 });
      setCurrent(est);
    });
  };

  const updateCons = (lineId, body) => {
    doSave(async () => { const est = await api.updateConsumableLine(lineId, body); setCurrent(est); });
  };

  const deleteCons = (lineId) => {
    if (!confirm('Delete consumable line?')) return;
    doSave(async () => { const est = await api.deleteConsumableLine(lineId); setCurrent(est); });
  };

  const emailConsVendor = (line) => {
    const to = prompt(`Vendor email address for "${line.description}" (comma-separate multiple).\nYou will be CC'd on the send:`);
    if (!to || !to.trim()) return;
    doSave(async () => {
      const r = await api.emailConsumableVendor(line.id, to.trim());
      alert(`Quote request sent to ${r.to}${r.cc ? `\nCC: ${r.cc}` : ''}`);
    });
  };

  // Markup-over-cost default for new/edited consumable lines (fraction, e.g. 0.15).
  const consMarkup = Number(current?.consumable_markup_pct) || 0;
  const applyConsMarkup = () => {
    const pct = (consMarkup * 100).toFixed(1).replace(/\.0$/, '');
    if (!confirm(`Set Bill Rate = Cost × (1 + ${pct}%) on every consumable line that has a cost? This overwrites their current bill rates.`)) return;
    doSave(async () => { const est = await api.applyConsumableMarkup(current.id, consMarkup); setCurrent(est); setConsNonce(n => n + 1); });
  };

  // Export the CURRENTLY FILTERED/SORTED consumable lines (what's on screen) as a
  // VENDOR RFQ — the file leaves the building, so it carries only what a vendor
  // needs to price the list, and blank columns for them to fill in and return.
  //
  // It deliberately carries NO cost, bill rate or line total. The first version
  // of this export did (2026-09-11, George asked for "a CSV to submit to a vendor
  // for pricing" and this button already existed): it put our Unit Cost, Extended
  // Cost, Bill Rate and Total in a file whose own tooltip said to send it to a
  // vendor, i.e. it handed the counterparty our cost basis AND our sell price
  // before they quoted. Never re-add those columns here. The internal view of the
  // same lines, cost and bill_rate included, is the ⬇ Workbook export, which is
  // also the round-trip import path — see services/estimateWorkbook.js.
  const exportConsumablesCsv = () => {
    // csvSafeCell runs FIRST (formula-injection guard: a description typed as
    // "=HYPERLINK(...)" must not evaluate when the vendor opens the file), then
    // the ordinary CSV quoting.
    const esc = (v) => {
      const s = v == null ? '' : String(csvSafeCell(v));
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    // Line # is our line_no, so a returned sheet can be matched back to the
    // estimate even after the vendor re-sorts or drops rows.
    const header = ['Line', 'Description', 'Part #', 'Category', 'Qty', 'Unit',
      'Quoted Unit Price', 'Quoted Extended', 'Vendor Part #', 'Lead Time', 'Vendor Notes'];
    const out = [header];
    for (const line of consCtl.view) {
      out.push([
        line.line_no ?? '',
        line.description || '',
        line.part_number || '',
        line.category || 'Consumable',
        Math.round(Number(line.qty) || 0),
        line.unit || '',
        '', '', '', '', '',   // vendor fills these in and sends it back
      ]);
    }
    const bom = String.fromCharCode(0xFEFF); // so Excel reads the file as UTF-8
    const csv = bom + out.map(cols => cols.map(esc).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const site = (current.project_site || '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    a.download = `RFQ-consumables-EST-${current.root_id || current.id}-${current.revision || 0}${site ? '-' + site : ''}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  // ---- Render --------------------------------------------------------------
  if (disabled) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: '#6B7280' }}>
          Estimating Module
        </div>
        <div style={{ color: '#9CA3AF', fontSize: 14 }}>
          This module is not currently enabled.<br />
          Ask admin to set <code>estimating_enabled = true</code> in Admin → Settings.
        </div>
      </div>
    );
  }

  // ---- Sort/filter controls for the estimate line tables ------------------
  // Hooks run unconditionally (rows default to [] when no estimate is open).
  const compL = (id) => (current?.computed?.laborLines || []).find(c => c.id === id) || {};
  const compE = (id) => (current?.computed?.equipmentLines || []).find(c => c.id === id) || {};
  const compC = (id) => (current?.computed?.consumableLines || []).find(c => c.id === id) || {};
  const shiftOf = (line) => {
    const ph = line.phases || [];
    const day = ph.some(p => (Number(p.day_hc) || 0) > 0);
    const night = ph.some(p => (Number(p.night_hc) || 0) > 0);
    return day && night ? 'Day+Night' : day ? 'Day' : night ? 'Night' : '';
  };
  // Phases a line actually staffs (HC > 0) — drives the Phase filter.
  const staffedPhases = (line) => (line.phases || [])
    .filter(p => ((Number(p.day_hc) || 0) + (Number(p.night_hc) || 0)) > 0)
    .map(p => p.phase);
  // Group key for craft sort: base craft (shift suffix stripped) + Day(0)/Night(1),
  // so every craft family blocks together and Day sorts before Night within it —
  // regardless of spacing/case in the typed name.
  const craftGroupKey = (line) => {
    const c = (line.craft || '').toLowerCase().trim();
    const night = /night/.test(c);
    const base = c.replace(/[\s(]*\b(?:day|night)\b\)?\s*$/, '').trim();
    return `${base}|${night ? 1 : 0}`;
  };

  const laborRows = current?.labor_lines || [];
  const laborCols = [
    { k: 'craft', sort: l => craftGroupKey(l), filter: { type: 'text', label: 'Craft' } },
    { k: 'labor_type', sort: l => l.labor_type || '', filter: { type: 'select', label: 'Type', options: LABOR_TYPES } },
    { k: 'shift', filter: { type: 'select', label: 'Shift', options: ['Day', 'Night'], get: shiftOf,
        // Phase-aware: when a Phase is also filtered, Day/Night checks the
        // headcount IN that phase (a night super is Day in Pre/Post but Night
        // in Mechanical). With no phase filter, match across all phases.
        match: (l, v, f) => {
          const ph = f && f.phase && (l.phases || []).find(p => p.phase === f.phase);
          if (f && f.phase) return ph ? (v === 'Day' ? (Number(ph.day_hc) || 0) > 0 : (Number(ph.night_hc) || 0) > 0) : false;
          return shiftOf(l).includes(v);
        } } },
    { k: 'phase', filter: { type: 'select', label: 'Phase', options: ['Pre-TA', 'Mechanical', 'Post-TA'], get: staffedPhases, match: (l, v) => staffedPhases(l).includes(v) } },
    { k: 'qty', sort: l => positionsFor(l) },
    { k: 'stHours', sort: l => Number(compL(l.id).stHours) || 0 },
    { k: 'otHours', sort: l => Number(compL(l.id).otHours) || 0 },
    { k: 'revenue', sort: l => Number(compL(l.id).laborRevenue) || 0 },
    { k: 'cost', sort: l => Number(compL(l.id).laborTrueCost) || 0 },
    { k: 'perdiem', sort: l => Number(compL(l.id).pdRevenue) || 0 },
    { k: 'mob', sort: l => Number(compL(l.id).mobRevenue) || 0 },
    { k: 'below', filter: { type: 'bool', label: 'Below margin', get: l => compL(l.id).belowThreshold } },
  ];
  const laborCtl = useTableControls(laborRows, laborCols);

  const equipRows = (current?.equipment_lines || []).filter(l => l.equip_type !== 'Third-Party');
  const equipCols = [
    { k: 'description', sort: l => l.description || '', filter: { type: 'text', label: 'Description' } },
    { k: 'cat_class', sort: l => l.cat_class || '' },
    { k: 'equip_type', sort: l => l.equip_type || '', filter: { type: 'select', label: 'Type', options: ['Utilization', 'Non-Consignment'] } },
    { k: 'qty', sort: l => Number(l.qty) || 0 },
    { k: 'num_days', sort: l => Number(l.num_days) || 0 },
    { k: 'day_rate', sort: l => Number(l.day_rate) || 0 },
    { k: 'total', sort: l => Number(compE(l.id).revenueSelected) || 0 },
    { k: 'margin', sort: l => Number(compE(l.id).margin) || 0 },
    { k: 'below', filter: { type: 'bool', label: 'Below margin', get: l => compE(l.id).belowThreshold } },
    { k: 'nocat', filter: { type: 'bool', label: 'No CAT-CLASS', get: l => !l.cat_class } },
  ];
  const equipCtl = useTableControls(equipRows, equipCols);

  const tpRows = (current?.equipment_lines || []).filter(l => l.equip_type === 'Third-Party');
  const tpCols = [
    { k: 'description', sort: l => l.description || '', filter: { type: 'text', label: 'Description' } },
    { k: 'msa_description', sort: l => l.msa_description || '', filter: { type: 'text', label: 'MSA Description' } },
    { k: 'vendor', sort: l => l.vendor_name || '', filter: { type: 'text', label: 'Vendor' } },
    { k: 'num_days', sort: l => Number(l.num_days) || 0 },
    { k: 'qty', sort: l => Number(l.qty) || 0 },
    { k: 'cost', sort: l => Number(compE(l.id).cost) || 0 },
    { k: 'day_rate', sort: l => Number(l.day_rate) || 0 },
    { k: 'revenue', sort: l => Number(compE(l.id).revenueSelected) || 0 },
    { k: 'margin', sort: l => Number(compE(l.id).margin) || 0 },
    { k: 'below', filter: { type: 'bool', label: 'Below margin', get: l => compE(l.id).belowThreshold } },
  ];
  const tpCtl = useTableControls(tpRows, tpCols);

  const consRows = current?.consumable_lines || [];
  const consCols = [
    { k: 'description', sort: l => l.description || '', filter: { type: 'text', label: 'Description' } },
    { k: 'part_number', sort: l => l.part_number || '' },
    { k: 'category', sort: l => l.category || 'Consumable', filter: { type: 'select', label: 'Category', options: ['Consumable', 'Service', 'Charge', 'Mobilization', 'Air Sale'] } },
    { k: 'qty', sort: l => Number(l.qty) || 0 },
    { k: 'bill_rate', sort: l => Number(l.bill_rate) || 0 },
    { k: 'cost', sort: l => Number(l.cost) || 0 },
    { k: 'total', sort: l => Number(compC(l.id).revenue) || 0 },
    { k: 'margin', sort: l => Number(compC(l.id).margin) || 0 },
    { k: 'below', filter: { type: 'bool', label: 'Below margin', get: l => compC(l.id).belowThreshold } },
  ];
  const consCtl = useTableControls(consRows, consCols);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ ...styles.h1, margin: 0 }}>Estimating</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <EstimatingGuide />
          <select style={{ ...styles.input, width: 260, maxWidth: '100%' }} value={selProjectId} onChange={e => setSelProjectId(e.target.value)}>
            <option value="">— Pick a project —</option>
            <option value={STANDALONE}>➕ Standalone — no opportunity yet</option>
            {projects.map(p => <option key={p.id} value={p.id}>{oppLabel(p)}</option>)}
          </select>
          <button style={styles.btn} disabled={!selProjectId || saving} onClick={createEstimate}>
            + New Estimate
          </button>
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Status tiles — click a number to filter the list to that status.
          List view only; the editor needs the vertical space. */}
      {!current && (estimates.length > 0 || waiting.length > 0) && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          {/* Requests, not estimates — a new sales request has no estimate yet, so it
              can't live in the status tiles. Permanent tile so it has a known home
              even at zero; jumps to the queue below. */}
          <button
            onClick={() => waitingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            disabled={!waiting.length}
            title={waiting.length
              ? 'Submitted sales requests with no estimate yet — click to jump to the queue'
              : 'No sales requests are waiting on an estimate'}
            style={{
              flex: '1 1 140px', minWidth: 120, textAlign: 'left',
              cursor: waiting.length ? 'pointer' : 'default',
              padding: '10px 14px', borderRadius: 8,
              background: waiting.length ? '#FFFBEB' : '#fff',
              border: `2px solid ${waiting.length ? '#FBBF24' : '#E5E7EB'}`,
              boxShadow: waiting.length ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
            }}>
            <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1,
              color: waiting.length ? '#92400E' : '#9CA3AF' }}>{waiting.length}</div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
              color: waiting.length ? '#92400E' : '#6B7280', marginTop: 2 }}>⏳ New requests</div>
          </button>
          <div style={{ width: 1, background: '#E5E7EB', margin: '4px 2px' }} />
          {[{ key: '', label: 'All Estimates', count: baseEstimates.length,
              c: { bg: '#F3F4F6', fg: '#374151', border: '#9CA3AF' } },
            ...EST_STATUSES.map(s => ({ key: s, label: s, count: statusCounts[s], c: EST_STATUS_COLOR[s] }))
          ].map(t => {
            const on = estFilterStatus === t.key;
            return (
              <button
                key={t.key || 'all'}
                onClick={() => setEstFilterStatus(on && t.key ? '' : t.key)}
                title={t.key ? `Show only ${t.key} estimates` : 'Clear the status filter'}
                style={{
                  flex: '1 1 140px', minWidth: 120, textAlign: 'left', cursor: 'pointer',
                  padding: '10px 14px', borderRadius: 8,
                  background: on ? t.c.bg : '#fff',
                  border: `2px solid ${on ? t.c.border : '#E5E7EB'}`,
                  boxShadow: on ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
                }}>
                <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, color: t.c.fg }}>{t.count}</div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                  color: on ? t.c.fg : '#6B7280', marginTop: 2 }}>{t.label}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Estimate list sidebar */}
      <div style={{ display: 'grid', gridTemplateColumns: current ? '280px 1fr' : '1fr', gap: 16 }}>
        <div>
        {estimates.filter(e => e.status === 'Submitted' && e.is_current !== false).length > 0 && (
          <div style={{ ...styles.card, marginBottom: 12, border: '1px solid #93C5FD', background: '#EFF6FF' }}>
            <h2 style={{ ...styles.h2, marginBottom: 8 }}>
              ✋ Waiting for Approval ({estimates.filter(e => e.status === 'Submitted' && e.is_current !== false).length})
            </h2>
            {estimates.filter(e => e.status === 'Submitted' && e.is_current !== false).map(e => (
              <div key={e.id} style={{ padding: '8px 10px', borderRadius: 6, marginBottom: 4,
                background: '#fff', border: '1px solid #BFDBFE', fontSize: 12, cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}
                onClick={() => reload(e.id)}>
                <div>
                  <div style={{ fontWeight: 700, color: '#1F2937' }}>
                    EST-{e.root_id || e.id}-{e.revision || 0} — {e.project_site}
                  </div>
                  <div style={{ color: '#6B7280', fontSize: 11 }}>
                    {e.estimator_name || e.created_by_name} · Tier {e.utilization_tier}% · open to review the Summary, then set Status to Approved or Rejected
                  </div>
                </div>
                <button
                  style={{ ...styles.btnSecondary, fontSize: 10, padding: '2px 8px', flexShrink: 0 }}
                  title="Pull this back to Draft — removes it from the approval queue (audit-logged)"
                  onClick={async (ev) => {
                    ev.stopPropagation();
                    if (!confirm(`Withdraw EST-${e.root_id || e.id}-${e.revision || 0} from approval? It returns to Draft.`)) return;
                    doSave(async () => { await api.updateEstimate(e.id, { status: 'Draft' }); loadEstimates(); if (current?.id === e.id) reload(e.id); });
                  }}>
                  ↩ Withdraw
                </button>
              </div>
            ))}
          </div>
        )}
        {waiting.length > 0 && (
          <div ref={waitingRef} style={{ ...styles.card, marginBottom: 12, border: '1px solid #FBBF24', background: '#FFFBEB' }}>
            <h2 style={{ ...styles.h2, marginBottom: 8 }}>⏳ Waiting for Estimate ({waiting.length})</h2>
            {waiting.map(r => {
              const overdue = r.quote_due_by && new Date(r.quote_due_by) < new Date();
              return (
                <div key={r.id} style={{ padding: '8px 10px', borderRadius: 6, marginBottom: 4,
                  background: '#fff', border: '1px solid #FDE68A', fontSize: 12 }}>
                  <div style={{ fontWeight: 700, color: '#1F2937', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>REQ-{r.root_id || r.id}-{r.revision || 0} — {r.customer_name || r.new_customer_name || 'No customer'}</span>
                    {(r.scope_flags || []).includes('rescue') && (
                      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, padding: '1px 8px', borderRadius: 10,
                        background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FCA5A5', whiteSpace: 'nowrap' }}>⛑ RESCUE</span>
                    )}
                    {(r.scope_flags || []).includes('csvm') && (
                      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, padding: '1px 8px', borderRadius: 10,
                        background: '#EEF2FF', color: '#4338CA', border: '1px solid #A5B4FC', whiteSpace: 'nowrap' }}>📹 CSVM</span>
                    )}
                    {r.kind === 'change_order' && (
                      <span title="Submitted by the onsite PM from Field Services — the estimate will auto-link as a change order of the job's base estimate"
                        style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, padding: '1px 8px', borderRadius: 10,
                        background: '#F3E8FF', color: '#6B21A8', border: '1px solid #C4B5FD', whiteSpace: 'nowrap' }}>🔁 CHANGE ORDER</span>
                    )}
                  </div>
                  <div style={{ color: '#6B7280', fontSize: 11, margin: '2px 0 6px' }}>
                    {reqDisplayTitle(r.title) || '(untitled)'} · {r.created_by_name || (r.external_submitter_name ? `${r.external_submitter_name} (FS PM)` : '')}
                    {r.quote_due_by && (
                      <span style={{ marginLeft: 6, fontWeight: 700, color: overdue ? '#DC2626' : '#92400E' }}>
                        Due {new Date(r.quote_due_by).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {overdue ? ' — OVERDUE' : ''}
                      </span>
                    )}
                  </div>
                  {/* Same handler either way — create-estimate is idempotent and
                      returns the existing estimate. A request that already has
                      one is still HERE because nobody has worked it yet, so the
                      button says "open", not "create". */}
                  <button style={{ ...styles.btn, fontSize: 11, padding: '4px 12px', background: r.estimate_id ? '#1F2937' : '#065F46' }}
                    disabled={saving}
                    title={r.estimate_id
                      ? 'Opens the estimate already started from this request. It stays in this queue until someone prices it.'
                      : 'Creates the estimate from this request (copies lines + target rates) and opens it. Looking at it does NOT take it out of this queue — only pricing it does.'}
                    onClick={() => estimateFromRequest(r)}>
                    {r.estimate_id ? '📊 Open Estimate' : '📊 Create Estimate'}
                  </button>
                  {/* Peek only: opens the request itself. Opening a request never
                      claims it and creates nothing, so it stays in this queue. */}
                  <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 12px', marginLeft: 6 }}
                    title="Open the request to read it. Creates nothing and leaves it in this queue."
                    onClick={() => navigate(`/requests?open=${r.id}&from=estimating`)}>
                    👁 Review Request
                  </button>
                  {r.estimate_id && (
                    <span style={{ marginLeft: 8, fontSize: 10, color: '#92400E' }}>
                      estimate opened, not yet priced
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h2 style={{ ...styles.h2, margin: 0 }}>All Estimates</h2>
            <label style={{ fontSize: 10, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}>
              <input type="checkbox" checked={showSuperseded} onChange={e => setShowSuperseded(e.target.checked)} />
              All revisions
            </label>
          </div>
          {/* Filters: name, start date, status, salesperson */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            <input
              style={{ ...styles.input, flex: '1 1 120px', minWidth: 100, fontSize: 12 }}
              placeholder="Filter by name…"
              value={estFilterName}
              onChange={e => setEstFilterName(e.target.value)}
            />
            <input
              type="date"
              title="Start date on or after"
              style={{ ...styles.input, flex: '1 1 120px', minWidth: 100, fontSize: 12 }}
              value={estFilterStart}
              onChange={e => setEstFilterStart(e.target.value)}
            />
            <select
              style={{ ...styles.input, flex: '1 1 110px', minWidth: 90, fontSize: 12 }}
              value={estFilterStatus}
              onChange={e => setEstFilterStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {EST_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              style={{ ...styles.input, flex: '1 1 130px', minWidth: 100, fontSize: 12 }}
              value={estFilterSalesperson}
              onChange={e => setEstFilterSalesperson(e.target.value)}
            >
              <option value="">All salespeople</option>
              {salespeople.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {estFiltersActive && (
              <button
                onClick={() => { setEstFilterName(''); setEstFilterStart(''); setEstFilterStatus(''); setEstFilterSalesperson(''); }}
                style={{ background: 'transparent', border: '1px solid #E5E7EB', borderRadius: 6, color: '#6B7280', fontSize: 11, cursor: 'pointer', padding: '2px 8px' }}
              >Clear</button>
            )}
          </div>
          {estimates.length === 0 && <div style={{ color: '#9CA3AF', fontSize: 12 }}>No estimates yet.</div>}
          {estimates.length > 0 && filteredEstimates.length === 0 && (
            <div style={{ color: '#9CA3AF', fontSize: 12 }}>No estimates match these filters.</div>
          )}
          {estFiltersActive && filteredEstimates.length > 0 && (
            <div style={{ color: '#9CA3AF', fontSize: 10, marginBottom: 4 }}>
              {filteredEstimates.length} of {estimates.length}
            </div>
          )}
          {filteredEstimates.map(e => (
            <div
              key={e.id}
              style={{
                padding: '8px 10px', borderRadius: 6, marginBottom: 4,
                background: current?.id === e.id ? '#FFF7ED' : '#F9FAFB',
                border: `1px solid ${current?.id === e.id ? ORANGE : '#E5E7EB'}`,
                fontSize: 13,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                <div style={{ cursor: 'pointer', flex: 1, opacity: e.is_current === false ? 0.6 : 1 }} onClick={() => reload(e.id)}>
                  <div style={{ fontWeight: 600, color: ORANGE }}>
                    {e.project_site}
                    <span style={{ color: '#9CA3AF', fontWeight: 600, fontSize: 10, marginLeft: 6 }}>
                      EST-{e.root_id || e.id}-{e.revision || 0}
                    </span>
                    {e.change_order_of_estimate_id != null && (
                      <span title={`Change order of the EST-${e.change_order_of_estimate_id} chain (open it to see the current base revision) — approval ADDS this estimate's total to the opportunity value`}
                        style={{ background: '#EDE9FE', color: '#5B21B6', fontWeight: 700, fontSize: 9,
                          padding: '1px 5px', borderRadius: 4, marginLeft: 5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        CO of EST-{e.change_order_of_estimate_id}
                      </span>
                    )}
                  </div>
                  {/* The request's Project Name (Q1) — the site alone reads as
                      "Cedar Bayou" on a row whose job is "CP Chem Cedar Bayou
                      1799 Fall TA". Lighter than the scope label below it:
                      the name is the JOB, the label is which slice of it. */}
                  {e.request_project_name && (
                    <div style={{ color: '#4B5563', fontSize: 11.5, marginTop: 1 }}>
                      {e.request_project_name}
                    </div>
                  )}
                  {e.scope_label && (
                    <div style={{ color: '#374151', fontSize: 11.5, fontWeight: 600, marginTop: 1 }}>
                      {e.scope_label}
                    </div>
                  )}
                  <div style={{ color: '#6B7280', fontSize: 11 }}>
                    v{e.version} · {e.status} · Tier {e.utilization_tier}%
                    {e.is_current === false && <span style={{ marginLeft: 4, fontWeight: 600 }}>· Superseded</span>}
                  </div>
                  <div style={{ color: '#9CA3AF', fontSize: 11, marginTop: 1 }}>
                    {e.salesperson ? `👤 ${e.salesperson}` : '👤 —'}
                    {e.start_date && <span style={{ marginLeft: 6 }}>📅 {fmtDate(e.start_date)}</span>}
                  </div>
                  {/* Who ESTIMATED this and when it was last worked. The name is
                      estimator_name — the person who priced it, claimed on the
                      first pricing edit — NOT created_by, which is only the
                      handoff click that opened the request (George 2026-08-20:
                      Air Liquide Norco read as his, Kevin Leach priced it). The
                      server COALESCEs back to created_by while an estimate is
                      still untouched. The last editor is appended only when
                      it's someone else. Draft says so out loud — that's the row
                      leadership chases. A Draft with NO last touch has only ever
                      been opened, never worked (server excludes the 'created'
                      event) — it reads "Not started" and its request is still in
                      the ⏳ queue, so it must not look like anyone holds it. */}
                  <div style={{ color: '#9CA3AF', fontSize: 11, marginTop: 1 }}
                    title={`Estimated by ${e.estimator_name || e.created_by_name || 'nobody yet'}\nCreated ${fmtDateTime(e.created_at) || '—'} by ${e.created_by_name || 'unknown'}\nLast change ${fmtDateTime(e.last_touch_at || e.updated_at) || '—'} by ${e.last_touch_by || (e.status === 'Draft' ? 'nobody — not started' : e.created_by_name || 'unknown')}`}>
                    {e.status === 'Draft' && !e.last_touch_by
                      ? <span style={{ fontStyle: 'italic' }}>✍ Not started — still in the queue</span>
                      : <>✍ {e.status === 'Draft' ? 'Draft by ' : ''}{e.estimator_name || e.created_by_name || '—'}</>}
                    <span style={{ marginLeft: 6 }}>🕓 {fmtDateTime(e.last_touch_at || e.updated_at) || '—'}</span>
                    {e.last_touch_by && e.last_touch_by !== (e.estimator_name || e.created_by_name) && (
                      <span style={{ marginLeft: 4 }}>(by {e.last_touch_by})</span>
                    )}
                  </div>
                </div>
                {e.status === 'Draft' && (
                  <button
                    title="Delete this estimate"
                    onClick={ev => { ev.stopPropagation(); deleteEstimate(e.id, `v${e.version} – ${e.project_site}`); }}
                    style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '2px 4px', flexShrink: 0 }}
                  >🗑</button>
                )}
              </div>
            </div>
          ))}
        </div>
        </div>

        {current && (
          <div>
            {/* Superseded / locked banners */}
            {current.is_current === false && (
              <div style={{ background: '#FEF3C7', border: '1px solid #FBBF24', borderRadius: 6,
                padding: '8px 14px', marginBottom: 12, fontSize: 13, fontWeight: 600, color: '#92400E' }}>
                ⚠ This is revision -{current.revision || 0}, superseded by a newer revision. Read-only.
              </div>
            )}
            {current.is_current !== false && current.status === 'Approved' && (
              <div style={{ background: '#D1FAE5', border: '1px solid #10B981', borderRadius: 6,
                padding: '8px 14px', marginBottom: 12, fontSize: 13, color: '#065F46' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, flex: 1, minWidth: 200 }}>
                    ✓ Approved — pricing is locked. Use New Revision for changes; use the Quote tab to issue the quote.
                  </span>
                  <button
                    onClick={pushToOpportunity}
                    disabled={pushing}
                    title="Set the linked opportunity's value to this estimate's total and attach the current quote PDF. Safe to re-run."
                    style={{
                      background: '#065F46', color: '#fff', border: 'none', borderRadius: 6,
                      padding: '6px 12px', fontSize: 12, fontWeight: 700,
                      cursor: pushing ? 'default' : 'pointer', opacity: pushing ? 0.6 : 1, whiteSpace: 'nowrap'
                    }}
                  >{pushing ? 'Pushing…' : '⬆ Push to Opportunity'}</button>
                  {/* Change orders never drive the crew plan — the server 400s
                      this anyway (a CO's labor is the delta, not the crew). */}
                  {!current.change_order_of && (
                  <button
                    onClick={resyncCrewPlan}
                    disabled={resyncing}
                    title="Push this estimate's crafts + headcount into the Operations staffing plan AND assign the named people from the Personnel tab to the job's Allocations. Day/Night land as separate crafts. Auto-runs on approval (and on Personnel saves after approval); this re-syncs. Never deletes or overwrites what ops already manages."
                    style={{
                      background: '#065F46', color: '#fff', border: 'none', borderRadius: 6,
                      padding: '6px 12px', fontSize: 12, fontWeight: 700,
                      cursor: resyncing ? 'default' : 'pointer', opacity: resyncing ? 0.6 : 1, whiteSpace: 'nowrap'
                    }}
                  >{resyncing ? 'Syncing…' : '👷 Resync Crew Plan'}</button>
                  )}
                </div>
                {pushMsg && (
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600,
                    color: pushMsg.ok ? '#065F46' : '#B91C1C' }}>
                    {pushMsg.ok ? '✓ ' : '⚠ '}{pushMsg.text}
                  </div>
                )}
              </div>
            )}

            {/* CHANGE-ORDER estimate strip (2026-08-13, George — Cenovus Lima).
                Purple = this estimate is a CO of another chain on the same
                opportunity: it bills ON TOP of the base, approval ADDS its
                total to the opportunity value, and the FS forecast feed ships
                base + approved COs. Link/unlink is Draft-only server-side. */}
            {current.change_order_of && (
              <div style={{ background: '#F5F3FF', border: '1px solid #C4B5FD', borderRadius: 6,
                padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ background: '#EDE9FE', color: '#5B21B6', fontWeight: 800, fontSize: 11,
                  padding: '2px 8px', borderRadius: 4, letterSpacing: 0.5 }}>CHANGE ORDER</span>
                <span style={{ fontSize: 13, color: '#5B21B6', flex: 1, minWidth: 240 }}>
                  Bills on top of <strong>{current.change_order_of.label}</strong>
                  {current.change_order_of.status ? ` (${current.change_order_of.status})` : ''}.
                  Approval <strong>adds</strong> this estimate's total to the opportunity value instead of replacing it.
                </span>
                {current.is_current !== false && current.status === 'Draft' && (
                  <button
                    disabled={saving}
                    title="Remove the change-order link — this estimate goes back to being a normal standalone estimate"
                    style={{ ...styles.btnSecondary, fontSize: 12, padding: '3px 10px', opacity: saving ? 0.6 : 1 }}
                    onClick={() => {
                      if (window.confirm(`Unlink from ${current.change_order_of.label}? This estimate becomes a normal estimate again.`)) {
                        setChangeOrderLink(null);
                      }
                    }}
                  >✕ Unlink</button>
                )}
              </div>
            )}
            {!current.change_order_of && current.is_current !== false && current.status === 'Draft' && coBaseOptions.length > 0 && (
              <div style={{ background: '#F5F3FF', border: '1px dashed #C4B5FD', borderRadius: 6,
                padding: '8px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#5B21B6' }}>Change order?</span>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  If this estimate adds scope on top of an existing estimate, mark it — approval then ADDS its total to the opportunity value.
                </span>
                <select
                  value=""
                  disabled={saving}
                  style={{ ...styles.input, width: 'auto', fontSize: 12, padding: '4px 8px' }}
                  onChange={e => { const v = e.target.value; if (v) setChangeOrderLink(Number(v)); }}
                >
                  <option value="">Mark as change order of…</option>
                  {coBaseOptions.map(o => (
                    <option key={o.id} value={o.id}>
                      EST-{o.root_id || o.id}-{o.revision || 0} · {o.status} · Tier {o.utilization_tier}%
                      {o.scope_label ? ` · ${o.scope_label}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Change-order banner: sales submitted a newer request revision
                after this estimate was built. Review + pull it into a new
                estimate revision. (George 2026-06-19) */}
            {current.pending_request && (
              <div style={{ background: '#FEF2F2', border: '1px solid #F87171', borderRadius: 6,
                padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#991B1B', fontWeight: 600, flex: 1, minWidth: 240 }}>
                  ⚠ Sales submitted <strong>{current.pending_request.label}</strong> after this estimate
                  {current.pending_request.submitted_by_name ? ` — ${current.pending_request.submitted_by_name}` : ''}
                  {current.pending_request.submitted_at ? ` · ${fmtDate(current.pending_request.submitted_at)}` : ''}.
                  Review the change and build a revised estimate from it.
                </span>
                <button
                  style={{ ...styles.btnSecondary, fontSize: 12, padding: '4px 10px' }}
                  title="Open the revised sales request to see what changed"
                  onClick={() => navigate(`/requests?open=${current.pending_request.id}`)}
                >📄 View {current.pending_request.label}</button>
                <button
                  disabled={saving}
                  style={{ background: '#B91C1C', color: '#fff', border: 'none', borderRadius: 6,
                    padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
                  title="Clone this estimate forward and link it to the revised request, so you can add/adjust the changed items and resubmit for approval"
                  onClick={() => estimateFromRequest({ id: current.pending_request.id })}
                >Build estimate revision →</button>
              </div>
            )}

            {/* CSVM vendor response — the vendor's quote + the SSS PO# issued
                to them, living on the request chain (also shown to Operations
                on the Job Hub). Editable here so leadership can log the
                vendor's response while pricing. */}
            {current.csvm_vendor && (
              <div style={{ background: '#EEF2FF', border: '1px solid #A5B4FC', borderRadius: 6,
                padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#3730A3' }}>CSVM Vendor</span>
                <span style={{ fontSize: 11, color: '#6B7280' }}>SSS PO #</span>
                <input style={{ ...styles.input, width: 150, fontSize: 13 }}
                  key={`est-csvmpo-${current.id}-${current.csvm_vendor.po_number || ''}`}
                  defaultValue={current.csvm_vendor.po_number || ''}
                  disabled={saving}
                  placeholder="PO number"
                  onBlur={e => {
                    const v = e.target.value.trim();
                    if (v !== (current.csvm_vendor.po_number || '')) saveCsvmVendor({ po_number: v || null });
                  }} />
                <span style={{ fontSize: 11, color: '#6B7280' }}>Vendor Quote #</span>
                <input style={{ ...styles.input, width: 150, fontSize: 13 }}
                  key={`est-csvmqn-${current.id}-${current.csvm_vendor.vendor_quote_number || ''}`}
                  defaultValue={current.csvm_vendor.vendor_quote_number || ''}
                  disabled={saving}
                  placeholder="Vendor's quote number"
                  onBlur={e => {
                    const v = e.target.value.trim();
                    if (v !== (current.csvm_vendor.vendor_quote_number || '')) saveCsvmVendor({ vendor_quote_number: v || null });
                  }} />
                {current.csvm_vendor.quote_attachment_id ? (
                  <button style={{ ...styles.btnSecondary, fontSize: 12, padding: '3px 10px' }} disabled={saving}
                    title="Download the CSVM vendor's quote"
                    onClick={() => doSave(() => api.downloadRequestFile(
                      current.csvm_vendor.request_id, current.csvm_vendor.quote_attachment_id, current.csvm_vendor.quote_name))}>
                    📄 {current.csvm_vendor.quote_name || 'vendor quote'}
                  </button>
                ) : (
                  <DropZone onFiles={files => uploadCsvmQuoteFile(files[0])} disabled={saving} title="Drop the vendor quote here">
                    <label style={{ ...styles.btnSecondary, fontSize: 12, padding: '3px 10px', cursor: saving ? 'wait' : 'pointer', display: 'inline-block' }}>
                      {saving ? '…' : '⬆ Attach vendor quote'}
                      <input type="file" style={{ display: 'none' }} disabled={saving}
                        onChange={e => { uploadCsvmQuoteFile(e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                  </DropZone>
                )}
              </div>
            )}

            {/* Header bar */}
            <div style={{ ...styles.card, marginBottom: 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                    PROJECT <span style={{ fontWeight: 700, color: '#374151' }}>EST-{current.root_id || current.id}-{current.revision || 0}</span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{current.project_site}</div>
                  {/* The job's name as SALES typed it, off the request's
                      questionnaire (PROJECT INFO Q1). projects.site on this job
                      is the bare word "Cedar Bayou" — the request calls it
                      "CP Chem Cedar Bayou 1799 Fall TA" (George 2026-09-11).
                      Read-only here: the request owns it, and it is resolved
                      through this estimate's CHAIN so a revision still shows it. */}
                  {current.request_project_name && (
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 1 }}
                      title="Project Name from the sales request (PROJECT INFO, Q1). Edit it on the request.">
                      <span style={{ color: '#9CA3AF' }}>Project name </span>
                      <span style={{ fontWeight: 600 }}>{current.request_project_name}</span>
                    </div>
                  )}
                  {/* Which opportunity this estimate actually points at. The
                      site name alone is ambiguous when a customer has two open
                      bids on the same site -- show the id and let leadership
                      move it if it landed on the wrong one. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: '#6B7280' }}>
                      Opportunity {current.project_id ? oppLabel(projects.find(p => p.id === current.project_id)) || `#${current.project_id}` : '— not linked'}
                    </span>
                    {isAdminLike(user) && (
                      relinking ? (
                        <>
                          <select style={{ ...styles.input, width: 320, maxWidth: '100%', fontSize: 12, padding: '2px 6px' }}
                            defaultValue=""
                            onChange={ev => relink(ev.target.value)}>
                            <option value="">— Move to opportunity —</option>
                            {projects.filter(p => p.id !== current.project_id)
                              .map(p => <option key={p.id} value={p.id}>{oppLabel(p)}</option>)}
                          </select>
                          <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                            onClick={() => setRelinking(false)}>Cancel</button>
                        </>
                      ) : (
                        <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                          onClick={() => setRelinking(true)}
                          title="Move this estimate chain to a different sales opportunity.">
                          Relink
                        </button>
                      )
                    )}
                  </div>
                  <input
                    key={`scope-${current.id}`}
                    type="text"
                    defaultValue={current.scope_label || ''}
                    placeholder="Scope label (e.g. staffing plan / PO this estimate covers)"
                    maxLength={120}
                    onBlur={ev => {
                      const v = ev.target.value.trim();
                      if (v !== (current.scope_label || '')) updateHeader('scope_label', v || null);
                    }}
                    disabled={current.status === 'Approved' || current.is_current === false}
                    style={{ marginTop: 3, width: 320, maxWidth: '100%', fontSize: 12, fontWeight: 600,
                      color: '#374151', border: '1px solid #D1D5DB', borderRadius: 4, padding: '3px 8px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>v{current.version}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 10px', borderRadius: 10,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        background: (EST_STATUS_COLOR[current.status] || EST_STATUS_COLOR.Draft).bg,
                        color: (EST_STATUS_COLOR[current.status] || EST_STATUS_COLOR.Draft).fg }}>
                      {current.status}
                    </span>
                    {saving && <span style={{ fontSize: 11, color: '#9CA3AF' }}>Saving…</span>}
                    {!saving && savedAt && (
                      <span style={{ fontSize: 11, color: '#10B981', fontWeight: 600 }}>
                        ✓ Auto-saved {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    <button
                      style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                      onClick={() => setShowRevisions(true)}
                    >View History</button>
                    <button
                      style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                      title="Full request → estimate → quote → PO audit trail for this job (who changed what, when) — viewable here and exportable to PDF"
                      onClick={openAudit}
                    >🧾 Audit Trail</button>
                    <button
                      style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                      title="Download Labor, Equipment, Third-Party Rentals, and Consumables as one .xlsx (4 tabs) to edit in Excel"
                      onClick={exportWorkbook}
                    >⬇ Workbook</button>
                    <button
                      style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                      title="Download the consolidated bill-side summary: Dashboard cost summary, Manpower roster, Equipment & Materials, and a day-by-day schedule — one .xlsx. No cost or margin."
                      onClick={exportBilledWorkbook}
                    >📊 Billed Summary</button>
                    <button
                      style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                      title="Copy this whole estimate into a NEW estimate — same scope, new dates, any opportunity. This one is untouched and the copy opens as a Draft. Use it for the next turnaround; use New Revision for a price change on this one."
                      onClick={() => { setDupMsg(null); setDupOpen(true); }}
                    >⧉ Duplicate</button>
                    {current.is_current !== false && current.status !== 'Approved' && (
                      <button
                        style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                        title="Import an edited workbook back into this estimate (shows a preview before it writes)"
                        onClick={() => setShowImport(true)}
                      >⬆ Import</button>
                    )}
                    {current.request_id && (
                      <button
                        style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                        title="Open the sales request this estimate was created from"
                        onClick={() => navigate(`/requests?open=${current.request_id}&from_est=${current.id}&from_est_label=EST-${current.root_id || current.id}-${current.revision || 0}`)}
                      >📄 View Request REQ-{current.request_root_id || current.request_id}-{current.request_revision || 0}</button>
                    )}
                    {!current.request_id && current.project_id && current.is_current !== false && (
                      <button
                        style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                        title="No sales request behind this estimate. Check the scope options and send the salesperson a Draft request, already linked here, to fill in and submit."
                        onClick={async () => {
                          const opp = projects.find(p => Number(p.id) === Number(current.project_id));
                          setReqAsk({ flags: [], owner_id: opp?.account_owner_id ? String(opp.account_owner_id) : '',
                            note: '', catalog: [], saving: false, error: null });
                          try {
                            const c = await api.getRequestCatalog();
                            setReqAsk(r => r && { ...r, catalog: c.scope_flags || [] });
                          } catch (e) { setReqAsk(r => r && { ...r, error: e.message }); }
                        }}
                      >📨 Request from Sales</button>
                    )}
                    {current.is_current !== false && (
                      <button
                        style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                        title="Soft-reserve this estimate's equipment in the Inventory app for warehouse availability planning (does NOT pull or stage). Converts to a real pull list when the deal is won and pushed from Operations."
                        disabled={reserving}
                        onClick={reserveEquipment}
                      >{reserving ? 'Reserving…' : '📦 Reserve in Inventory'}{current.last_reservation ? ` ✓ rev ${current.last_reservation.push_seq}` : ''}</button>
                    )}
                    {current.is_current !== false && (
                      <button
                        style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px' }}
                        title="Push this estimate's equipment to Inventory as a REAL pull list (pullable now). Use when the bid is won but not yet flipped to Won. Requires a job number on the opportunity. Re-pushes update the same list without erasing pulled barcodes."
                        disabled={pulling}
                        onClick={pushPullList}
                      >{pulling ? 'Pushing…' : '📥 Push Initial Pull List'}{current.last_pull ? ` ✓ rev ${current.last_pull.push_seq}` : ''}</button>
                    )}
                    {current.is_current !== false && ['Submitted', 'Approved', 'Rejected'].includes(current.status) && (
                      <button
                        style={{ ...styles.btn, fontSize: 11, padding: '2px 8px' }}
                        title={current.status === 'Submitted'
                          ? 'Email the ready-for-approval summary to the estimate notify list. Automatic estimate emails are switched off (Admin → Mail) — this sends the same email on demand. Shows the recipients before sending.'
                          : `Email the ${current.status} decision to the AM + estimate notify list. Automatic estimate emails are switched off (Admin → Mail) — this sends the same email on demand. Shows the recipients before sending.`}
                        disabled={sendingEmail}
                        onClick={sendStatusEmail}
                      >{sendingEmail ? 'Sending…' : (current.status === 'Submitted' ? '📧 Send Approval Email' : '📧 Send Decision Email')}</button>
                    )}
                  </div>
                  {reserveMsg && (
                    <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600,
                      color: reserveMsg.ok ? '#065F46' : '#B91C1C' }}>
                      {reserveMsg.ok ? '✓ ' : '⚠ '}{reserveMsg.text}
                    </div>
                  )}
                  {pullMsg && (
                    <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600,
                      color: pullMsg.ok ? '#065F46' : '#B91C1C' }}>
                      {pullMsg.ok ? '✓ ' : '⚠ '}{pullMsg.text}
                    </div>
                  )}
                  {dupMsg && (
                    <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600,
                      color: dupMsg.ok ? '#065F46' : '#B91C1C' }}>
                      {dupMsg.ok ? '✓ ' : '⚠ '}{dupMsg.text}
                    </div>
                  )}
                  {emailMsg && (
                    <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600,
                      color: emailMsg.ok ? '#065F46' : '#B91C1C' }}>
                      {emailMsg.ok ? '✓ ' : '⚠ '}{emailMsg.text}
                    </div>
                  )}
                </div>

                <HeaderField label="Util. Tier">
                  <div style={{ display: 'flex', gap: 4 }}>
                    {TIERS.map(t => (
                      <button key={t} type="button"
                        onClick={() => updateHeader('utilization_tier', t)}
                        style={{
                          padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700,
                          cursor: 'pointer', border: '1px solid',
                          background: current.utilization_tier === t ? '#1F2937' : '#fff',
                          color: current.utilization_tier === t ? '#fff' : '#374151',
                          borderColor: current.utilization_tier === t ? '#1F2937' : '#D1D5DB',
                        }}
                      >{t}%</button>
                    ))}
                  </div>
                </HeaderField>

                <HeaderField label="OT Rule">
                  <select style={{ ...styles.input, width: 150, fontSize: 12 }}
                    value={current.ot_rule}
                    onChange={e => updateHeader('ot_rule', e.target.value)}>
                    {OT_RULES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </HeaderField>

                <HeaderField label="Day Rate">
                  <label title="Quote this estimate as one $/day: labor at 8 ST + balance OT per person, per diem, and equipment. The breakdown shows below the markup panel."
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', height: 28 }}>
                    <input type="checkbox" checked={!!current.day_rate_mode}
                      onChange={e => updateHeader('day_rate_mode', e.target.checked)} />
                    Quote as day rate
                  </label>
                </HeaderField>

                <HeaderField label="Margin % (ST)">
                  <input type="number" min="0" max="1" step="0.01"
                    title="Target margin for ST labor and every non-labor category. Lines/categories below it flag BELOW (orange)."
                    style={{ ...styles.input, width: 72, fontSize: 12 }}
                    defaultValue={current.margin_threshold}
                    onBlur={e => updateHeader('margin_threshold', Number(e.target.value))} />
                </HeaderField>

                <HeaderField label="Margin % (OT)">
                  <input type="number" min="0" max="1" step="0.01"
                    title="Target margin for OT labor only (typically looser than ST since OT premium dilutes margin). OT margin cells grade against this."
                    style={{ ...styles.input, width: 72, fontSize: 12 }}
                    defaultValue={current.ot_margin_threshold ?? 0.15}
                    onBlur={e => updateHeader('ot_margin_threshold', Number(e.target.value))} />
                </HeaderField>

                <HeaderField label="OT Burden %">
                  {current.computed?.markupSums?.otBurdenDerived ? (
                    <div title="Derived automatically: sum of the markup components flagged OT (FICA + unemployment — the burdens that follow premium wage dollars). Toggle the OT box on a component to change what's included."
                      style={{ ...styles.input, width: 80, fontSize: 12, background: '#F3F4F6',
                        color: '#374151', cursor: 'help', display: 'flex', alignItems: 'center' }}>
                      {Number(current.computed.markupSums.otBurden).toFixed(4)}
                    </div>
                  ) : (
                    <input type="number" min="0" step="0.0001"
                      style={{ ...styles.input, width: 80, fontSize: 12 }}
                      defaultValue={current.ot_burden_pct}
                      onBlur={e => updateHeader('ot_burden_pct', Number(e.target.value))} />
                  )}
                </HeaderField>

                <HeaderField label="OT Profit %">
                  <input type="number" min="0" step="0.0001"
                    style={{ ...styles.input, width: 80, fontSize: 12 }}
                    defaultValue={current.ot_profit_pct}
                    onBlur={e => updateHeader('ot_profit_pct', Number(e.target.value))} />
                </HeaderField>

                <HeaderField label="Status">
                  <select style={{ ...styles.input, width: 130, fontSize: 13, fontWeight: 800,
                      background: (EST_STATUS_COLOR[current.status] || EST_STATUS_COLOR.Draft).bg,
                      color: (EST_STATUS_COLOR[current.status] || EST_STATUS_COLOR.Draft).fg,
                      border: `2px solid ${(EST_STATUS_COLOR[current.status] || EST_STATUS_COLOR.Draft).border}`,
                      textTransform: 'uppercase', letterSpacing: '0.03em', cursor: 'pointer' }}
                    value={current.status}
                    onChange={e => updateHeader('status', e.target.value)}>
                    {['Draft','Submitted','Approved','Rejected'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </HeaderField>

                {current.is_current !== false && (
                  <HeaderField label="Revision">
                    <button style={{ ...styles.btnSecondary, fontSize: 12, fontWeight: 700 }}
                      disabled={saving}
                      title="Clone this estimate into an editable new revision; this one freezes"
                      onClick={reviseEstimate}>
                      📝 New Revision (-{Number(current.revision || 0) + 1})
                    </button>
                  </HeaderField>
                )}
              </div>
            </div>

            {/* Markup panel */}
            <MarkupPanel
              markupLines={current.markup_lines}
              sums={current.computed?.markupSums}
              otBurden={current.computed?.markupSums?.otBurden ?? current.ot_burden_pct}
              otProfit={current.ot_profit_pct}
              onSave={saveMarkup}
            />

            {current.day_rate_mode && current.computed?.dayRate && (
              <DayRatePanel dayRate={current.computed.dayRate} />
            )}

            {/* Section tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
              {['labor','equipment','thirdparty','consumables','personnel','summary','msa','quote','history'].map(s => (
                <button key={s} type="button" onClick={() => { setSection(s); setError(''); }} style={{
                  padding: '6px 16px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', border: 'none',
                  background: section === s ? '#1F2937' : '#E5E7EB',
                  color: section === s ? '#fff' : '#374151',
                }}>
                  {s === 'msa' ? 'MSA Rates' : s === 'consumables' ? 'Consumables, Services and Charges' : s === 'thirdparty' ? 'Third-Party Rentals' : s.charAt(0).toUpperCase() + s.slice(1)}
                  {s === 'summary' && current.computed?.hasBelow
                    ? <span style={{ marginLeft: 4, color: ORANGE }}>⚠</span> : null}
                </button>
              ))}
            </div>

            {/* Labor section */}
            {section === 'labor' && (
              <div style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <h2 style={{ ...styles.h2, margin: 0 }}>Labor</h2>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={{ ...styles.btnSecondary, fontSize: 12 }}
                      onClick={() => setShowRollup(v => !v)}
                      title="Per-craft man-hours and headcount, split by Day/Night shift and by phase">
                      {showRollup ? 'Hide' : 'Show'} Craft Rollup
                    </button>
                    <button style={styles.btn} onClick={addLaborLine} disabled={saving}>+ Add Labor Line</button>
                  </div>
                </div>
                {showRollup && <CraftRollup laborLines={current.labor_lines || []} />}
                <datalist id="est-craft-options">
                  {crafts.map(c => <option key={c.id} value={c.name} />)}
                </datalist>
                <TableControlsBar ctl={laborCtl} columns={laborCols} total={laborRows.length} />
                {laborRows.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 8,
                    padding: '6px 10px', background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12 }}>
                    <span style={{ fontWeight: 700, color: '#374151' }}>{selectedLabor.size} selected</span>
                    <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px' }}
                      onClick={() => setSelectedLabor(new Set((current.labor_lines || []).filter(l => !l.rate_only).map(l => l.id)))}>Select all</button>
                    <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px' }}
                      onClick={() => setSelectedLabor(new Set())}>None</button>
                    {selectedLabor.size > 0 && (<>
                      <span style={{ color: '#D1D5DB' }}>|</span>
                      <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px' }} disabled={saving}
                        title={`Set the mileage rate to the SSS standard ${STANDARD_MILEAGE_LABEL}/mi (paid = billed) on the selected lines`}
                        onClick={() => applyBulkLabor({ target_ids: [...selectedLabor], set: { mob_cost_rate: STANDARD_MILEAGE_RATE, mob_bill_rate: STANDARD_MILEAGE_RATE } })}>
                        ⛽ Set standard mileage ({STANDARD_MILEAGE_LABEL})
                      </button>
                      <span style={{ color: '#6B7280' }}>To copy a schedule, click <strong>⎘</strong> on the line you want to copy FROM.</span>
                    </>)}
                  </div>
                )}
                {copyPanel && (
                  <CopyToSelectedPanel
                    source={(current.labor_lines || []).find(l => l.id === copyPanel.sourceId) || {}}
                    targetIds={[...selectedLabor].filter(id => id !== copyPanel.sourceId)}
                    saving={saving}
                    onApply={applyBulkLabor}
                    onCancel={() => setCopyPanel(null)} />
                )}
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead><tr>
                      <SortTh ctl={laborCtl} k="craft" style={styles.th}>Craft</SortTh>
                      <SortTh ctl={laborCtl} k="labor_type" style={styles.th}>Type</SortTh>
                      <th style={styles.th} title="Quote the rate only — no hours, no revenue. 'We're not quoting this position, but if you want them this is what they cost.'">Rate<br/><span style={{fontWeight:400,fontSize:9,color:'#9CA3AF'}}>only</span></th>
                      <SortTh ctl={laborCtl} k="qty" style={styles.th}>Qty</SortTh>
                      <th style={styles.th}>Pay ST</th>
                      <th style={styles.th}>Pay OT</th>
                      <th style={styles.th}>Bill ST</th>
                      <th style={styles.th}>Bill OT</th>
                      <th style={styles.th} title="Per-hour ST spread after burdens: Bill ST minus true hourly cost (pay + non-profit markups + add-on). Rate-level check — works before any phases/hours exist.">$/Hr<br/><span style={{fontWeight:400,fontSize:9,color:'#9CA3AF'}}>ST after burden</span></th>
                      <SortTh ctl={laborCtl} k="stHours" style={styles.th}>ST Hrs</SortTh>
                      <SortTh ctl={laborCtl} k="otHours" style={styles.th}>OT Hrs</SortTh>
                      <SortTh ctl={laborCtl} k="revenue" style={styles.th}>Revenue<HelpGlyph help="Labor revenue ONLY — billable hours × bill rate (ST + OT). Per diem and mobilization are NOT included here; they have their own columns and roll up as their own lines on the Summary. This is the base for the Margin/Markup %." /><br/><span style={{fontWeight:400,fontSize:9,color:'#9CA3AF'}}>labor</span></SortTh>
                      <SortTh ctl={laborCtl} k="cost" style={styles.th}>Cost<HelpGlyph help="Labor cost ONLY — billable hours × fully-burdened cost (pay + payroll taxes, WC, insurance, fringe, overhead). Per diem and mobilization are NOT included here; they have their own columns." /><br/><span style={{fontWeight:400,fontSize:9,color:'#9CA3AF'}}>labor</span></SortTh>
                      <SortTh ctl={laborCtl} k="perdiem" style={styles.th}>Per Diem<HelpGlyph help="Per diem revenue for this line — bill/day × days × positions. A reimbursable, billed in full; rolls up as its own line on the Summary. NOT in the Revenue/Cost/Margin/Markup at left." /></SortTh>
                      <SortTh ctl={laborCtl} k="mob" style={styles.th}>Mob<HelpGlyph help="Mobilization revenue for this line — mileage (one-way × 2 × $/mi × positions). A reimbursable, billed in full; rolls up as its own line on the Summary. NOT in the Revenue/Cost/Margin/Markup at left." /></SortTh>
                      <th style={styles.th}>Margin<HelpGlyph help="Margin = Gross Profit ÷ Bill, per hour (Gross Profit = Bill minus fully-burdened cost: taxes, WC, insurance, fringe, overhead). ST grades against the Margin % (ST) threshold up top, OT against Margin % (OT) — orange when below its own target. Per diem & mobilization are NOT in this % (their own columns). The gray 'unburdened' line beneath is the same bill measured against the PAY rate, before our burden — display only, never graded. Subcontractor lines show no 'unburdened' line: the sub's rate already IS our true cost, so there is only one figure." /><br/><span style={{fontWeight:400,fontSize:9,color:'#9CA3AF'}}>ST / OT</span></th>
                      <th style={styles.th}>Markup<HelpGlyph help="Markup = Gross Profit ÷ Cost, per hour. Same profit as Margin — Gross Profit is Bill minus fully-burdened cost; markup divides it by the cost, so it always reads higher than margin on the same line. ST and OT shown separately. Per diem & mobilization are NOT in this % (their own columns). The gray 'unburdened' line beneath is (Bill − Pay) ÷ Pay — the wage multiplier, before our burden. It is NOT the same profit: the burden we pay out is still inside it, so on a $40 wage billed at $65 it reads 62.50% where the true markup is 40.64%. Subcontractor lines show no 'unburdened' line: the sub's rate already IS our true cost. On a Subcontractor line the ST % here is EDITABLE: type the markup over the sub cost and Bill ST and Bill OT are set to cost × (1 + markup); Margin re-derives from that." /><br/><span style={{fontWeight:400,fontSize:9,color:'#9CA3AF'}}>ST / OT</span></th>
                      <th style={styles.th}></th>
                    </tr></thead>
                    <tbody>
                      {laborCtl.view.map((line, vi) => {
                        const comp = (current.computed?.laborLines || []).find(l => l.id === line.id) || {};
                        const isBelow = comp.belowThreshold;
                        // One background per line GROUP (row + recap), zebra-alternated so
                        // adjacent groups read as distinct blocks — a 1px border alone
                        // wasn't enough to tie the recap line to its own row (George
                        // 2026-08-11). Below-threshold orange overrides the stripe.
                        const groupBg = isBelow ? '#FFF7ED' : (vi % 2 === 1 ? '#F3F6FA' : undefined);
                        return (
                          <>
                            <tr key={line.id} style={{ background: groupBg }}>
                              <td style={styles.td}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                  <input type="checkbox" checked={selectedLabor.has(line.id)}
                                    onChange={() => toggleLaborSel(line.id)}
                                    title="Select this line for bulk copy / standard mileage"
                                    style={{ marginTop: 7, flexShrink: 0 }} />
                                  <div>
                                    <span title="Line number — the Personnel tab shows the same L# on every slot this line drives."
                                      style={{ display: 'inline-block', background: '#EEF2FF', color: '#4338CA', fontWeight: 800, fontSize: 11, borderRadius: 4, padding: '1px 6px', marginBottom: 3 }}>
                                      L{line.line_no}
                                    </span>
                                    <input style={{ ...styles.input, width: 240, fontSize: 12 }}
                                      list="est-craft-options"
                                      placeholder="Search crafts…"
                                      title={line.craft || 'Search the Crafts list (Operations → Crafts) or type a custom craft'}
                                      defaultValue={line.craft}
                                      onBlur={e => updateLabor(line.id, { craft: e.target.value })} />
                                    <input style={{ ...styles.input, width: 240, fontSize: 11, marginTop: 3,
                                        ...(current.msa_wording_required && !line.msa_description ? { borderColor: '#DC2626' } : {}) }}
                                      key={`msad-${line.id}-${line.msa_description || ''}`}
                                      placeholder={current.msa_wording_required ? 'MSA wording (required)…' : 'MSA / Set Pricing wording…'}
                                      title="The exact craft wording from the MSA or Set Pricing sheet — keeps the quote matching the contract"
                                      defaultValue={line.msa_description || ''}
                                      onBlur={e => updateLabor(line.id, { msa_description: e.target.value.trim() || null })} />
                                    {!line.msa_description && line.labor_type !== 'Subcontractor' && (
                                      <MsaPick kind="labor" sheet={msaSheet} text={line.craft}
                                        onPick={l => updateLabor(line.id, {
                                          msa_description: l.msa_description,
                                          ...(l.bill_st != null ? { bill_st: Number(l.bill_st) } : {}),
                                          ...(l.bill_ot != null ? { bill_ot: Number(l.bill_ot) } : {}),
                                        })} />
                                    )}
                                    <input style={{ ...styles.input, width: 240, fontSize: 11, marginTop: 3 }}
                                      list={`sec-${line.id}`}
                                      placeholder="Section (Turnaround / Safety / CSVM / Rescue)…"
                                      title="Section the Billed Summary groups this line under. Leave blank to auto-detect from the craft name."
                                      defaultValue={line.labor_group || ''}
                                      onBlur={e => updateLabor(line.id, { labor_group: e.target.value.trim() || null })} />
                                    <datalist id={`sec-${line.id}`}>
                                      <option value="Turnaround" /><option value="Safety" />
                                      <option value="CSVM" /><option value="Rescue" />
                                    </datalist>
                                    <LastQuoted hint={priceHints?.labor?.[line.id]}
                                      onOpen={() => openPrice('labor', line.id, line.craft)} />
                                  </div>
                                </div>
                              </td>
                              <td style={styles.td}>
                                <select style={{ ...styles.input, width: 110, fontSize: 12 }}
                                  value={line.labor_type}
                                  onChange={e => updateLabor(line.id, { labor_type: e.target.value })}>
                                  {LABOR_TYPES.map(t => <option key={t}>{t}</option>)}
                                </select>
                              </td>
                              <td style={{ ...styles.td, textAlign: 'center' }}>
                                <input type="checkbox" checked={!!line.rate_only}
                                  title="Quote rate only — no hours/revenue"
                                  onChange={e => updateLabor(line.id, { rate_only: e.target.checked })} />
                              </td>
                              <td style={styles.td}>
                                {!line.rate_only && peakHeadcount(line.phases) > 0 ? (
                                  // Auto-derived from the phases (peak day+night headcount) — the
                                  // same people whose hours the phases already book. Typing a
                                  // different number here silently under-billed per diem + mob.
                                  <button
                                    onClick={() => setExpandedPhase(expandedPhase === line.id ? null : line.id)}
                                    title={`Auto from phase headcount (peak day + night = ${positionsFor(line)}). Not typed here — click to open Phases and change the headcount.`}
                                    style={{ width: 50, padding: '2px 0', cursor: 'pointer', background: '#F9FAFB',
                                      border: '1px dashed #D1D5DB', borderRadius: 4, lineHeight: 1.1 }}>
                                    <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151' }}>
                                      {positionsFor(line)}
                                    </span>
                                    <span style={{ display: 'block', fontSize: 8, fontWeight: 700, letterSpacing: 0.3,
                                      textTransform: 'uppercase', color: '#9CA3AF' }}>auto</span>
                                  </button>
                                ) : (
                                  <input type="number" min="1" style={{ ...styles.input, width: 50, fontSize: 12 }}
                                    title="No staffed phases on this line — positions are typed here. Once phases carry headcount, this auto-derives from them."
                                    defaultValue={line.qty_positions}
                                    onBlur={e => updateLabor(line.id, { qty_positions: Number(e.target.value) })} />
                                )}
                              </td>
                              <td style={styles.td}>
                                {/* MSA pay is mandatory (server/services/msaPay.js): matched wording locks Pay ST. */}
                                {line.msa_pay_st != null && Number(line.pay_st) === Number(line.msa_pay_st) ? (
                                  <div title="MSA pay rate for this MSA wording (mandatory). Change the MSA wording or the MSA Rates sheet to change it."
                                    style={{ ...styles.input, width: 70, fontSize: 12, color: '#DC2626', fontWeight: 600,
                                      background: '#F3F4F6', cursor: 'help' }}>
                                    {Number(line.pay_st).toFixed(2)} <span style={{ fontSize: 8, color: '#1E40AF' }}>MSA</span>
                                  </div>
                                ) : (<>
                                <input type="number" step="0.01" style={{ ...styles.input, width: 70, fontSize: 12, color: '#DC2626', fontWeight: 600 }}
                                  key={`payst-${line.id}-${line.pay_st}`}
                                  defaultValue={line.pay_st}
                                  title={line.labor_type === 'Subcontractor'
                                    ? "What the sub charges us per ST hour. Changing it re-marks-up Bill ST: holds the line's Markup % (typed in the Markup column, or implied by the existing bill rate)."
                                    : undefined}
                                  onBlur={e => {
                                    const st = Number(e.target.value);
                                    // Pay OT is always 1.5× ST (federal law) — except
                                    // subcontractors, whose ST/OT are whatever the sub
                                    // agreement establishes.
                                    const body = { pay_st: st };
                                    if (line.labor_type !== 'Subcontractor') body.pay_ot = parseFloat((st * 1.5).toFixed(2));
                                    else {
                                      // Sub cost drives the whole line: hold the ST markup and
                                      // re-derive Bill ST (same rule as consumables). Pay OT
                                      // defaults to 1.5× and follows ST while it still equals
                                      // that; Bill OT re-marks-up at the OT-held markup.
                                      const m = subMarkupOf(line);
                                      if (m != null && st > 0) body.bill_st = Math.round(st * (1 + m) * 100) / 100;
                                      const follows = subOtFollowsSt(line);
                                      const ot = follows ? Math.round(st * 150) / 100 : (Number(line.pay_ot) || 0);
                                      if (follows && st > 0) body.pay_ot = ot;
                                      const mo = subOtMarkupOf(line);
                                      if (follows && mo != null && ot > 0) body.bill_ot = Math.round(ot * (1 + mo) * 100) / 100;
                                    }
                                    updateLabor(line.id, body);
                                  }} />
                                {line.msa_pay_st != null && (
                                  <div title="This line's MSA wording carries a mandatory pay rate. Click to apply it."
                                    onClick={() => updateLabor(line.id, { pay_st: Number(line.msa_pay_st) })}
                                    style={{ marginTop: 3, fontSize: 10, fontWeight: 600, color: '#1E40AF',
                                      background: '#DBEAFE', padding: '2px 6px', borderRadius: 8, cursor: 'pointer',
                                      display: 'inline-block' }}>
                                    MSA pay ${Number(line.msa_pay_st).toFixed(2)}, apply
                                  </div>
                                )}
                                </>)}
                              </td>
                              {line.labor_type === 'Subcontractor' ? (
                                <td style={styles.td}>
                                  <input type="number" step="0.01" style={{ ...styles.input, width: 70, fontSize: 12 }}
                                    key={`payot-${line.id}-${line.pay_ot}`}
                                    defaultValue={line.pay_ot}
                                    title="Subcontractor OT rate. Defaults to 1.5× Pay ST and follows it until you type something else here. Changing it re-marks-up Bill OT at the OT % (or the ST Markup % if no OT % is set)."
                                    onBlur={e => {
                                      const ot = Number(e.target.value);
                                      const body = { pay_ot: ot };
                                      const m = subOtMarkupOf(line);
                                      if (m != null && ot > 0) body.bill_ot = Math.round(ot * (1 + m) * 100) / 100;
                                      updateLabor(line.id, body);
                                    }} />
                                </td>
                              ) : (
                                <td style={{ ...styles.td, color: '#6B7280', fontSize: 12 }}
                                  title="Always 1.5× Pay ST (federal OT law) — derived, not editable">
                                  {fmtMoney(Number(line.pay_st) * 1.5)}
                                </td>
                              )}
                              <td style={{ ...styles.td, color: '#6B7280', fontSize: 12 }}>
                                {line.labor_type === 'Standard'
                                  ? fmtMoney(comp.billST)
                                  : <input type="number" step="0.01" style={{ ...styles.input, width: 70, fontSize: 12 }}
                                      key={`billst-${line.id}-${line.bill_st}`}
                                      defaultValue={line.bill_st || ''}
                                      title={line.labor_type === 'Subcontractor'
                                        ? 'Manual sell rate. Typing here sets Bill ST as-is; the Markup column then shows the implied markup over the sub cost.'
                                        : undefined}
                                      onBlur={e => {
                                        const body = { bill_st: Number(e.target.value) };
                                        // A hand-typed bill breaks the markup link (consumables rule).
                                        if (line.labor_type === 'Subcontractor') body.subcontractor_markup = null;
                                        updateLabor(line.id, body);
                                      }} />}
                              </td>
                              <td style={{ ...styles.td, color: '#6B7280', fontSize: 12 }}>
                                {line.labor_type === 'Standard' ? (
                                  <span title="Derived by the SSS OT rule: bill ST + (OT premium × (1 + OT burden + OT profit)). Nothing to fill in — Standard lines apply it automatically.">
                                    {fmtMoney(comp.billOT)}
                                  </span>
                                ) : (() => {
                                  // SSS OT rule: bill OT = billed ST + premium × (1 + OT burden
                                  // + OT profit). We take no profit on the premium — it rides
                                  // through with only the burdens that legally follow premium
                                  // dollars — so the OT hour earns the SAME GP/hr as the ST
                                  // hour. Standard lines derive this already; Fixed/MSA lines
                                  // are hand-typed, which is what this button removes.
                                  // Subcontractors get no button: their rate carries no SSS
                                  // burden, so there is no premium of ours to burden.
                                  const otB  = Number(current.computed?.markupSums?.otBurden ?? current.ot_burden_pct) || 0;
                                  const otP  = Number(current.ot_profit_pct) || 0;
                                  const bST  = Number(line.bill_st) || 0;
                                  const prem = Math.max(0, (Number(line.pay_ot) || 0) - (Number(line.pay_st) || 0));
                                  const derived = bST > 0 && prem > 0
                                    ? Math.round((bST + prem * (1 + otB + otP)) * 100) / 100 : null;
                                  const matches = derived != null && Math.abs((Number(line.bill_ot) || 0) - derived) < 0.005;
                                  return (<>
                                    <input type="number" step="0.01" style={{ ...styles.input, width: 70, fontSize: 12 }}
                                      key={`billot-${line.id}-${line.bill_ot}`}
                                      defaultValue={line.bill_ot || ''}
                                      onBlur={e => {
                                        const body = { bill_ot: Number(e.target.value) };
                                        if (line.labor_type === 'Subcontractor') body.subcontractor_markup = null;
                                        updateLabor(line.id, body);
                                      }} />
                                    {line.labor_type === 'Fixed' && derived != null && (
                                      <button
                                        disabled={matches || saving}
                                        title={matches
                                          ? `Matches the SSS OT rule — bill ST $${bST.toFixed(2)} + premium $${prem.toFixed(2)} × ${(1 + otB + otP).toFixed(4)} = $${derived.toFixed(2)}`
                                          : `Set Bill OT to $${derived.toFixed(2)} — bill ST $${bST.toFixed(2)} + premium $${prem.toFixed(2)} × ${(1 + otB + otP).toFixed(4)} (OT burden ${(otB * 100).toFixed(2)}%${otP ? ` + OT profit ${(otP * 100).toFixed(2)}%` : ''}). Earns the same GP/hr as the ST hour. An MSA-dictated OT rate overrides this — leave it typed.`}
                                        style={{ ...styles.btnSecondary, display: 'block', fontSize: 10,
                                          padding: '1px 5px', marginTop: 2, whiteSpace: 'nowrap',
                                          ...(matches
                                            ? { border: '1px solid #10B981', background: '#ECFDF5', color: '#047857', cursor: 'default' }
                                            : { border: '1px solid #F59E0B', background: '#FFFBEB', color: '#B45309' }) }}
                                        onClick={() => { if (!matches) updateLabor(line.id, { bill_ot: derived }); }}>
                                        {matches ? '✓ OT rule' : `⚡ ${derived.toFixed(2)}`}
                                      </button>
                                    )}
                                  </>);
                                })()}
                              </td>
                              <td style={{ ...styles.td, fontSize: 12 }}>
                                {(() => {
                                  const bill = Number(comp.billST) || 0;
                                  const cost = Number(comp.trueCostST) || 0;
                                  if (!bill) return <span style={{ color: '#9CA3AF' }}>—</span>;
                                  const spread = bill - cost;
                                  const pct = spread / bill;
                                  const below = pct < Number(current.margin_threshold || 0);
                                  return (
                                    <span title={`Bill $${bill.toFixed(2)} − true cost $${cost.toFixed(2)}/hr. OT: $${(Number(comp.billOT)||0).toFixed(2)} − $${(Number(comp.trueCostOT)||0).toFixed(2)} = $${((Number(comp.billOT)||0)-(Number(comp.trueCostOT)||0)).toFixed(2)}/hr`}
                                      style={{ fontWeight: 700, color: below ? ORANGE : colors.green, whiteSpace: 'nowrap' }}>
                                      ${spread.toFixed(2)}<br/>
                                      <span style={{ fontWeight: 400, fontSize: 10 }}>{(pct * 100).toFixed(1)}%</span>
                                    </span>
                                  );
                                })()}
                              </td>
                              {line.rate_only ? (
                                <td colSpan={8} style={{ ...styles.td, fontSize: 11, color: '#6B7280', fontStyle: 'italic' }}>
                                  Rate only — on the quote, no hours/revenue
                                </td>
                              ) : (() => {
                                const thr = Number(current.margin_threshold || 0);
                                const otThr = Number(current.ot_margin_threshold ?? 0.15);
                                const dash = <span style={{ color: '#9CA3AF' }}>—</span>;
                                return (
                                <>
                                  <td style={{ ...styles.td, fontSize: 12 }}>{comp.stHours ?? '—'}</td>
                                  <td style={{ ...styles.td, fontSize: 12 }}>{comp.otHours ?? '—'}</td>
                                  <td style={{ ...styles.td, fontWeight: 600, fontSize: 12 }}>{fmtMoney(comp.laborRevenue)}</td>
                                  <td style={{ ...styles.td, fontSize: 12, color: '#DC2626' }}>{fmtMoney(comp.laborTrueCost)}</td>
                                  <td style={{ ...styles.td, fontSize: 12 }}
                                    title={Number(comp.pdRevenue) > 0 ? `${comp.perDiemDays} day-positions · cost ${fmtMoney(comp.pdCost)}` : 'Not per-diem eligible'}>
                                    {Number(comp.pdRevenue) > 0 ? fmtMoney(comp.pdRevenue) : dash}
                                  </td>
                                  <td style={{ ...styles.td, fontSize: 12 }}
                                    title={Number(comp.mobRevenue) > 0 ? `Mobilization billed · cost ${fmtMoney(comp.mobCost)}` : 'No mobilization on this line'}>
                                    {Number(comp.mobRevenue) > 0 ? fmtMoney(comp.mobRevenue) : dash}
                                  </td>
                                  <td style={{ ...styles.td, fontSize: 11, whiteSpace: 'nowrap' }}>
                                    {comp.marginST != null
                                      ? <div style={{ fontWeight: 700, color: comp.marginST < thr ? ORANGE : colors.green }}>ST {pct(comp.marginST)}</div>
                                      : null}
                                    {comp.marginOT != null
                                      ? <div style={{ fontWeight: 700, color: comp.marginOT < otThr ? ORANGE : colors.green }}>OT {pct(comp.marginOT)}</div>
                                      : null}
                                    {comp.marginST == null && comp.marginOT == null ? dash : null}
                                    <Unburdened st={comp.rawMarginST} ot={comp.rawMarginOT}
                                      title="Unburdened — (Bill − Pay) ÷ Bill, before our burden. Not the same profit as the burdened figure above: the burden we pay out is still inside this number, so it always reads higher. Thresholds grade the burdened figure." />
                                  </td>
                                  <td style={{ ...styles.td, fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}
                                    title="(Bill − fully-burdened cost) ÷ cost, per hour">
                                    {line.labor_type === 'Subcontractor' ? (() => {
                                      // Sub lines: markup is TYPED here and drives both bill rates
                                      // (bill = sub cost × (1 + m)); Margin re-derives from the calc.
                                      const held = subMarkupOf(line);
                                      const heldOT = subOtMarkupOf(line);
                                      const payST = Number(line.pay_st) || 0;
                                      // Pay OT blank = it will be seeded at 1.5× ST by the ST % box.
                                      const payOT = Number(line.pay_ot) > 0 ? Number(line.pay_ot) : Math.round(payST * 150) / 100;
                                      const mgn = comp.marginST != null ? (comp.marginST * 100).toFixed(2) : null;
                                      const mgnOT = comp.marginOT != null ? (comp.marginOT * 100).toFixed(2) : null;
                                      const box = { display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' };
                                      return (<>
                                        <label style={box}>
                                          <span style={{ fontSize: 10, width: 16 }}>ST</span>
                                          <input type="text" inputMode="decimal"
                                            key={`submk-${line.id}-${line.pay_st}-${line.bill_st}-${line.subcontractor_markup}`}
                                            style={{ ...styles.input, width: 56, fontSize: 12, fontWeight: 700,
                                              color: comp.marginST != null && comp.marginST < thr ? ORANGE : colors.green }}
                                            defaultValue={held != null ? Math.round(held * 1000) / 10 : ''}
                                            disabled={!(payST > 0)}
                                            title={payST > 0
                                              ? `Markup over the sub cost — sets Bill ST = ${fmtMoney(payST)} × (1 + markup) AND Bill OT = ${fmtMoney(payOT)} × (1 + markup)${Number(line.pay_ot) > 0 ? '' : ' (Pay OT seeded at 1.5× ST)'}.${mgn != null ? ` Resulting ST margin: ${mgn}%.` : ''} Use the OT box below to give OT its own markup.`
                                              : 'Enter the sub Pay ST first — the markup has nothing to mark up yet.'}
                                            onBlur={e => {
                                              if (e.target.value === '') return;
                                              const m = Number(e.target.value) / 100;
                                              if (isNaN(m) || m < 0 || m >= 100 || !(payST > 0)) return;
                                              const body = { subcontractor_markup: Math.round(m * 10000) / 10000,
                                                bill_st: Math.round(payST * (1 + m) * 100) / 100 };
                                              if (!(Number(line.pay_ot) > 0)) body.pay_ot = payOT;
                                              if (payOT > 0) body.bill_ot = Math.round(payOT * (1 + m) * 100) / 100;
                                              updateLabor(line.id, body);
                                            }} />
                                          %
                                        </label>
                                        <label style={{ ...box, marginTop: 2 }}>
                                          <span style={{ fontSize: 10, width: 16 }}>OT</span>
                                          <input type="text" inputMode="decimal"
                                            key={`submkot-${line.id}-${line.pay_ot}-${line.bill_ot}-${line.subcontractor_markup}`}
                                            style={{ ...styles.input, width: 56, fontSize: 12, fontWeight: 700,
                                              color: comp.marginOT != null && comp.marginOT < otThr ? ORANGE : colors.green }}
                                            defaultValue={heldOT != null ? Math.round(heldOT * 1000) / 10 : ''}
                                            disabled={!(Number(line.pay_ot) > 0)}
                                            title={Number(line.pay_ot) > 0
                                              ? `OT markup over the sub OT cost — sets Bill OT = ${fmtMoney(payOT)} × (1 + markup) only; Bill ST is untouched.${mgnOT != null ? ` Resulting OT margin: ${mgnOT}%.` : ''} Retyping the ST % resets OT to match it.`
                                              : 'Enter the sub Pay OT first (or type the ST % — it seeds Pay OT at 1.5× ST).'}
                                            onBlur={e => {
                                              if (e.target.value === '') return;
                                              const m = Number(e.target.value) / 100;
                                              const po = Number(line.pay_ot) || 0;
                                              if (isNaN(m) || m < 0 || m >= 100 || !(po > 0)) return;
                                              const newOT = Math.round(po * (1 + m) * 100) / 100;
                                              if (Math.abs(newOT - (Number(line.bill_ot) || 0)) < 0.005) return;
                                              updateLabor(line.id, { bill_ot: newOT });
                                            }} />
                                          %
                                        </label>
                                      </>);
                                    })() : null}
                                    {line.labor_type !== 'Subcontractor' && comp.markupST != null ? <div>ST {pct(comp.markupST)}</div> : null}
                                    {line.labor_type !== 'Subcontractor' && comp.markupOT != null ? <div>OT {pct(comp.markupOT)}</div> : null}
                                    {line.labor_type !== 'Subcontractor' && comp.markupST == null && comp.markupOT == null ? dash : null}
                                    <Unburdened st={comp.rawMarkupST} ot={comp.rawMarkupOT}
                                      title="Unburdened — (Bill − Pay) ÷ Pay, before our burden. The wage multiplier: 62.50% here means we bill 1.625× the wage. Not the same profit as the burdened figure above — the burden we pay out is still inside it." />
                                  </td>
                                </>
                                );
                              })()}
                              <td style={styles.td}>
                                {!line.rate_only && (() => {
                                  const filledPhases = (line.phases || []).filter(ph =>
                                    ph.start_date && ph.end_date &&
                                    ((Number(ph.day_hc) || 0) + (Number(ph.night_hc) || 0)) > 0);
                                  const filled = filledPhases.length;
                                  const hasDay = filledPhases.some(ph => (Number(ph.day_hc) || 0) > 0);
                                  const hasNight = filledPhases.some(ph => (Number(ph.night_hc) || 0) > 0);
                                  const shiftChip = hasDay && hasNight ? '☀️/🌙 Day+Night'
                                    : hasDay ? '☀️ Day' : hasNight ? '🌙 Night' : null;
                                  return (<>
                                    {shiftChip && (
                                      <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 3, textAlign: 'center',
                                        color: hasNight && !hasDay ? '#4338CA' : '#B45309' }}>
                                        {shiftChip}
                                      </div>
                                    )}
                                    <button
                                      title={filled
                                        ? `${filled} phase${filled > 1 ? 's' : ''} entered`
                                        : 'No phases yet — line books no hours'}
                                      style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px',
                                        ...(filled
                                          ? { background: '#ECFDF5', border: '1px solid #10B981', color: '#047857', fontWeight: 700 }
                                          : { border: '1px solid #F59E0B', background: '#FFFBEB', color: '#B45309' }) }}
                                      onClick={() => setExpandedPhase(expandedPhase === line.id ? null : line.id)}>
                                      {expandedPhase === line.id ? 'Close'
                                        : filled ? `✓ Phases (${filled})` : 'Phases'}
                                    </button>
                                    {' '}
                                  </>);
                                })()}
                                {!line.rate_only && (
                                  <button title="Copy this line's phases / per diem / miles to the selected lines"
                                    style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 7px', marginRight: 4 }}
                                    onClick={() => setCopyPanel({ sourceId: line.id })}>⎘</button>
                                )}
                                <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 8px' }}
                                  onClick={() => deleteLabor(line.id)}>Del</button>
                              </td>
                            </tr>
                            <LaborLineSummary line={line} comp={comp} bg={groupBg} />
                            {expandedPhase === line.id && (
                              <tr key={`ph-${line.id}`}>
                                <td colSpan={18} style={{ background: '#F8FAFC', padding: 12 }}>
                                  <PhaseEditor
                                    phases={line.phases || []}
                                    otRule={current.ot_rule}
                                    priorWeekHours={line.prior_week_hours}
                                    isFco={current.change_order_of_estimate_id != null}
                                    onSave={(phases) => savePhases(line.id, phases)}
                                  />
                                  <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                                    <LaborExtrasEditor line={line} estimateId={current.id} otRule={current.ot_rule} isFco={current.change_order_of_estimate_id != null} onSave={(b) => updateLabor(line.id, b)} />
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                      {laborCtl.view.length > 0 && (() => {
                        let st = 0, ot = 0, pdDays = 0, pdRev = 0, rev = 0, cost = 0, mob = 0, rawCost = 0;
                        for (const l of laborCtl.view) {
                          const comp = (current.computed?.laborLines || []).find(c => c.id === l.id) || {};
                          st += Number(comp.stHours) || 0; ot += Number(comp.otHours) || 0;
                          pdDays += Number(comp.perDiemDays) || 0; pdRev += Number(comp.pdRevenue) || 0;
                          rev += Number(comp.laborRevenue) || 0; cost += Number(comp.laborTrueCost) || 0;
                          mob += Number(comp.mobRevenue) || 0; rawCost += Number(comp.rawLaborCost) || 0;
                        }
                        const gp = rev - cost;
                        const tMargin = rev > 0 ? gp / rev : null;
                        const tMarkup = cost > 0 ? gp / cost : null;
                        // Blended unburdened. Unlike the per-line pair, this DOES include
                        // subcontractor lines at their sub rate — blended, the honest
                        // question is revenue vs everything we hand out in pay.
                        const rawGp = rev - rawCost;
                        const tRawMargin = rev > 0 ? rawGp / rev : null;
                        const tRawMarkup = rawCost > 0 ? rawGp / rawCost : null;
                        const thr = Number(current.margin_threshold || 0);
                        return (
                          <tr style={{ borderTop: '2px solid #1F2937', background: '#F9FAFB', fontWeight: 700 }}>
                            <td colSpan={9} style={{ ...styles.td, textAlign: 'right' }}>
                              Totals
                              <span style={{ fontWeight: 400, color: '#9CA3AF' }}> · per diem {pdDays.toLocaleString()} days</span>
                            </td>
                            <td style={{ ...styles.td, fontSize: 12 }}>{st.toLocaleString()}</td>
                            <td style={{ ...styles.td, fontSize: 12 }}>{ot.toLocaleString()}</td>
                            <td style={{ ...styles.td, fontSize: 12 }}>{fmtMoney(rev)}</td>
                            <td style={{ ...styles.td, fontSize: 12, color: '#DC2626' }}>{fmtMoney(cost)}</td>
                            <td style={{ ...styles.td, fontSize: 12 }}>{fmtMoney(pdRev)}</td>
                            <td style={{ ...styles.td, fontSize: 12 }}>{fmtMoney(mob)}</td>
                            <td style={{ ...styles.td, fontSize: 12, color: tMargin != null && tMargin < thr ? ORANGE : colors.green }}
                              title="Blended labor margin (per diem & mob excluded)">
                              {tMargin != null ? pct(tMargin) : '—'}
                              {tRawMargin != null && (
                                <div style={{ fontWeight: 400, fontSize: 10, color: '#9CA3AF' }}
                                  title="Unburdened — revenue vs pay/sub rates, before our burden. Subcontractor lines ARE included here at their sub rate.">
                                  unburdened {pct(tRawMargin)}
                                </div>
                              )}
                            </td>
                            <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}
                              title="Blended labor markup = GP ÷ burdened cost (per diem & mob excluded)">
                              {tMarkup != null ? pct(tMarkup) : '—'}
                              {tRawMarkup != null && (
                                <div style={{ fontWeight: 400, fontSize: 10, color: '#9CA3AF' }}
                                  title="Unburdened — revenue vs pay/sub rates, before our burden. Subcontractor lines ARE included here at their sub rate.">
                                  unburdened {pct(tRawMarkup)}
                                </div>
                              )}
                            </td>
                            <td style={styles.td}></td>
                          </tr>
                        );
                      })()}
                      {(current.labor_lines || []).length === 0 && (
                        <tr><td colSpan={18} style={{ ...styles.td, textAlign: 'center', color: '#9CA3AF' }}>No labor lines.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Equipment section */}
            {section === 'equipment' && (
              <div style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                  <h2 style={{ ...styles.h2, margin: 0 }}>Equipment</h2>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input style={{ ...styles.input, width: 200, fontSize: 12 }}
                      placeholder="Search catalog…"
                      value={catalogQ}
                      onChange={e => setCatalogQ(e.target.value)} />
                    <button style={{ ...styles.btnSecondary, fontSize: 12, whiteSpace: 'nowrap' }}
                      title="Add a line that isn't in the catalog — free-text description, you set type and rates"
                      disabled={saving}
                      onClick={() => {
                        const desc = catalogQ.trim() || prompt('Description for the custom equipment line:');
                        if (!desc) return;
                        doSave(async () => {
                          const est = await api.addEquipmentLine(current.id, {
                            description: desc, equip_type: 'Utilization', qty: 1, num_days: 0, day_rate: 0,
                          });
                          setCurrent(est); setCatalogQ('');
                        });
                      }}>
                      + Custom Item
                    </button>
                  </div>
                </div>
                {catalogQ && catalog.length > 0 && (
                  <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 10, maxHeight: 200, overflowY: 'auto' }}>
                    {catalog.slice(0, 30).map(item => (
                      <div key={item.id}
                        onClick={() => { addEquipment(item); setCatalogQ(''); }}
                        style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', gap: 8 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FFF7ED'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span>
                          <strong>{item.description}</strong> <span style={{ color: '#9CA3AF' }}>{item.category}</span>
                          {item.cat_class && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#374151', background: '#E5E7EB', padding: '1px 5px', borderRadius: 6 }}>{item.cat_class}</span>}
                        </span>
                        {item.unit_count > 1 && <span style={{ color: '#6B7280', fontSize: 11, flexShrink: 0 }}>{item.unit_count} units</span>}
                      </div>
                    ))}
                  </div>
                )}
                <TableControlsBar ctl={equipCtl} columns={equipCols} total={equipRows.length} />
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead><tr>
                      <SortTh ctl={equipCtl} k="description" style={styles.th}>Description</SortTh>
                      <SortTh ctl={equipCtl} k="cat_class" style={styles.th} title="Rental Man CAT-CLASS (e.g. 350-14). Follows the line to the Equipment Gantt stock match — blank lines can't be checked against owned stock.">CAT-CLASS</SortTh>
                      <th style={styles.th}>MSA Description</th>
                      <SortTh ctl={equipCtl} k="equip_type" style={styles.th}>Type</SortTh>
                      <SortTh ctl={equipCtl} k="qty" style={styles.th}>Qty</SortTh>
                      <th style={styles.th} title="On-site start — sets this line's window in the Equipment Gantt">Start</th>
                      <th style={styles.th} title="On-site end — Days auto-fills from Start→End (inclusive)">End</th>
                      <SortTh ctl={equipCtl} k="num_days" style={styles.th} title="Billing days — auto-set from Start→End when both are picked; otherwise enter manually (a dateless line spans the whole job in the Gantt). On hourly lines (/hr) this column holds billable HOURS per unit and never auto-fills from dates">Days/Hrs</SortTh>
                      <SortTh ctl={equipCtl} k="day_rate" style={styles.th}>Usage Rate</SortTh>
                      <SortTh ctl={equipCtl} k="total" style={styles.th} title="Line total — Qty × Days (or Hrs) × Usage Rate. Non-Consignment bills at 100% always; Utilization lines are metered at the selected tier">Total</SortTh>
                      <SortTh ctl={equipCtl} k="margin" style={styles.th}>Margin</SortTh>
                      <th style={styles.th}></th>
                    </tr></thead>
                    <tbody>
                      {equipCtl.view.map(line => {
                        const comp = (current.computed?.equipmentLines || []).find(e => e.id === line.id) || {};
                        const setDates = (patch) => {
                          const s = patch.start_date !== undefined ? patch.start_date : line.start_date;
                          const en = patch.end_date !== undefined ? patch.end_date : line.end_date;
                          // Hourly lines keep their hours — dates are window info only.
                          if (s && en && line.rate_unit !== 'hour') {
                            const span = Math.floor((new Date(en) - new Date(s)) / 86400000) + 1;
                            if (span > 0) patch.num_days = span;
                          }
                          return updateEquip(line.id, patch);
                        };
                        const phaseCount = (line.phases || []).filter(p => p.start_date && p.end_date && (Number(p.qty) || 0) > 0).length;
                        return (
                          <>
                          <tr key={line.id} style={{ background: comp.belowThreshold ? '#FFF7ED' : undefined }}>
                            <td style={styles.td}>
                              <input style={{ ...styles.input, width: 180, fontSize: 12 }}
                                defaultValue={line.description}
                                onBlur={e => updateEquip(line.id, { description: e.target.value })} />
                              <LastQuoted hint={priceHints?.equipment?.[line.id]}
                                onOpen={() => openPrice('equipment', line.id, line.description)} />
                            </td>
                            <td style={styles.td}>
                              <input style={{ ...styles.input, width: 78, fontSize: 12,
                                  ...(!line.cat_class ? { border: '1px solid #F59E0B', background: '#FFFBEB' } : {}) }}
                                placeholder="e.g. 350-14"
                                defaultValue={line.cat_class || ''}
                                onBlur={e => updateEquip(line.id, { cat_class: e.target.value.trim() || null })} />
                              {!line.cat_class && (
                                <div title="CAT-CLASS is the join key that follows this line to Inventory and field-services — lines without one can't be matched to Rental Man stock"
                                  style={{ fontSize: 10, fontWeight: 700, color: '#B45309', marginTop: 2 }}>⚠ no CAT-CLASS</div>
                              )}
                            </td>
                            <td style={styles.td}>
                              <input style={{ ...styles.input, width: 170, fontSize: 12,
                                  ...(current.msa_wording_required && !line.msa_description ? { borderColor: '#DC2626' } : {}) }}
                                key={`msad-${line.id}-${line.msa_description || ''}`}
                                placeholder={current.msa_wording_required ? 'MSA wording (required)…' : 'Quote description…'}
                                defaultValue={line.msa_description || ''}
                                onBlur={e => updateEquip(line.id, { msa_description: e.target.value || null })} />
                              {!line.msa_description && (
                                <MsaPick kind="equipment" sheet={msaSheet} text={line.description}
                                  onPick={l => updateEquip(line.id, {
                                    msa_description: l.msa_description,
                                    ...(l.day_rate != null ? { day_rate: Number(l.day_rate) } : {}),
                                  })} />
                              )}
                            </td>
                            <td style={styles.td}>
                              <select style={{ ...styles.input, width: 130, fontSize: 12 }}
                                value={line.equip_type}
                                onChange={e => updateEquip(line.id, { equip_type: e.target.value })}>
                                {EQUIP_TYPES.map(t => <option key={t}>{t}</option>)}
                              </select>
                            </td>
                            <td style={styles.td}>
                              <input type="number" min="0" style={{ ...styles.input, width: 84, padding: '8px 6px', fontSize: 12 }}
                                defaultValue={line.qty}
                                onBlur={e => updateEquip(line.id, { qty: Number(e.target.value) })} />
                            </td>
                            <td style={styles.td}>
                              <BlurDateInput style={{ ...styles.input, width: 120, fontSize: 11 }}
                                value={line.start_date}
                                onCommit={v => setDates({ start_date: v })} />
                            </td>
                            <td style={styles.td}>
                              <BlurDateInput style={{ ...styles.input, width: 120, fontSize: 11 }}
                                min={line.start_date ? String(line.start_date).slice(0, 10) : undefined}
                                value={line.end_date}
                                onCommit={v => setDates({ end_date: v })} />
                              {line.start_date && line.end_date && (
                                <button title="Carry these dates (and Days) down to every equipment line"
                                  style={{ ...styles.btnSecondary, fontSize: 10, padding: '2px 5px', marginLeft: 3 }}
                                  onClick={() => applyEquipDatesToAll(line)}>⤓ all</button>
                              )}
                              {line.start_date && line.end_date && String(line.end_date).slice(0, 10) < String(line.start_date).slice(0, 10) && (
                                <div style={{ color: '#B91C1C', fontSize: 10, fontWeight: 700 }}>ends before start</div>
                              )}
                            </td>
                            <td style={styles.td}>
                              <input type="number" min="0" style={{ ...styles.input, width: 60, fontSize: 12 }}
                                key={`d-${line.num_days}`}
                                defaultValue={line.num_days}
                                title={line.rate_unit === 'hour' ? 'Billable HOURS per unit (hourly line)' : 'Enter # of days to fill End from Start (inclusive)'}
                                onBlur={e => {
                                  const days = Number(e.target.value) || 0;
                                  const s = line.start_date ? String(line.start_date).slice(0, 10) : '';
                                  if (s && days > 0 && line.rate_unit !== 'hour') setDates({ end_date: endFromDays(s, days) });
                                  else updateEquip(line.id, { num_days: days });
                                }} />
                              {line.rate_unit === 'hour' && <span style={{ fontSize: 10, color: '#6B7280', marginLeft: 2 }}>hr</span>}
                            </td>
                            <td style={styles.td}>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 12, color: '#6B7280' }}>$
                              <input type="number" min="0" step="0.01" style={{ ...styles.input, width: 80, fontSize: 12 }}
                                defaultValue={line.day_rate}
                                onBlur={e => updateEquip(line.id, { day_rate: Number(e.target.value) })} />
                              </label>
                              <select title="Billing basis — /day: Qty × Days × rate. /hr: Qty × Hours × rate (Days/Hrs column holds hours; dates never overwrite it)"
                                style={{ ...styles.input, width: 52, fontSize: 11, padding: '4px 2px', marginLeft: 2 }}
                                value={line.rate_unit === 'hour' ? 'hour' : 'day'}
                                onChange={e => updateEquip(line.id, { rate_unit: e.target.value })}>
                                <option value="day">/day</option>
                                <option value="hour">/hr</option>
                              </select>
                            </td>
                            <td style={{ ...styles.td, fontWeight: 600, fontSize: 12 }}>
                              {fmtMoney(comp.revenueSelected)}
                              {line.equip_type === 'Utilization' && (
                                <span style={{ fontWeight: 400, color: '#9CA3AF', fontSize: 10 }}> @ {current.utilization_tier}%</span>
                              )}
                            </td>
                            <td style={{ ...styles.td, fontWeight: 700, color: comp.belowThreshold ? ORANGE : colors.green, fontSize: 12 }}>
                              {comp.margin != null ? pct(comp.margin) : '--'}
                            </td>
                            <td style={styles.td}>
                              <button
                                title="Per-phase ramp — set qty + dates per phase (Pre-TA / Mechanical / Post-TA). Leave empty to bill the flat line."
                                style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', marginRight: 4,
                                  ...(phaseCount ? { background: '#ECFDF5', border: '1px solid #10B981', color: '#047857', fontWeight: 700 } : {}) }}
                                onClick={() => setExpandedEquipPhase(expandedEquipPhase === line.id ? null : line.id)}>
                                {expandedEquipPhase === line.id ? 'Close' : phaseCount ? `✓ Phases (${phaseCount})` : 'Phases'}
                              </button>
                              <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', marginRight: 4 }}
                                title="Duplicate this line (same description / CAT-CLASS / rate) — set a new qty + dates for a ramp up or down"
                                onClick={() => duplicateEquip(line)}>Dup</button>
                              <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 8px' }}
                                onClick={() => deleteEquip(line.id)}>Del</button>
                            </td>
                          </tr>
                          {expandedEquipPhase === line.id && (
                            <tr><td colSpan={12} style={{ background: '#F8FAFC', padding: 12 }}>
                              <EquipPhaseEditor phases={line.phases || []} rate={line.day_rate}
                                onSave={(phases) => saveEquipPhases(line.id, phases)} />
                            </td></tr>
                          )}
                          </>
                        );
                      })}
                      {equipCtl.view.length > 0 && (() => {
                        const byType = {};
                        for (const l of equipCtl.view) {
                          const comp = (current.computed?.equipmentLines || []).find(e => e.id === l.id) || {};
                          const t = l.equip_type || 'Utilization';
                          byType[t] = (byType[t]||0) + (Number(comp.revenueSelected) || 0);
                        }
                        return (<>
                          {EQUIP_TYPES.filter(t => byType[t] != null).map(t => (
                            <tr key={`et-${t}`} style={{ background: '#F9FAFB' }}>
                              <td colSpan={7} style={{ ...styles.td, fontWeight: 600, textAlign: 'right', color: '#6B7280' }}>
                                {t} total{t === 'Utilization' ? ` (@ ${current.utilization_tier}%)` : ''}
                              </td>
                              <td style={{ ...styles.td, fontWeight: 600, color: '#6B7280' }}>{fmtMoney(byType[t])}</td>
                              <td colSpan={2} style={styles.td}></td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: '2px solid #1F2937' }}>
                            <td colSpan={7} style={{ ...styles.td, fontWeight: 700, textAlign: 'right' }}>Tab total</td>
                            <td style={{ ...styles.td, fontWeight: 700 }}>
                              {fmtMoney(Object.values(byType).reduce((s,v) => s+v, 0))}
                            </td>
                            <td colSpan={2} style={styles.td}></td>
                          </tr>
                        </>);
                      })()}
                      {equipRows.length === 0 && (
                        <tr><td colSpan={10} style={{ ...styles.td, textAlign: 'center', color: '#9CA3AF' }}>
                          No equipment lines. Search the catalog above, or + Custom Item for anything not in it.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {(current.equipment_lines || []).filter(l => l.equip_type !== 'Third-Party').length > 0 && (() => {
                  const tiers = [['50', 'revenue50'], ['70', 'revenue70'], ['100', 'revenue100']];
                  const byTier = { revenue50: {}, revenue70: {}, revenue100: {} };
                  for (const l of current.equipment_lines.filter(l => l.equip_type !== 'Third-Party')) {
                    const comp = (current.computed?.equipmentLines || []).find(e => e.id === l.id) || {};
                    const t = l.equip_type || 'Utilization';
                    for (const [, k] of tiers) byTier[k][t] = (byTier[k][t] || 0) + (Number(comp[k]) || 0);
                  }
                  return (
                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12 }}>
                      <span style={{ fontWeight: 700, color: '#374151' }}>Equipment totals by utilization tier</span>
                      {tiers.map(([tier, k]) => {
                        const m = byTier[k];
                        const tabTotal = Object.values(m).reduce((s, v) => s + v, 0);
                        const sel = String(current.utilization_tier) === tier;
                        return (
                          <div key={tier} style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 4, fontWeight: sel ? 700 : 400, color: sel ? '#111827' : '#6B7280' }}>
                            <span style={{ width: 64 }}>@ {tier}%{sel ? ' ✓' : ''}</span>
                            {EQUIP_TYPES.filter(t => m[t] != null).map(t => (
                              <span key={t}>{t}: <strong>{fmtMoney(m[t])}</strong></span>
                            ))}
                            <span>Tab total: <strong>{fmtMoney(tabTotal)}</strong></span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                {(() => {
                  const mobRow = (current.computed?.rollupSelected?.rows || []).find(r => r.category === 'Employee Mobilization');
                  if (!mobRow || mobRow.status === 'EMPTY') return null;
                  return (
                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: '#374151' }}
                        title="Employee mobilization mileage from the Labor tab (mob miles × $/mi × heads, both ways). Equipment/freight mobilization lives on the Consumables tab under category 'Mobilization'.">
                        Employee Mobilization (labor mob miles)</span>
                      <span>Bill: <strong>{fmtMoney(mobRow.revenue)}</strong></span>
                      <span>Cost: <strong>{fmtMoney(mobRow.cost)}</strong></span>
                      <span>GP: <strong>{fmtMoney(mobRow.grossProfit)}</strong></span>
                      <span style={{ fontWeight: 700, color: mobRow.grossProfit >= 0 ? colors.green : '#DC2626' }}>
                        Margin: {pct(mobRow.margin)}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Third-Party Rentals section */}
            {section === 'thirdparty' && (() => {
              return (
                <div style={styles.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                    <h2 style={{ ...styles.h2, margin: 0 }}>Third-Party Rentals</h2>
                    <button style={styles.btn} disabled={saving} onClick={() => {
                      const desc = prompt('Description for the re-rented item:');
                      if (!desc) return;
                      doSave(async () => {
                        const est = await api.addEquipmentLine(current.id, {
                          description: desc, equip_type: 'Third-Party', qty: 1, num_days: 0, day_rate: 0,
                        });
                        setCurrent(est);
                      });
                    }}>+ Rental Line</button>
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>
                    Re-rented equipment. Cost = the vendor's day/week/month ladder over the rental span
                    (best-rate caps, or every calendar day). Billing = your usage rate × qty × days, as
                    Non-Consignment (100%) or Utilization (@ the selected tier). Flip any Equipment line's
                    Type to Third-Party and it moves here; <strong>↩ Equip</strong> on a row sends it back
                    to the Equipment tab.
                  </div>
                  <TableControlsBar ctl={tpCtl} columns={tpCols} total={tpRows.length} />
                  <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                      <thead><tr>
                        <SortTh ctl={tpCtl} k="description" style={styles.th}>Description</SortTh>
                        <SortTh ctl={tpCtl} k="msa_description" style={styles.th} title="What the CUSTOMER sees on the quote for this line. Blank = the Description is used. Carries over when a line is flipped between Equipment and Third-Party.">MSA Description</SortTh>
                        <SortTh ctl={tpCtl} k="vendor" style={styles.th}>Vendor</SortTh>
                        <th style={styles.th}>Vendor Contact</th>
                        <th style={styles.th}>Start</th>
                        <th style={styles.th}>End</th>
                        <SortTh ctl={tpCtl} k="num_days" style={styles.th} title="Billing days — auto-set from the dates when both are picked">Days</SortTh>
                        <SortTh ctl={tpCtl} k="qty" style={styles.th}>Qty</SortTh>
                        <th style={styles.th} title="Vendor's rate ladder. Best rate applies the week/month caps (handles 3-day weeks and 7-day months); Every day pays the day rate for every calendar day.">Vendor $/day · /wk · /mo</th>
                        <th style={styles.th}>Vendor Bills</th>
                        <SortTh ctl={tpCtl} k="cost" style={{ ...styles.th, color: '#DC2626' }} title="Ladder cost over the span × qty">Our Cost</SortTh>
                        <SortTh ctl={tpCtl} k="day_rate" style={styles.th} title="What we charge the customer per day">Bill Usage Rate</SortTh>
                        <th style={styles.th} title="Non-Consignment bills qty × days × rate at 100%. Utilization meters the same gross at the selected tier.">Bill As</th>
                        <SortTh ctl={tpCtl} k="revenue" style={styles.th}>Revenue</SortTh>
                        <th style={styles.th}>GP</th>
                        <SortTh ctl={tpCtl} k="margin" style={styles.th}>Margin</SortTh>
                        <th style={styles.th}>SSS PO # / Vendor Quote #</th>
                        <th style={styles.th}>Quote</th>
                        <th style={styles.th}></th>
                      </tr></thead>
                      <tbody>
                        {tpCtl.view.map(line => {
                          const comp = (current.computed?.equipmentLines || []).find(e => e.id === line.id) || {};
                          const gp = (Number(comp.revenueSelected) || 0) - (Number(comp.cost) || 0);
                          const setDates = (patch) => {
                            const s = patch.start_date !== undefined ? patch.start_date : line.start_date;
                            const en = patch.end_date !== undefined ? patch.end_date : line.end_date;
                            if (s && en) {
                              const span = Math.floor((new Date(en) - new Date(s)) / 86400000) + 1;
                              if (span > 0) patch.num_days = span;
                            }
                            return updateEquip(line.id, patch);
                          };
                          return (
                            <tr key={line.id} style={{ background: comp.belowThreshold ? '#FFF7ED' : undefined }}>
                              <td style={styles.td}>
                                <input style={{ ...styles.input, width: 150, fontSize: 12 }}
                                  defaultValue={line.description}
                                  onBlur={e => updateEquip(line.id, { description: e.target.value })} />
                                <LastQuoted hint={priceHints?.equipment?.[line.id]}
                                  onOpen={() => openPrice('equipment', line.id, line.description)} />
                              </td>
                              <td style={styles.td}>
                                <input style={{ ...styles.input, width: 150, fontSize: 12,
                                    ...(current.msa_wording_required && !line.msa_description ? { borderColor: '#DC2626' } : {}) }}
                                  key={`msad-${line.id}-${line.msa_description || ''}`}
                                  placeholder={current.msa_wording_required ? 'MSA wording (required)…' : 'Quote description…'}
                                  defaultValue={line.msa_description || ''}
                                  onBlur={e => updateEquip(line.id, { msa_description: e.target.value || null })} />
                              </td>
                              <td style={styles.td}>
                                <input style={{ ...styles.input, width: 110, fontSize: 12 }}
                                  placeholder="Vendor"
                                  defaultValue={line.vendor_name || ''}
                                  onBlur={e => updateEquip(line.id, { vendor_name: e.target.value || null })} />
                              </td>
                              <td style={styles.td}>
                                <input style={{ ...styles.input, width: 130, fontSize: 12 }}
                                  placeholder="Name / phone / email"
                                  defaultValue={line.vendor_contact || ''}
                                  onBlur={e => updateEquip(line.id, { vendor_contact: e.target.value || null })} />
                              </td>
                              <td style={styles.td}>
                                <BlurDateInput style={{ ...styles.input, width: 120, fontSize: 11 }}
                                  value={line.start_date}
                                  onCommit={v => setDates({ start_date: v })} />
                              </td>
                              <td style={styles.td}>
                                <BlurDateInput style={{ ...styles.input, width: 120, fontSize: 11 }}
                                  min={line.start_date ? String(line.start_date).slice(0, 10) : undefined}
                                  value={line.end_date}
                                  onCommit={v => setDates({ end_date: v })} />
                                {line.start_date && line.end_date && String(line.end_date).slice(0, 10) < String(line.start_date).slice(0, 10) && (
                                  <div style={{ color: '#B91C1C', fontSize: 10, fontWeight: 700 }}>ends before start</div>
                                )}
                              </td>
                              <td style={styles.td}>
                                <input type="number" min="0" style={{ ...styles.input, width: 52, fontSize: 12 }}
                                  key={`d-${line.num_days}`}
                                  defaultValue={line.num_days}
                                  title="Enter # of days to fill End from Start (inclusive)"
                                  onBlur={e => {
                                    const days = Number(e.target.value) || 0;
                                    const s = line.start_date ? String(line.start_date).slice(0, 10) : '';
                                    if (s && days > 0) setDates({ end_date: endFromDays(s, days) });
                                    else updateEquip(line.id, { num_days: days });
                                  }} />
                              </td>
                              <td style={styles.td}>
                                <input type="number" min="0" style={{ ...styles.input, width: 84, padding: '8px 6px', fontSize: 12 }}
                                  defaultValue={line.qty}
                                  onBlur={e => updateEquip(line.id, { qty: Number(e.target.value) })} />
                              </td>
                              <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                                <input type="number" min="0" step="0.01" style={{ ...styles.input, width: 62, fontSize: 12 }}
                                  placeholder="day" title="Vendor day rate"
                                  defaultValue={Number(line.vendor_day_rate) || ''}
                                  onBlur={e => updateEquip(line.id, { vendor_day_rate: Number(e.target.value) || null })} />
                                <input type="number" min="0" step="0.01" style={{ ...styles.input, width: 66, fontSize: 12, marginLeft: 3 }}
                                  placeholder="week" title="Vendor week rate (cap on 7 days)"
                                  defaultValue={Number(line.vendor_week_rate) || ''}
                                  onBlur={e => updateEquip(line.id, { vendor_week_rate: Number(e.target.value) || null })} />
                                <input type="number" min="0" step="0.01" style={{ ...styles.input, width: 70, fontSize: 12, marginLeft: 3 }}
                                  placeholder="month" title="Vendor month rate (cap on 28 days)"
                                  defaultValue={Number(line.vendor_month_rate) || ''}
                                  onBlur={e => updateEquip(line.id, { vendor_month_rate: Number(e.target.value) || null })} />
                              </td>
                              <td style={styles.td}>
                                <select style={{ ...styles.input, width: 96, fontSize: 11 }}
                                  value={line.vendor_bill_mode || 'best_rate'}
                                  onChange={e => updateEquip(line.id, { vendor_bill_mode: e.target.value })}>
                                  <option value="best_rate">Best rate</option>
                                  <option value="calendar">Every day</option>
                                </select>
                              </td>
                              <td style={{ ...styles.td, color: '#DC2626', fontWeight: 600, fontSize: 12 }}>
                                {fmtMoney(comp.cost)}
                              </td>
                              <td style={styles.td}>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 12, color: '#6B7280' }}>$
                                <input type="number" min="0" step="0.01" style={{ ...styles.input, width: 72, fontSize: 12 }}
                                  defaultValue={line.day_rate}
                                  onBlur={e => updateEquip(line.id, { day_rate: Number(e.target.value) })} />
                                </label>
                              </td>
                              <td style={styles.td}>
                                <select style={{ ...styles.input, width: 150, fontSize: 11 }}
                                  value={line.tp_bill_basis || 'straight'}
                                  onChange={e => updateEquip(line.id, { tp_bill_basis: e.target.value })}>
                                  <option value="straight">Non-Consignment (100%)</option>
                                  <option value="utilization">Utilization (@ tier)</option>
                                </select>
                              </td>
                              <td style={{ ...styles.td, fontWeight: 600, fontSize: 12 }}>
                                {fmtMoney(comp.revenueSelected)}
                                {(line.tp_bill_basis === 'utilization') && (
                                  <span style={{ fontWeight: 400, color: '#9CA3AF', fontSize: 10 }}> @ {current.utilization_tier}%</span>
                                )}
                              </td>
                              <td style={{ ...styles.td, fontWeight: 600, fontSize: 12, color: gp >= 0 ? colors.green : '#DC2626' }}>
                                {fmtMoney(gp)}
                              </td>
                              <td style={{ ...styles.td, fontWeight: 700, color: comp.belowThreshold ? ORANGE : colors.green, fontSize: 12 }}>
                                {comp.margin != null ? pct(comp.margin) : '--'}
                              </td>
                              <td style={styles.td}>
                                <input style={{ ...styles.input, width: 110, fontSize: 11, marginBottom: 3, display: 'block' }}
                                  placeholder="SSS PO #"
                                  defaultValue={line.po_number || ''}
                                  onBlur={e => updateEquip(line.id, { po_number: e.target.value.trim() || null })} />
                                <input style={{ ...styles.input, width: 110, fontSize: 11, display: 'block' }}
                                  placeholder="Vendor quote #"
                                  defaultValue={line.vendor_quote_number || ''}
                                  onBlur={e => updateEquip(line.id, { vendor_quote_number: e.target.value.trim() || null })} />
                              </td>
                              <td style={styles.td}>
                                <VendorQuoteFiles lineId={line.id} count={line.file_count || 0} />
                              </td>
                              <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                                <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', marginRight: 4 }}
                                  title="Move this line back to the Equipment tab (owned gear, no vendor cost). Utilization-billed goes back as Utilization; straight-billed as Non-Consignment. Vendor info stays saved on the line in case it flips again."
                                  onClick={() => updateEquip(line.id, { equip_type: line.tp_bill_basis === 'straight' ? 'Non-Consignment' : 'Utilization' })}>
                                  ↩ Equip
                                </button>
                                <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', marginRight: 4 }}
                                  title="Email a quote request for this line to the vendor — you're CC'd"
                                  onClick={() => emailEquipVendor(line)}>
                                  📧
                                </button>
                                <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 8px' }}
                                  onClick={() => deleteEquip(line.id)}>Del</button>
                              </td>
                            </tr>
                          );
                        })}
                        {tpCtl.view.length > 0 && (() => {
                          const tot = tpCtl.view.reduce((s, l) => {
                            const c = (current.computed?.equipmentLines || []).find(e => e.id === l.id) || {};
                            s.cost += Number(c.cost) || 0; s.rev += Number(c.revenueSelected) || 0;
                            return s;
                          }, { cost: 0, rev: 0 });
                          return (
                            <tr style={{ borderTop: '2px solid #1F2937' }}>
                              <td colSpan={10} style={{ ...styles.td, fontWeight: 700, textAlign: 'right' }}>Tab total</td>
                              <td style={{ ...styles.td, fontWeight: 700, color: '#DC2626' }}>{fmtMoney(tot.cost)}</td>
                              <td colSpan={2} style={styles.td}></td>
                              <td style={{ ...styles.td, fontWeight: 700 }}>{fmtMoney(tot.rev)}</td>
                              <td style={{ ...styles.td, fontWeight: 700, color: (tot.rev - tot.cost) >= 0 ? colors.green : '#DC2626' }}>{fmtMoney(tot.rev - tot.cost)}</td>
                              <td style={{ ...styles.td, fontWeight: 700 }}>{tot.rev > 0 ? pct((tot.rev - tot.cost) / tot.rev) : '--'}</td>
                              <td colSpan={3} style={styles.td}></td>
                            </tr>
                          );
                        })()}
                        {tpRows.length === 0 && (
                          <tr><td colSpan={19} style={{ ...styles.td, textAlign: 'center', color: '#9CA3AF' }}>
                            No third-party rentals. + Rental Line here, or flip an Equipment line's Type to Third-Party.
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* Consumables section */}
            {section === 'consumables' && (
              <div style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
                  <h2 style={{ ...styles.h2, margin: 0 }}>Consumables, Services and Charges</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6B7280' }}
                      title="Default markup over cost for new consumable lines and the 'Apply to all' button. Bill Rate = Cost × (1 + this). Seeded from the fleet default in Admin → Settings.">
                      Default markup
                      <input type="text" inputMode="decimal" key={`cm-${current.id}-${consMarkup}`}
                        style={{ ...styles.input, width: 64, fontSize: 12, fontWeight: 700, color: colors.green }}
                        defaultValue={Math.round(consMarkup * 1000) / 10}
                        onBlur={e => {
                          if (e.target.value === '') return;
                          const m = Number(e.target.value) / 100;
                          if (isNaN(m) || m < 0 || m >= 100) return;
                          if (Math.abs(m - consMarkup) < 0.00005) return;
                          updateHeader('consumable_markup_pct', Math.round(m * 10000) / 10000);
                        }} />
                      %
                    </label>
                    <button style={styles.btnSecondary} onClick={applyConsMarkup} disabled={saving}
                      title="Set Bill Rate = Cost × (1 + default markup) on every consumable line that has a cost.">Apply to all lines</button>
                    <button style={styles.btnSecondary} onClick={exportConsumablesCsv} disabled={consRows.length === 0}
                      title="Download the consumable lines shown (respects filters/sort) as a vendor RFQ CSV — description, part #, qty and unit, plus blank columns for the vendor to price and return. Carries NO cost, bill rate or total: safe to email out. For the internal view use ⬇ Workbook.">⬇ Vendor RFQ</button>
                    <button style={styles.btn} onClick={addConsumable} disabled={saving}>+ Add Line</button>
                  </div>
                </div>
                <TableControlsBar ctl={consCtl} columns={consCols} total={consRows.length} />
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead><tr>
                      <SortTh ctl={consCtl} k="description" style={styles.th}>Description</SortTh>
                      <SortTh ctl={consCtl} k="part_number" style={styles.th}>Part #</SortTh>
                      <SortTh ctl={consCtl} k="category" style={styles.th} title="Tags the line for later grouping — does not change the quote.">Category</SortTh>
                      <SortTh ctl={consCtl} k="qty" style={styles.th}>Qty</SortTh>
                      <SortTh ctl={consCtl} k="bill_rate" style={styles.th}>Bill Rate</SortTh>
                      <SortTh ctl={consCtl} k="cost" style={styles.th}>Cost</SortTh>
                      <th style={styles.th} title="Cost × Qty.">Total Cost</th>
                      <SortTh ctl={consCtl} k="total" style={styles.th} title="Qty × Bill Rate — the line total.">Total</SortTh>
                      <th style={styles.th} title="Gross profit — Revenue minus (Cost × Qty).">Profit</th>
                      <SortTh ctl={consCtl} k="margin" style={styles.th} title="Editable markup over cost — entering a % sets Bill Rate to cost × (1 + markup). Hover a cell to see the resulting margin.">Markup %</SortTh>
                      <th style={styles.th}>Vendor</th>
                      <th style={styles.th}>SSS PO # / Vendor Quote #</th>
                      <th style={styles.th}>Quote</th>
                      <th style={styles.th}></th>
                    </tr></thead>
                    <tbody>
                      {consCtl.view.map(line => {
                        const comp = (current.computed?.consumableLines || []).find(c => c.id === line.id) || {};
                        // Markup over cost = GP ÷ cost (co-monotonic with the margin used for sort/threshold).
                        const consMk = comp.lineCost > 0 ? comp.grossProfit / comp.lineCost : null;
                        const consMgnPct = comp.margin != null ? Math.round(comp.margin * 1000) / 10 : null;
                        return (
                          <tr key={line.id} style={{ background: comp.belowThreshold ? '#FFF7ED' : undefined }}>
                            <td style={styles.td}>
                              <input style={{ ...styles.input, width: 200, fontSize: 12 }}
                                defaultValue={line.description}
                                onBlur={e => updateCons(line.id, { description: e.target.value })} />
                              <LastQuoted hint={priceHints?.consumable?.[line.id]}
                                onOpen={() => openPrice('consumable', line.id, line.description)} />
                            </td>
                            <td style={styles.td}>
                              <input style={{ ...styles.input, width: 90, fontSize: 12 }}
                                placeholder="part #"
                                defaultValue={line.part_number || ''}
                                onBlur={e => updateCons(line.id, { part_number: e.target.value.trim() || null })} />
                            </td>
                            <td style={styles.td}>
                              <select style={{ ...styles.input, width: 110, fontSize: 12 }}
                                value={line.category || 'Consumable'}
                                onChange={e => updateCons(line.id, { category: e.target.value })}>
                                {['Consumable','Service','Charge','Mobilization','Air Sale'].map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </td>
                            <td style={styles.td}>
                              <input type="text" inputMode="numeric" key={`q-${line.id}-${line.qty}`}
                                style={{ ...styles.input, width: 70, fontSize: 12 }}
                                defaultValue={Math.round(Number(line.qty)||0)}
                                onBlur={e => updateCons(line.id, { qty: Math.max(0, Math.round(Number(e.target.value)||0)) })} />
                            </td>
                            <td style={styles.td}>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 12, color: '#6B7280' }}>$
                              <input type="text" inputMode="decimal" key={`br-${line.id}-${line.bill_rate}`}
                                style={{ ...styles.input, width: 80, fontSize: 12 }}
                                defaultValue={line.bill_rate}
                                title="Manual sell price. Editing this directly sets the bill rate as-is (the Markup % column then reflects the implied markup over cost)."
                                onBlur={e => updateCons(line.id, { bill_rate: Number(e.target.value) || 0, third_party_markup: null })} />
                              </label>
                            </td>
                            <td style={styles.td}>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 12, color: '#6B7280' }}>$
                              <input type="text" inputMode="decimal" key={`cc-${line.id}-${line.cost}`}
                                style={{ ...styles.input, width: 80, fontSize: 12, color: '#DC2626', fontWeight: 600 }}
                                defaultValue={line.cost}
                                title="Changing the cost re-marks-up the Bill Rate: holds the line's existing markup, or applies the default markup if it had none. A line with a sale price but no cost yet keeps its sale price, and the Markup % is derived from it."
                                onBlur={e => {
                                  const v = Number(e.target.value) || 0;
                                  const body = { cost: v, third_party_markup: null };
                                  const oldCost = Number(line.cost), oldBill = Number(line.bill_rate);
                                  // First cost on a line that already has a sale price (Markup % blank):
                                  // keep the sale price, the % then reads off bill vs cost (George 2026-10-21).
                                  const keepBill = !(oldCost > 0) && oldBill > 0;
                                  // Bill = cost × (1 + markup). Hold the line's implied markup if it
                                  // already had one (bill > cost), else apply the estimate default.
                                  if (v > 0 && !keepBill) {
                                    const implied = (oldCost > 0 && oldBill > oldCost) ? (oldBill - oldCost) / oldCost : null;
                                    const eff = implied != null ? implied : consMarkup;
                                    body.bill_rate = Math.round(v * (1 + eff) * 100) / 100;
                                  }
                                  updateCons(line.id, body);
                                }} />
                              </label>
                            </td>
                            <td style={{ ...styles.td, fontSize: 12, color: '#DC2626' }}>{fmtMoney(comp.lineCost)}</td>
                            <td style={{ ...styles.td, fontWeight: 600, fontSize: 12 }}>{fmtMoney(comp.revenue)}</td>
                            <td style={{ ...styles.td, fontWeight: 600, fontSize: 12,
                              color: comp.grossProfit < 0 ? '#EF4444' : comp.belowThreshold ? ORANGE : colors.green }}>
                              {fmtMoney(comp.grossProfit)}
                            </td>
                            <td style={styles.td}>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 12, color: '#6B7280' }}>
                              <input type="text" inputMode="decimal" key={`mk-${line.id}-${line.cost}-${consNonce}`}
                                style={{ ...styles.input, width: 64, fontSize: 12, fontWeight: 700,
                                  color: comp.belowThreshold ? ORANGE : colors.green }}
                                defaultValue={consMk != null ? Math.round(consMk * 1000) / 10 : ''}
                                title={`Markup over cost — sets Bill Rate to cost × (1 + markup).${consMgnPct != null ? ` Resulting margin: ${consMgnPct}%.` : ''}`}
                                onBlur={e => {
                                  if (e.target.value === '') return;
                                  const m = Number(e.target.value) / 100;
                                  const cost = Number(line.cost);
                                  if (isNaN(m) || m < 0 || m >= 100 || !(cost > 0)) return;
                                  const newRate = Math.round((cost * (1 + m)) * 100) / 100;
                                  if (Math.abs(newRate - Number(line.bill_rate)) < 0.005) return;
                                  updateCons(line.id, { bill_rate: newRate, third_party_markup: null });
                                }} />
                              %</label>
                            </td>
                            <td style={styles.td}>
                              <input style={{ ...styles.input, width: 110, fontSize: 12 }}
                                placeholder="Vendor"
                                defaultValue={line.vendor_name || ''}
                                onBlur={e => updateCons(line.id, { vendor_name: e.target.value.trim() || null })} />
                            </td>
                            <td style={styles.td}>
                              <input style={{ ...styles.input, width: 110, fontSize: 11, marginBottom: 3, display: 'block' }}
                                placeholder="SSS PO #"
                                defaultValue={line.po_number || ''}
                                onBlur={e => updateCons(line.id, { po_number: e.target.value.trim() || null })} />
                              <input style={{ ...styles.input, width: 110, fontSize: 11, display: 'block' }}
                                placeholder="Vendor quote #"
                                defaultValue={line.vendor_quote_number || ''}
                                onBlur={e => updateCons(line.id, { vendor_quote_number: e.target.value.trim() || null })} />
                            </td>
                            <td style={styles.td}>
                              <VendorQuoteFiles lineId={line.id} count={line.file_count || 0} fileApi={CONS_FILE_API} />
                            </td>
                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                              <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', marginRight: 4 }}
                                title="Email a quote request for this line to the vendor — you're CC'd"
                                onClick={() => emailConsVendor(line)}>
                                📧
                              </button>
                              <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 8px' }}
                                onClick={() => deleteCons(line.id)}>Del</button>
                            </td>
                          </tr>
                        );
                      })}
                      {consCtl.view.length > 0 && (() => {
                        const lineTotal = l => (Number(l.qty)||0) * (Number(l.bill_rate)||0);
                        const byCat = {};
                        for (const l of consCtl.view) {
                          const c = l.category || 'Consumable';
                          byCat[c] = (byCat[c]||0) + lineTotal(l);
                        }
                        return (<>
                          {['Consumable','Service','Charge','Mobilization','Air Sale'].filter(c => byCat[c] != null).map(c => (
                            <tr key={`cat-${c}`} style={{ background: '#F9FAFB' }}>
                              <td colSpan={9} style={{ ...styles.td, fontWeight: 600, textAlign: 'right', color: '#6B7280' }}>{c} total</td>
                              <td style={{ ...styles.td, fontWeight: 600, color: '#6B7280' }}>{fmtMoney(byCat[c])}</td>
                              <td colSpan={4} style={styles.td}></td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: '2px solid #1F2937' }}>
                            <td colSpan={9} style={{ ...styles.td, fontWeight: 700, textAlign: 'right' }}>Tab total</td>
                            <td style={{ ...styles.td, fontWeight: 700 }}>
                              {fmtMoney(Object.values(byCat).reduce((s,v) => s+v, 0))}
                            </td>
                            <td colSpan={4} style={styles.td}></td>
                          </tr>
                        </>);
                      })()}
                      {consRows.length === 0 && (
                        <tr><td colSpan={14} style={{ ...styles.td, textAlign: 'center', color: '#9CA3AF' }}>No consumable lines.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Personnel section — submittal manpower Gantt */}
            {section === 'personnel' && (
              <PersonnelPanel current={current} onSave={savePersonnel} onExport={exportPersonnelGantt} onExportOrg={exportOrgChart} />
            )}

            {/* Summary section */}
            {section === 'summary' && current.computed && (
              <SummaryPanel computed={current.computed} tier={current.utilization_tier} />
            )}

            {/* MSA rate sheet — leadership final verify */}
            {section === 'msa' && (
              <div style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                  <h2 style={{ ...styles.h2, margin: 0 }}>
                    MSA Rate Sheet {msaSheet ? `— ${msaSheet.customer_name}` : ''}
                    {msaSheet && (
                      <span style={{
                        marginLeft: 8, padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                        background: msaSheet.status === 'Verified' ? '#D1FAE5' : msaSheet.status === 'Confirmed' ? '#DBEAFE' : '#FEF3C7',
                        color: msaSheet.status === 'Verified' ? '#065F46' : msaSheet.status === 'Confirmed' ? '#1E40AF' : '#92400E',
                      }}>
                        {msaSheet.status}
                      </span>
                    )}
                  </h2>
                  {msaSheet && msaSheet.status === 'Confirmed' && (
                    <button style={{ ...styles.btn, background: '#065F46' }} disabled={saving}
                      title="Leadership sign-off: the rates feeding this estimate match the agreement"
                      onClick={() => doSave(async () => setMsaSheet(await api.updateMsaSheet(msaSheet.id, { status: 'Verified' })))}>
                      ✅ Verify Rates
                    </button>
                  )}
                  {msaSheet && msaSheet.status === 'Verified' && (
                    <button style={{ ...styles.btnSecondary, fontSize: 12 }} disabled={saving}
                      onClick={() => doSave(async () => setMsaSheet(await api.updateMsaSheet(msaSheet.id, { status: 'Confirmed' })))}>
                      ↩ Un-verify
                    </button>
                  )}
                </div>
                {!msaSheet && (
                  <div style={{ color: '#9CA3AF', fontSize: 13 }}>
                    No MSA rate sheet found for this estimate's customer.
                    {current.request_id ? (
                      <div style={{ marginTop: 10 }}>
                        <button style={styles.btn} disabled={saving}
                          title="Runs AI extraction on the MSA file attached to the source request (~30-60s)"
                          onClick={() => doSave(async () => {
                            setMsaSheet(await api.extractMsaRates(current.request_id));
                          })}>
                          {saving ? '⏳ Extracting…' : "⚡ Extract from the request's MSA file"}
                        </button>
                        <div style={{ fontSize: 11, marginTop: 6 }}>
                          Uses the MSA file attached to this estimate's own request (Q7) — never another customer's sheet.
                        </div>
                      </div>
                    ) : ' The AM extracts and confirms it on the request (MSA Rates tab).'}
                  </div>
                )}
                {msaSheet && msaSheet.status === 'Draft' && (
                  <div style={{ background: '#FEF3C7', border: '1px solid #FBBF24', borderRadius: 6,
                    padding: '8px 14px', marginBottom: 10, fontSize: 12, fontWeight: 600, color: '#92400E' }}>
                    ⚠ The AM has not confirmed this extraction yet — review with them before relying on these rates.
                  </div>
                )}
                {msaSheet && (
                  <MsaSheetEditor
                    sheet={msaSheet}
                    catalog={catalog}
                    readOnly={false}
                    saving={saving}
                    onLineUpdate={(lineId, body) => doSave(async () => setMsaSheet(await api.updateMsaLine(lineId, body)))}
                    onLineDelete={(lineId) => { if (!confirm('Delete this MSA line?')) return; doSave(async () => setMsaSheet(await api.deleteMsaLine(lineId))); }}
                    onLineAdd={(body) => doSave(async () => setMsaSheet(await api.addMsaLine(msaSheet.id, body)))}
                  />
                )}
              </div>
            )}

            {/* Quote section */}
            {section === 'quote' && (
              <QuotePanel
                estimate={current}
                quotes={quotes}
                saving={saving}
                doSave={doSave}
                refresh={() => api.listQuotes({ estimate_root: current.root_id || current.id, all_revisions: 'true' })
                  .then(setQuotes).catch(() => {})}
              />
            )}

            {/* Audit history */}
            {section === 'history' && (
              <div style={styles.card}>
                <h2 style={{ ...styles.h2, marginBottom: 10 }}>
                  Audit Log — EST-{current.root_id || current.id}-{current.revision || 0}
                </h2>
                {estHistory.length === 0 && <div style={{ color: '#9CA3AF', fontSize: 13 }}>No events recorded.</div>}
                {estHistory.map(ev => (
                  <div key={ev.id} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid #F3F4F6', fontSize: 12 }}>
                    <span style={{ color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                      {new Date(ev.changed_at).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{ev.changed_by_name || 'system'}</span>
                    <span style={{ color: '#6B7280' }}>
                      <strong>{ev.field}</strong>
                      {ev.old_value != null && ev.old_value !== '' ? <> : {String(ev.old_value)} →</> : ' :'}
                      {' '}{ev.new_value != null ? String(ev.new_value) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {current && (
        <RevisionDrawer
          open={showRevisions}
          onClose={() => setShowRevisions(false)}
          title={`Estimate History — ${current.project_site} v${current.version}`}
          listFn={() => api.listEstimateRevisions(current.id)}
          snapshotFn={(revId) => api.getEstimateRevisionSnapshot(current.id, revId)}
          renderSnapshot={(snap) => (
            <div style={{ fontSize: 12, color: '#374151' }}>
              <div><strong>Status:</strong> {snap.status} · v{snap.version}</div>
              <div><strong>Project:</strong> {snap.project_site}</div>
              {snap.computed?.rollup70?.total && (
                <div><strong>Revenue (70%):</strong> {fmtMoney(snap.computed.rollup70.total.revenue)}</div>
              )}
              <div style={{ marginTop: 6, color: '#9CA3AF' }}>
                {(snap.labor_lines || []).length} labor · {(snap.equipment_lines || []).length} equipment · {(snap.consumable_lines || []).length} consumables
              </div>
            </div>
          )}
        />
      )}
      {current && showImport && (
        <WorkbookImportModal
          estimateId={current.id}
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); reload(current.id); }}
        />
      )}
      {current && auditOpen && (
        <AuditTrailModal
          trail={auditData}
          onClose={() => setAuditOpen(false)}
          onDownload={() => api.exportAuditTrail(current.id).catch(e => setError(e.message))}
        />
      )}

      {/* 📈 Price history for one craft / CAT-CLASS / part — what we quoted
          before and what we won with. Scoped to this estimate's customer so
          their own history is highlighted. */}
      {priceModal && (
        <PriceHistoryModal
          kind={priceModal.kind}
          itemKey={priceModal.key}
          label={priceModal.label}
          customerId={priceHints?.customer_id || null}
          onClose={() => setPriceModal(null)}
        />
      )}

      {/* Duplicate → a second, independent estimate from the same scope */}
      {dupOpen && current && (
        <DuplicateEstimateModal
          est={current}
          projects={projects}
          oppLabel={oppLabel}
          onCancel={() => setDupOpen(false)}
          onDone={(r) => {
            setDupOpen(false);
            setCurrent(r);
            setSection('labor');
            loadEstimates();
            setDupMsg({ ok: true, text:
              `Copied ${r.duplicated_from.label} into EST-${r.root_id || r.id}-0 (Draft).`
              + (r.files_left_behind
                  ? ` ${r.files_left_behind} vendor quote file${r.files_left_behind === 1 ? '' : 's'} stayed on ${r.duplicated_from.label}.`
                  : '') });
          }}
        />
      )}

      {/* Request from Sales — open a Draft request on this estimate for the AM to fill in */}
      {reqAsk && current && (
        <div onClick={() => !reqAsk.saving && setReqAsk(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 20,
            width: 520, maxWidth: '94vw', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#1F2937' }}>Request from Sales</h3>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 14 }}>
              Opens a Draft request for EST-{current.root_id || current.id}-{current.revision || 0}, already linked to this
              estimate, and emails the salesperson a link. They answer the questions for the scope you check and submit.
            </div>
            <label style={styles.label}>Scope (check all that apply) *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {!reqAsk.catalog.length && !reqAsk.error && <span style={{ fontSize: 12, color: '#9CA3AF' }}>Loading…</span>}
              {reqAsk.catalog.map(f => {
                const on = reqAsk.flags.includes(f.flag_key);
                return (
                  <button key={f.flag_key} type="button" title={f.covers || ''}
                    onClick={() => setReqAsk(r => ({ ...r, flags: on ? r.flags.filter(k => k !== f.flag_key) : [...r.flags, f.flag_key] }))}
                    style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                      background: on ? '#1F2937' : 'linear-gradient(#FFFFFF, #F3F4F6)', color: on ? '#fff' : '#374151', borderColor: on ? '#1F2937' : '#C3C8D0',
                      boxShadow: on ? 'inset 0 2px 3px rgba(0, 0, 0, 0.3)' : '0 1px 2px rgba(17, 24, 39, 0.12), 0 2px 4px rgba(17, 24, 39, 0.06)' }}>
                    {on ? '✓ ' : ''}{f.label}
                  </button>
                );
              })}
            </div>
            <label style={styles.label}>Send to *</label>
            <select style={{ ...styles.input, width: '100%', marginBottom: 12 }} value={reqAsk.owner_id}
              onChange={e => setReqAsk(r => ({ ...r, owner_id: e.target.value }))}>
              <option value="">—</option>
              {reps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
            </select>
            <label style={styles.label}>Note (optional)</label>
            <textarea rows={3} style={{ ...styles.input, width: '100%', marginBottom: 12, resize: 'vertical' }}
              placeholder="Anything they need to know"
              value={reqAsk.note} onChange={e => setReqAsk(r => ({ ...r, note: e.target.value }))} />
            {reqAsk.error && <div style={{ ...styles.errorBox, marginBottom: 10 }}>{reqAsk.error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={styles.btnSecondary} disabled={reqAsk.saving} onClick={() => setReqAsk(null)}>Cancel</button>
              <button style={styles.btn} disabled={reqAsk.saving || !reqAsk.flags.length || !reqAsk.owner_id}
                onClick={async () => {
                  setReqAsk(r => ({ ...r, saving: true, error: null }));
                  try {
                    const r = await api.requestFromEstimate({ estimate_id: current.id, owner_id: Number(reqAsk.owner_id),
                      scope_flags: reqAsk.flags, note: reqAsk.note });
                    const who = reps.find(x => String(x.id) === String(reqAsk.owner_id))?.full_name || 'the salesperson';
                    setReqAsk(null);
                    await reload(current.id);
                    setDupMsg({ ok: true, text: `REQ-${r.root_id || r.id}-0 created and linked. `
                      + (r.emailed ? `Emailed ${who} the link.` : `Email NOT sent (no email on file or mail is off); send ${who} the request link.`) });
                  } catch (e) { setReqAsk(r => r && { ...r, saving: false, error: e.message }); }
                }}>
                {reqAsk.saving ? 'Sending…' : 'Create + email link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Standalone estimate — name the deal, we create the opportunity for it */}
      {standalone && (
        <div onClick={() => !saving && setStandalone(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 20,
            width: 460, maxWidth: '94vw', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#1F2937' }}>Standalone estimate</h3>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 14 }}>
              No sales request and no opportunity yet. Name the deal and we'll create the
              Project Tracker opportunity (Bidding) so the quote, audit trail, and ops
              handoff all have something to hang on.
            </div>
            <label style={styles.label}>Site / deal name *</label>
            <input autoFocus style={{ ...styles.input, width: '100%', marginBottom: 10 }}
              placeholder="e.g. Huntsman Port Neches"
              value={standalone.site}
              onChange={e => setStandalone(s => ({ ...s, site: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter' && standalone.site.trim() && isWinPct(standalone.win_pct) && standalone.revenue_type && !saving) createStandalone(); }} />
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Branch</label>
                <select style={{ ...styles.input, width: '100%' }} value={standalone.branch_id}
                  onChange={e => setStandalone(s => ({ ...s, branch_id: e.target.value }))}>
                  <option value="">—</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Account Manager</label>
                <select style={{ ...styles.input, width: '100%' }} value={standalone.account_owner_id}
                  onChange={e => setStandalone(s => ({ ...s, account_owner_id: e.target.value }))}>
                  <option value="">—</option>
                  {reps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
                </select>
              </div>
            </div>
            <label style={styles.label}>Revenue Type *</label>
            <select style={{ ...styles.input, width: '100%', marginBottom: 10 }} value={standalone.revenue_type}
              title="Required. TA deals get their TA Job # automatically when marked Won."
              onChange={e => setStandalone(s => ({ ...s, revenue_type: e.target.value }))}>
              <option value="" disabled>Choose...</option>
              <option value="Branch">Branch Revenue</option>
              <option value="TA">TA Revenue</option>
              <option value="Rescue">Rescue Revenue</option>
            </select>
            <label style={styles.label}>Planned start date</label>
            <input type="date" style={{ ...styles.input, width: '100%', marginBottom: 10 }}
              value={standalone.date_planned}
              onChange={e => setStandalone(s => ({ ...s, date_planned: e.target.value }))} />
            <label style={styles.label}>Win % (from sales) *</label>
            <input type="number" min="0" max="100" step="1" inputMode="numeric"
              style={{ ...styles.input, width: 120, marginBottom: 4,
                ...(standalone.win_pct !== '' && !isWinPct(standalone.win_pct) ? { border: '1px solid #EF4444' } : {}) }}
              placeholder="0-100"
              value={standalone.win_pct}
              onChange={e => setStandalone(s => ({ ...s, win_pct: e.target.value }))} />
            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 16 }}>
              The salesperson's chance of winning, a whole number 0 to 100. Ask the AM; it is never defaulted.
            </div>
            {error && <div style={{ ...styles.errorBox, marginBottom: 10 }}>{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={styles.btnSecondary} disabled={saving} onClick={() => setStandalone(null)}>Cancel</button>
              <button style={styles.btn} disabled={saving || !standalone.site.trim() || !isWinPct(standalone.win_pct) || !standalone.revenue_type} onClick={createStandalone}>
                Create opportunity + estimate
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 10 }}>
              Branch, AM, and date can be filled in later on the Project Tracker — the
              opportunity is flagged for review until they are. Sales can attach a request
              to this opportunity afterwards to fill in the questionnaire; leave the Account
              Manager blank and whoever links their request claims it.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Full audit trail: the chronological request -> estimate -> quote -> PO record
// for the whole job (who changed what, when), with a one-click PDF for disputes.
// ---- Duplicate estimate -----------------------------------------------------
// Same scope, next turnaround. Copies the whole estimate into a NEW chain
// (Draft, revision -0) instead of superseding this one, and slides every date to
// the new window in ONE move so the pre-TA / work / post-TA offsets survive the
// copy. The preview below runs the same rule the server does, so what it shows
// is what gets written — a re-date is the part of this that is easy to get
// silently wrong, so it is shown before it is committed, not after.
const dayOf = (v) => (v ? String(v).slice(0, 10) : '');
const DUP_MS_DAY = 86400000;
// Noon UTC, matching the server: immune to DST and to the browser's zone.
const dupDayMs = (s) => Date.parse(`${s}T12:00:00Z`);
const dupShift = (s, n) => new Date(dupDayMs(s) + n * DUP_MS_DAY).toISOString().slice(0, 10);
const dupDiff = (a, b) => Math.round((dupDayMs(a) - dupDayMs(b)) / DUP_MS_DAY);

function DuplicateEstimateModal({ est, projects, oppLabel, onCancel, onDone }) {
  const label = `EST-${est.root_id || est.id}-${est.revision || 0}`;

  // Every distinct fully dated window on the estimate, most-used first. Labor
  // phases, equipment lines, and equipment phases all count — an estimate's
  // real shape is spread across all three.
  const windows = useMemo(() => {
    const rows = [];
    for (const l of est.labor_lines || []) for (const p of (l.phases || [])) rows.push(p);
    for (const e of est.equipment_lines || []) {
      rows.push(e);
      for (const p of (e.phases || [])) rows.push(p);
    }
    const tally = new Map();
    for (const r of rows) {
      const s = dayOf(r.start_date), e = dayOf(r.end_date);
      if (!s || !e) continue;
      tally.set(`${s}|${e}`, (tally.get(`${s}|${e}`) || 0) + 1);
    }
    return [...tally.entries()]
      .map(([k, n]) => ({ start: k.split('|')[0], end: k.split('|')[1], n }))
      .sort((a, b) => b.n - a.n || (a.start < b.start ? -1 : 1));
  }, [est]);

  const [projectId, setProjectId] = useState(String(est.project_id || ''));
  const [anchor, setAnchor] = useState(windows[0]?.start || '');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [copyPeople, setCopyPeople] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // The anchor window's finish = the latest end among rows that START on the
  // anchor day. Same definition the server uses, so the preview cannot drift.
  const anchorEnd = useMemo(() => {
    const ends = windows.filter(w => w.start === anchor).map(w => w.end).sort();
    return ends.length ? ends[ends.length - 1] : '';
  }, [windows, anchor]);

  const badRange = !!(start && end && end < start);
  const reDate = !!(start && anchor);
  const delta = reDate ? dupDiff(start, anchor) : 0;
  const tail = (reDate && end && anchorEnd && !badRange) ? dupDiff(end, dupShift(anchorEnd, delta)) : 0;

  const preview = useMemo(() => {
    if (!reDate || badRange) return [];
    return windows.map(w => {
      const after = !!(anchorEnd && w.start > anchorEnd);
      const ds = after ? delta + tail : delta;
      const ns = dupShift(w.start, ds);
      let ne = dupShift(w.end, ds);
      if (!after && w.end === anchorEnd) ne = dupShift(ne, tail);
      if (ne < ns) ne = ns;
      return { ...w, ns, ne };
    });
  }, [windows, reDate, badRange, delta, tail, anchorEnd]);

  const go = async () => {
    if (busy || badRange) return;
    setErr(''); setBusy(true);
    try {
      const r = await api.duplicateEstimate(est.id, {
        project_id: Number(projectId) || est.project_id,
        start_date: start || null,
        end_date: (start && end) ? end : null,
        anchor_start: start ? anchor : null,
        copy_personnel: copyPeople,
      });
      onDone(r);
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  const cell = { padding: '3px 8px', fontSize: 12, whiteSpace: 'nowrap' };

  return (
    <div onClick={() => !busy && onCancel()} style={{ position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8,
        padding: 20, width: 560, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#1F2937' }}>Duplicate {label}</h3>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 14 }}>
          Copies every labor, equipment, third-party and consumable line into a
          brand-new estimate that opens as a Draft. {label} is not touched and stays
          exactly as it is. For a price change on this job use New Revision instead.
        </div>

        <label style={styles.label}>Opportunity</label>
        <select style={{ ...styles.input, width: '100%', marginBottom: 12 }}
          value={projectId} onChange={e => setProjectId(e.target.value)}>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{oppLabel(p)}</option>
          ))}
        </select>

        {windows.length > 0 ? (
          <>
            <label style={styles.label}>Window being moved</label>
            <select style={{ ...styles.input, width: '100%', marginBottom: 12 }}
              value={anchor} onChange={e => setAnchor(e.target.value)}>
              {[...new Set(windows.map(w => w.start))].map(s => {
                const n = windows.filter(w => w.start === s).reduce((a, w) => a + w.n, 0);
                const e = windows.filter(w => w.start === s).map(w => w.end).sort().pop();
                return <option key={s} value={s}>{fmtDate(s)} – {fmtDate(e)} ({n} line{n === 1 ? '' : 's'})</option>;
              })}
            </select>

            <div style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>New start *</label>
                <input type="date" style={{ ...styles.input, width: '100%' }}
                  value={start} onChange={e => setStart(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>New finish</label>
                <input type="date" style={{ ...styles.input, width: '100%' }}
                  min={start || undefined}
                  value={end} onChange={e => setEnd(e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 10 }}>
              Leave both blank to copy the dates as they are.
            </div>

            {badRange && (
              <div style={{ fontSize: 12, fontWeight: 600, color: '#B91C1C', marginBottom: 10 }}>
                The finish is before the start.
              </div>
            )}

            {preview.length > 0 && (
              <div style={{ border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 12 }}>
                <div style={{ padding: '5px 8px', fontSize: 11, fontWeight: 700, color: '#374151',
                  background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  Every date moves {delta >= 0 ? '+' : ''}{delta} day{Math.abs(delta) === 1 ? '' : 's'}
                  {tail !== 0 ? `, and the window being moved ${tail > 0 ? 'runs' : 'ends'} ${Math.abs(tail)} day${Math.abs(tail) === 1 ? '' : 's'} ${tail > 0 ? 'longer' : 'shorter'}` : ''}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {preview.map(w => (
                      <tr key={`${w.start}|${w.end}`} style={{ borderTop: '1px solid #F3F4F6' }}>
                        <td style={{ ...cell, color: '#9CA3AF' }}>
                          {fmtDate(w.start)} – {fmtDate(w.end)}
                        </td>
                        <td style={{ ...cell, color: '#9CA3AF' }}>→</td>
                        <td style={{ ...cell, fontWeight: 700, color: '#1F2937' }}>
                          {fmtDate(w.ns)} – {fmtDate(w.ne)}
                        </td>
                        <td style={{ ...cell, color: '#6B7280', width: '100%' }}>
                          {w.n} line{w.n === 1 ? '' : 's'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
            Nothing on this estimate carries dates, so there is nothing to re-date.
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
          color: '#374151', marginBottom: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={copyPeople} onChange={e => setCopyPeople(e.target.checked)} />
          Copy the named crew across
        </label>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 14 }}>
          Vendor quote files stay on {label} — a copy would share one stored file
          between two live estimates, and deleting it on either would take it off both.
        </div>

        {err && (
          <div style={{ fontSize: 12, fontWeight: 600, color: '#B91C1C', marginBottom: 10 }}>{err}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={styles.btnSecondary} disabled={busy} onClick={onCancel}>Cancel</button>
          <button style={styles.btn} disabled={busy || badRange} onClick={go}>
            {busy ? 'Copying…' : 'Create the copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditTrailModal({ trail, onClose, onDownload }) {
  const o = trail?.opportunity || {};
  const stageColor = (s) => s === 'PO' ? '#065F46' : s === 'Quote' ? '#7C3AED' : s === 'Estimate' ? ORANGE : '#1F2937';
  const changeText = (r) => {
    const f = r.field || '';
    const ov = r.old == null ? '' : String(r.old);
    const nv = r.new == null ? '' : String(r.new);
    if (!ov && nv) return f ? `${f}: ${nv}` : nv;
    if (ov && nv) return `${f}: ${ov} → ${nv}`;
    return f || nv || '(change)';
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 20, width: 900, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Audit Trail</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              {trail ? `${trail.jobLabel || '—'}${trail.customer ? ` · ${trail.customer}` : ''}` : 'Loading…'}
              {o.po_number ? ` · PO ${o.po_number}${o.po_amount != null ? ` ($${Number(o.po_amount).toLocaleString()})` : ''}${o.po_date ? ` · ${fmtDate(o.po_date)}` : ''}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...styles.btn, fontSize: 12 }} onClick={onDownload} title="Download the full audit trail as a PDF">⬇ PDF</button>
            <button style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={onClose}>✕</button>
          </div>
        </div>
        {!trail ? (
          <div style={{ color: '#9CA3AF', fontSize: 13, padding: 20, textAlign: 'center' }}>Loading audit trail…</div>
        ) : trail.rows.length === 0 ? (
          <div style={{ color: '#9CA3AF', fontSize: 13 }}>No recorded events for this chain yet.</div>
        ) : (
          <table style={{ ...styles.table, fontSize: 12 }}>
            <thead><tr>
              <th style={styles.th}>When</th>
              <th style={styles.th}>Stage</th>
              <th style={styles.th}>Ref</th>
              <th style={styles.th}>Who</th>
              <th style={styles.th}>Change</th>
            </tr></thead>
            <tbody>
              {trail.rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap', color: '#6B7280' }}>{r.ts ? fmtDateTime(r.ts) : '—'}</td>
                  <td style={{ ...styles.td, fontWeight: 700, color: stageColor(r.stage) }}>{r.stage}</td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{r.ref || '—'}</td>
                  <td style={{ ...styles.td }}>{r.actor || '—'}</td>
                  <td style={styles.td}>{changeText(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components

// Day rate breakdown (server: estimateCalc.js calcDayRate). Every number the
// $/day is built from, plus what is deliberately left out, so the estimator
// sees exactly what the customer's rate carries.
function DayRatePanel({ dayRate: d }) {
  const th = { ...styles.th, fontSize: 11 };
  const td = { ...styles.td, fontSize: 12 };
  const num = { ...td, textAlign: 'right' };
  const sub = (label, amt) => (
    <tr style={{ background: '#F9FAFB' }}>
      <td style={{ ...td, fontWeight: 700 }} colSpan={5}>{label}</td>
      <td style={{ ...num, fontWeight: 700 }}>{fmtMoney(amt)}</td>
    </tr>
  );
  return (
    <div style={{ ...styles.card, border: `2px solid ${ORANGE}`, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#1F2937' }}>Day Rate</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: ORANGE }}>{fmtMoney(d.total)} / day</div>
      </div>
      <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
        One crew-day. Labor bills 8 ST + the balance of the shift OT per person, every day, so the rate is the same whether the job runs 3 days or 10. Equipment at the estimate's {d.utilization_tier}% tier where it is metered.
      </div>
      <table style={{ ...styles.table, width: '100%' }}>
        <thead>
          <tr><th style={th}>Line</th><th style={th}>Qty</th><th style={th}>Basis</th><th style={{ ...th, textAlign: 'right' }}>Rate</th><th style={{ ...th, textAlign: 'right' }}>Per Unit</th><th style={{ ...th, textAlign: 'right' }}>$ / Day</th></tr>
        </thead>
        <tbody>
          {d.labor.map(l => (
            <tr key={`l${l.line_no}`}>
              <td style={td}>{l.craft}</td>
              <td style={td}>{l.heads}{l.night_hc ? ` (${l.day_hc} day / ${l.night_hc} night)` : ''}</td>
              <td style={td}>{l.hours_per_day} hr shift = {l.st_hours} ST + {l.ot_hours} OT</td>
              <td style={num}>{fmtMoney(l.bill_st)} / {fmtMoney(l.bill_ot)}</td>
              <td style={num}>{fmtMoney(l.per_person)}</td>
              <td style={num}>{fmtMoney(l.amount)}</td>
            </tr>
          ))}
          {sub('Labor', d.laborTotal)}
          {d.perDiem.map(p => (
            <tr key={`p${p.line_no}`}>
              <td style={td}>{p.craft} per diem</td>
              <td style={td}>{p.positions}</td>
              <td style={td}>Per person per day</td>
              <td style={num}>{fmtMoney(p.rate)}</td>
              <td style={num}>{fmtMoney(p.rate)}</td>
              <td style={num}>{fmtMoney(p.amount)}</td>
            </tr>
          ))}
          {sub('Per Diem', d.perDiemTotal)}
          {d.equipment.map(e => (
            <tr key={`e${e.line_no}`}>
              <td style={td}>{e.description}</td>
              <td style={td}>{e.qty}</td>
              <td style={td}>{e.basis}</td>
              <td style={num}>{fmtMoney(e.day_rate)}</td>
              <td style={num}>{fmtMoney(e.day_rate * e.factor)}</td>
              <td style={num}>{fmtMoney(e.amount)}</td>
            </tr>
          ))}
          {sub('Equipment', d.equipmentTotal)}
          <tr style={{ borderTop: '2px solid #1F2937' }}>
            <td style={{ ...td, fontWeight: 800 }} colSpan={5}>DAY RATE</td>
            <td style={{ ...num, fontWeight: 800 }}>{fmtMoney(d.total)}</td>
          </tr>
        </tbody>
      </table>
      {d.excluded.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            Not in the day rate (quoted separately)
          </div>
          {d.excluded.map((x, i) => (
            <div key={i} style={{ fontSize: 12, color: '#374151' }}>
              {x.label}: {x.reason}{x.amount != null ? ` (${fmtMoney(x.amount)})` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HeaderField({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
      {children}
    </div>
  );
}

// Workbook import: pick the edited .xlsx, preview the reconcile plan (adds /
// changes / removals + any validation errors), then apply. Apply is blocked
// while there are errors. The server keys reconciliation on line_id.
function WorkbookImportModal({ estimateId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const pick = async (f) => {
    setFile(f); setPreview(null); setErr('');
    if (!f) return;
    setBusy(true);
    try { setPreview(await api.previewEstimateWorkbook(estimateId, f)); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!file) return;
    setBusy(true); setErr('');
    try { const r = await api.importEstimateWorkbook(estimateId, file); onDone(r); }
    catch (e) { setErr(e.message); setBusy(false); }
  };

  const hasErrors = !!(preview && preview.errors && preview.errors.length);
  const P = preview && preview.plan;
  const fmtLine = (label, p) => `${label}:  +${p.add} new · ~${p.update} changed · −${p.delete} removed`;
  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
    alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '6vh 16px' };
  const card = { background: '#fff', borderRadius: 8, padding: 20, width: 'min(640px, 96vw)',
    maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' };
  const errBox = { background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B',
    borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 10 };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Import workbook</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280', lineHeight: 1 }}>×</button>
        </div>
        <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0 }}>
          Upload the edited .xlsx (keep all four tabs). Rows keep their <b>line_id</b> to update in place; a blank line_id adds a new line; any line_id you delete from a tab removes that line. Pricing recomputes after import.
        </p>
        <DropZone block onFiles={files => pick(files[0])} disabled={busy}
          title="Drop the edited workbook here" style={{ marginBottom: 10, padding: 6 }}>
          <input type="file" accept=".xlsx,.xls" onChange={e => pick(e.target.files && e.target.files[0])}
            style={{ fontSize: 13, display: 'block' }} />
        </DropZone>
        {busy && <div style={{ fontSize: 13, color: '#6B7280' }}>Checking…</div>}
        {err && <div style={errBox}>{err}</div>}
        {P && (
          <div style={{ fontSize: 13, lineHeight: 1.7, background: '#F9FAFB', border: '1px solid #E5E7EB',
            borderRadius: 6, padding: '10px 14px', marginBottom: 10 }}>
            <div>{fmtLine('Labor', P.labor)} · {P.labor.phase_upserts} phase rows</div>
            <div>{fmtLine('Equipment', P.equipment)}</div>
            <div>{fmtLine('Third-Party Rentals', P.third_party)}</div>
            <div>{fmtLine('Consumables / Svc / Charges', P.consumables)}</div>
          </div>
        )}
        {preview && preview.deletes && preview.deletes.length > 0 && (
          <div style={{ background: '#FEF3C7', border: '1px solid #FBBF24', color: '#92400E',
            borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 10 }}>
            <b>Will remove {preview.deletes.length} line(s)</b> (deleted from a tab):
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {preview.deletes.map((d, i) => <li key={i}>{d.category}: {d.description}</li>)}
            </ul>
          </div>
        )}
        {hasErrors && (
          <div style={errBox}>
            <b>Fix these before importing ({preview.errors.length}):</b>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {preview.errors.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button onClick={onClose} style={{ ...styles.btnSecondary }}>Cancel</button>
          <button onClick={apply} disabled={busy || !preview || hasErrors}
            style={{ background: ORANGE, color: '#fff', border: 'none', borderRadius: 6,
              padding: '8px 16px', fontSize: 13, fontWeight: 700,
              cursor: (busy || !preview || hasErrors) ? 'default' : 'pointer',
              opacity: (busy || !preview || hasErrors) ? 0.5 : 1 }}>
            {busy ? 'Importing…' : 'Apply import'}
          </button>
        </div>
      </div>
    </div>
  );
}

// MSA wording picker for a line with no wording: a fuzzy suggestion chip plus a
// pick-any select. Applying sets the wording and the MSA bill rate(s); the server
// then applies the mandatory MSA pay (services/msaPay.js). Confirmed/Verified sheets only.
function MsaPick({ kind, sheet, text, onPick }) {
  if (!sheet || !['Confirmed', 'Verified'].includes(sheet.status)) return null;
  const lines = (sheet.lines || []).filter(l => l.kind === kind);
  if (!lines.length) return null;
  const m = msaFuzzyMatch(text, lines);
  const rate = l => kind === 'labor'
    ? (l.bill_st != null ? ` $${Number(l.bill_st).toFixed(2)}/${Number(l.bill_ot || 0).toFixed(2)}` : '')
    : (l.day_rate != null ? ` $${Number(l.day_rate).toFixed(2)}/day` : '');
  return (
    <div style={{ marginTop: 3, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      {m && (
        <span title={`Matches MSA line "${m.msa_description}" — click to apply its wording and rate`}
          onClick={() => onPick(m)}
          style={{ fontSize: 10, fontWeight: 600, color: '#1E40AF', background: '#DBEAFE',
            padding: '2px 6px', borderRadius: 8, cursor: 'pointer' }}>
          MSA: {m.msa_description}{rate(m)}, apply
        </span>
      )}
      <select style={{ fontSize: 10, padding: '1px 2px', maxWidth: 200 }} value=""
        onChange={e => { const l = lines.find(x => x.id === Number(e.target.value)); if (l) onPick(l); }}>
        <option value="">Pick MSA line…</option>
        {lines.map(l => (
          <option key={l.id} value={l.id}>{l.msa_description}{l.craft ? ` (${l.craft})` : ''}{rate(l)}</option>
        ))}
      </select>
    </div>
  );
}

function MarkupPanel({ markupLines, sums, otBurden, otProfit, onSave }) {
  const [lines, setLines] = useState(markupLines || []);
  useEffect(() => { setLines(markupLines || []); }, [markupLines]);

  const toggle = (i) => {
    const next = lines.map((l, idx) => idx === i ? { ...l, included: !l.included } : l);
    setLines(next);
    onSave(next);
  };

  const toggleOt = (i) => {
    const next = lines.map((l, idx) => idx === i ? { ...l, applies_to_ot_premium: !l.applies_to_ot_premium } : l);
    setLines(next);
    onSave(next);
  };

  const setPct = (i, v) => {
    const next = lines.map((l, idx) => idx === i ? { ...l, st_pct: Number(v) } : l);
    setLines(next);
  };

  return (
    <div style={{ ...styles.card, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>Markup Components</h2>
        <div style={{ fontSize: 12, color: '#6B7280' }}>
          {/* Multipliers mirror estimateCalc: ST cost = pay x (1+burden), ST bill = pay x (1+all-in),
              OT premium cost = premium x (1+OT burden), OT premium bill = premium x (1+OT burden+OT profit). */}
          ST burden: <strong>{pct(sums?.nonProfitSum || 0)}</strong> <span title="ST cost multiplier on base pay">{mult(sums?.nonProfitSum)}</span>
          {' · '}Profit: <strong>{pct(sums?.profitSum || 0)}</strong>
          {' · '}All-in: <strong style={{ color: ORANGE }}>{pct(sums?.allSum || 0)}</strong> <span title="ST bill multiplier on base pay">{mult(sums?.allSum)}</span>
          {' · '}OT burden: <strong>{pct(otBurden || 0)}</strong> <span title="OT premium cost multiplier">{mult(otBurden)}</span>
          {' · '}OT profit: <strong>{pct(otProfit || 0)}</strong> <span title="OT premium bill multiplier (1 + OT burden + OT profit)">{mult(Number(otBurden || 0) + Number(otProfit || 0))}</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {lines.map((l, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
            background: l.included ? (l.is_profit ? '#FEF3C7' : '#F0F9FF') : '#F9FAFB',
            border: `1px solid ${l.included ? (l.is_profit ? '#F59E0B' : '#BAE6FD') : '#E5E7EB'}`,
            borderRadius: 6, fontSize: 12,
          }}>
            <input type="checkbox" checked={!!l.included} onChange={() => toggle(i)} />
            <span style={{ color: l.is_profit ? '#92400E' : '#075985', fontWeight: 600 }}>{l.label}</span>
            <input type="number" step="0.0001" style={{ width: 60, padding: '2px 4px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 3 }}
              value={l.st_pct}
              onChange={e => setPct(i, e.target.value)}
              onBlur={() => onSave(lines)} />
            {/* Pricing guidance from Matt (2026-10-26): set profit so All-in lands here. */}
            {l.is_profit && (
              <span title="Set Net Profit / Fee so the All-in % (top right) lands on the target for this job type."
                style={{ fontSize: 11, color: '#92400E' }}>
                Target All-in: TA / equipment-heavy 50% · Labor only 70% · Rescue 80-100%
              </span>
            )}
            {!l.is_profit && (
              <label title="Applies to OT premium — this component also burdens the premium half of overtime pay (FICA + unemployment legally follow premium dollars; WC/liability/overhead don't). The OT Burden % derives from the checked components."
                style={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
                  fontSize: 9, fontWeight: 700, color: l.applies_to_ot_premium ? '#7C3AED' : '#9CA3AF' }}>
                <input type="checkbox" checked={!!l.applies_to_ot_premium} onChange={() => toggleOt(i)}
                  style={{ width: 11, height: 11 }} />
                OT
              </label>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Generic table sort/filter controls ---------------------------------
// CRM port of the field-services list pattern, adapted for the editable
// estimate grids: clickable sortable headers + a filter bar with active
// chips, no pagination (the totals footer + inline editing stay intact).
// Operates on the raw line array and returns a filtered+sorted view, so each
// table body keeps its existing cell rendering — only the row source changes.
function useTableControls(rows, columns) {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [filters, setFilters] = useState({});
  const toggleSort = (key) => setSort(s =>
    s.key !== key ? { key, dir: 'asc' }
      : s.dir === 'asc' ? { key, dir: 'desc' }
      : { key: null, dir: 'asc' });           // third click clears the sort
  const setFilter = (key, val) => setFilters(f => {
    const next = { ...f };
    if (val === '' || val == null || val === false) delete next[key]; else next[key] = val;
    return next;
  });
  const clearFilters = () => setFilters({});

  const view = useMemo(() => {
    const colMap = Object.fromEntries(columns.map(c => [c.k, c]));
    let r = rows;
    for (const [key, val] of Object.entries(filters)) {
      const col = colMap[key]; if (!col || !col.filter) continue;
      const get = col.filter.get || col.sort;
      const match = col.filter.match || (
        col.filter.type === 'text'
          ? (row, v) => String(get(row) ?? '').toLowerCase().includes(String(v).toLowerCase())
          : col.filter.type === 'bool'
            ? (row) => !!get(row)
            : (row, v) => String(get(row) ?? '') === String(v));
      r = r.filter(row => match(row, val, filters));
    }
    if (sort.key && colMap[sort.key]?.sort) {
      const get = colMap[sort.key].sort;
      r = [...r].sort((a, b) => {
        const av = get(a), bv = get(b);
        const cmp = (typeof av === 'number' && typeof bv === 'number')
          ? av - bv
          : String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return r;
  }, [rows, filters, sort, columns]);

  return { view, sort, toggleSort, filters, setFilter, clearFilters };
}

// A sortable <th>. Wrap the existing header label; non-sortable columns stay <th>.
function SortTh({ ctl, k, children, style, ...rest }) {
  const active = ctl.sort.key === k;
  return (
    <th {...rest} onClick={() => ctl.toggleSort(k)} title="Click to sort"
      style={{ ...style, cursor: 'pointer', userSelect: 'none' }}>
      {children}{' '}
      <span style={{ fontSize: 9, color: active ? ORANGE : '#C7CDD6' }}>
        {active ? (ctl.sort.dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );
}

// Filter bar above a table: per-column inputs/selects + active-filter chips +
// a clear button + an "X of Y" count. Driven by the same column config.
function TableControlsBar({ ctl, columns, total }) {
  const defs = columns.filter(c => c.filter);
  const labelFor = (k) => columns.find(c => c.k === k)?.filter?.label || k;
  const active = Object.entries(ctl.filters);
  const shown = ctl.view.length;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
      {defs.map(c => {
        const val = ctl.filters[c.k] ?? '';
        if (c.filter.type === 'select') {
          return (
            <select key={c.k} value={val} onChange={e => ctl.setFilter(c.k, e.target.value)}
              style={{ ...styles.input, width: 'auto', fontSize: 12, padding: '4px 6px' }}>
              <option value="">{c.filter.label}: all</option>
              {c.filter.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          );
        }
        if (c.filter.type === 'bool') {
          return (
            <label key={c.k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' }}>
              <input type="checkbox" checked={!!ctl.filters[c.k]} onChange={e => ctl.setFilter(c.k, e.target.checked)} />
              {c.filter.label}
            </label>
          );
        }
        return (
          <input key={c.k} value={val} placeholder={`${c.filter.label}…`}
            onChange={e => ctl.setFilter(c.k, e.target.value)}
            style={{ ...styles.input, width: 130, fontSize: 12, padding: '4px 6px' }} />
        );
      })}
      {active.length > 0 && (
        <>
          {active.map(([k, v]) => (
            <span key={k} onClick={() => ctl.setFilter(k, '')} title="Remove filter"
              style={{ fontSize: 11, background: '#FFF7ED', border: `1px solid ${ORANGE}`, color: '#9A3412',
                borderRadius: 12, padding: '2px 8px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(17, 24, 39, 0.12), 0 2px 4px rgba(17, 24, 39, 0.06)' }}>
              {labelFor(k)}{v === true ? '' : `: ${v}`} ✕
            </span>
          ))}
          <button onClick={ctl.clearFilters} style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px' }}>Clear</button>
        </>
      )}
      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6B7280' }}>
        {shown === total ? `${total} line${total === 1 ? '' : 's'}` : `${shown} of ${total}`}
      </span>
    </div>
  );
}

// Per-craft man-hours + headcount, split by Day/Night shift and by phase.
// Pure client-side rollup off the labor lines — total man-hours is OT-rule
// independent, so it reconciles with the Labor table's ST+OT line totals.
const DAY_COLOR = '#B45309';   // amber — matches the Day shift chip
const NIGHT_COLOR = '#4338CA'; // indigo — matches the Night shift chip

function CraftRollup({ laborLines }) {
  const { phases, rows, grand } = craftRollup(laborLines);
  const fmtHrs = (v) => (v ? Math.round(v).toLocaleString() : null);
  const dash = <span style={{ color: '#D1D5DB' }}>—</span>;

  if (rows.length === 0) {
    return (
      <div style={{ ...styles.card, background: '#F9FAFB', padding: 12, marginBottom: 12, fontSize: 12, color: '#6B7280' }}>
        Craft rollup appears once labor lines have phases with dates + headcount.
      </div>
    );
  }

  // One shift cell: man-hours (bold) over headcount (gray).
  const cell = (hrs, hc, color) => (
    <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
      {fmtHrs(hrs)
        ? <>
            <div style={{ fontWeight: 700, color }}>{fmtHrs(hrs)}</div>
            <div style={{ fontSize: 10, color: '#9CA3AF' }}>{hc} HC</div>
          </>
        : dash}
    </td>
  );

  const shiftHead = (label, color) => (
    <th style={{ ...styles.th, textAlign: 'center', color }}>{label}</th>
  );

  return (
    <div style={{ ...styles.card, padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1F2937' }}>Craft Rollup — Days / Nights × Phase</div>
        <div style={{ fontSize: 11, color: '#9CA3AF' }}>
          Billable man-hours (bold) · peak headcount (HC) ·{' '}
          <span style={{ color: DAY_COLOR, fontWeight: 600 }}>☀️ Day</span>{' '}
          <span style={{ color: NIGHT_COLOR, fontWeight: 600 }}>🌙 Night</span>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, textAlign: 'left' }} rowSpan={2}>Craft</th>
              {phases.map(ph => (
                <th key={ph} style={{ ...styles.th, textAlign: 'center', borderLeft: '1px solid #E5E7EB' }} colSpan={2}>{ph}</th>
              ))}
              <th style={{ ...styles.th, textAlign: 'center', borderLeft: '2px solid #1F2937' }} colSpan={3}>Total</th>
            </tr>
            <tr>
              {phases.map(ph => (
                <Fragment key={ph}>
                  {shiftHead('☀️ Day', DAY_COLOR)}
                  {shiftHead('🌙 Night', NIGHT_COLOR)}
                </Fragment>
              ))}
              {shiftHead('☀️ Day', DAY_COLOR)}
              {shiftHead('🌙 Night', NIGHT_COLOR)}
              <th style={{ ...styles.th, textAlign: 'center' }}>Σ Hrs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.craft}>
                <td style={{ ...styles.td, fontWeight: 600 }}>{row.craft}</td>
                {phases.map(ph => (
                  <Fragment key={ph}>
                    {cell(row.cells[ph].dayHrs, row.cells[ph].dayHC, DAY_COLOR)}
                    {cell(row.cells[ph].nightHrs, row.cells[ph].nightHC, NIGHT_COLOR)}
                  </Fragment>
                ))}
                {cell(row.total.dayHrs, row.total.dayHC, DAY_COLOR)}
                {cell(row.total.nightHrs, row.total.nightHC, NIGHT_COLOR)}
                <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700 }}>{fmtHrs(row.total.hrs) || dash}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #1F2937', background: '#F9FAFB', fontWeight: 700 }}>
              <td style={{ ...styles.td, fontWeight: 700 }}>All Crafts</td>
              {phases.map(ph => (
                <Fragment key={ph}>
                  {cell(grand.byPhase[ph].dayHrs, grand.byPhase[ph].dayHC, DAY_COLOR)}
                  {cell(grand.byPhase[ph].nightHrs, grand.byPhase[ph].nightHC, NIGHT_COLOR)}
                </Fragment>
              ))}
              {cell(grand.total.dayHrs, grand.total.dayHC, DAY_COLOR)}
              {cell(grand.total.nightHrs, grand.total.nightHC, NIGHT_COLOR)}
              <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700 }}>{fmtHrs(grand.total.hrs) || dash}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 6 }}>
        Total HC columns show peak headcount across phases. Man-hours total (ST+OT) matches the Labor table.
      </div>
    </div>
  );
}

// Field-picker modal for copying a source line's phases / per diem / miles to
// the selected target lines. Headcount and mileage rates are never copied.
function CopyToSelectedPanel({ source, targetIds, saving, onApply, onCancel }) {
  const [fields, setFields] = useState({ phase_schedule: true, per_diem: true, per_diem_travel: false, mileage_miles: false });
  const t = (k) => setFields(f => ({ ...f, [k]: !f[k] }));
  const chosen = Object.keys(fields).filter(k => fields[k]);
  const n = targetIds.length;
  const row = (k, label, sub) => (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0', cursor: 'pointer' }}>
      <input type="checkbox" checked={fields[k]} onChange={() => t(k)} style={{ marginTop: 3 }} />
      <span><span style={{ fontWeight: 600, color: '#374151' }}>{label}</span>
        {sub && <span style={{ display: 'block', fontSize: 11, color: '#9CA3AF' }}>{sub}</span>}</span>
    </label>
  );
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 20,
        width: 440, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#1F2937' }}>Copy to selected lines</h3>
        <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
          From <strong>{source.craft || '(unnamed)'}</strong> → <strong>{n}</strong> selected line{n === 1 ? '' : 's'}
        </div>
        {n === 0
          ? <div style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FCD34D',
              borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
              Tick the target lines first (the checkbox by each craft name), then reopen this from the source line.
            </div>
          : <div style={{ borderTop: '1px solid #F1F5F9', borderBottom: '1px solid #F1F5F9', margin: '4px 0 14px' }}>
              {row('phase_schedule', 'Phase schedule', 'Dates, work days, hrs/day — never headcount')}
              {row('per_diem', 'Per diem', 'Eligible + $/day (bill & cost) + basis')}
              {row('per_diem_travel', '…also travel days', 'Off by default — travel days vary (e.g. rescue leads = 4)')}
              {row('mileage_miles', 'Mileage — miles', 'Rate stays standard; miles vary (leads 2,400 vs 1,200)')}
            </div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={{ ...styles.btnSecondary, fontSize: 13 }} onClick={onCancel}>Cancel</button>
          <button style={{ ...styles.btn, fontSize: 13 }} disabled={saving || n === 0 || !chosen.length}
            onClick={() => onApply({ source_id: source.id, target_ids: targetIds, copy_fields: chosen })}>
            Apply to {n}
          </button>
        </div>
      </div>
    </div>
  );
}

// Always-visible, read-only one-line recap under each labor line: phases
// (dates / work days / hrs / day+night headcount), per diem, and mileage —
// so the whole line can be verified without opening the Phases editor.
// The recap must read as part of the line ABOVE it: borderTop 'hidden' wins
// the border-collapse conflict against the line's own td borderBottom, so the
// only separator is the standard border BELOW the recap (between line groups).
// `bg` is the GROUP background computed in the map (zebra stripe, or orange
// when below threshold) — row and recap must always share it, or the recap
// reads as the next line's. The L# chip names the owner.
function LaborLineSummary({ line, comp, bg }) {
  if (line.rate_only) return null;
  const md = (d) => { const s = String(d || '').slice(0, 10); if (!s) return ''; const [, m, dd] = s.split('-'); return `${+m}/${+dd}`; };
  const WD = { all: 'all days', mon_fri: 'M–F', mon_thu: 'M–Th', mon_sat: 'M–Sa', rot_13_1: '13/1', rot_13_1_stag: '13/1 stag' };
  const order = ['Pre-TA', 'Mechanical', 'Post-TA'];
  const phases = (line.phases || [])
    .filter(p => ((Number(p.day_hc) || 0) + (Number(p.night_hc) || 0)) > 0 && p.start_date && p.end_date)
    .sort((a, b) => order.indexOf(a.phase) - order.indexOf(b.phase));
  const pdEligible = !!line.per_diem_eligible;
  const miles = Number(line.mob_miles_one_way) || 0;
  if (!phases.length && !pdEligible && !miles) return null;

  const seg = { fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' };
  const lbl = { color: '#374151', fontWeight: 700 };
  return (
    <tr style={{ background: bg }}>
      <td colSpan={18} style={{ padding: '0 12px 8px', borderTop: 'hidden', borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
          <span title={`Recap of line L${line.line_no} above — phases, per diem, mileage`}
            style={{ background: '#EEF2FF', color: '#4338CA', fontWeight: 800, fontSize: 10,
              borderRadius: 4, padding: '1px 6px' }}>
            L{line.line_no}
          </span>
          {phases.length > 0 && (
            <span style={{ ...seg, display: 'inline-flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <span style={lbl}>Phases:</span>
              {Number(line.prior_week_hours) > 0 && (
                <span title="Hours each head already worked in week 1 before this line starts — counts toward the weekly 40">
                  +{Number(line.prior_week_hours)}h prior wk
                </span>
              )}
              {phases.map(p => {
                const dh = Number(p.day_hc) || 0, nh = Number(p.night_hc) || 0;
                return (
                  <span key={p.phase}>
                    <strong style={{ color: '#374151' }}>{p.phase}</strong>{' '}
                    {md(p.start_date)}–{md(p.end_date)}{' '}
                    <span style={{ color: '#9CA3AF' }}>({WD[p.work_days] || p.work_days} · {Number(p.hours_per_day) || 0}h)</span>{' '}
                    {dh > 0 && <span style={{ color: '#B45309', fontWeight: 700 }}>☀️{dh}</span>}
                    {nh > 0 && <span style={{ color: '#4338CA', fontWeight: 700, marginLeft: dh > 0 ? 4 : 0 }}>🌙{nh}</span>}
                  </span>
                );
              })}
            </span>
          )}
          <span style={seg}>
            <span style={lbl}>Per diem:</span>{' '}
            {pdEligible
              ? <>${Number(line.per_diem_bill) || 0}/day · {comp.perDiemDays || 0} days · <strong>{fmtMoney(comp.pdRevenue)}</strong>{' '}
                  <span style={{ color: '#9CA3AF' }}>({line.per_diem_mode === 'work_days' ? 'work days' : 'calendar days'}{Number(line.travel_days) ? ` +${line.travel_days} travel` : ''})</span></>
              : <span style={{ color: '#9CA3AF' }}>none</span>}
          </span>
          <span style={seg}>
            <span style={lbl}>Mileage:</span>{' '}
            {miles > 0
              ? <>{miles.toLocaleString()} mi one-way ×2 @ ${Number(line.mob_bill_rate) || 0}/mi · <strong>{fmtMoney(comp.mobRevenue)}</strong></>
              : <span style={{ color: '#9CA3AF' }}>none</span>}
          </span>
        </div>
      </td>
    </tr>
  );
}

function PhaseEditor({ phases, otRule, priorWeekHours, isFco, onSave }) {
  const byPhase = {};
  for (const ph of PHASES) {
    const found = phases.find(p => p.phase === ph);
    // pg DATE columns serialize as ISO timestamps — date inputs need YYYY-MM-DD.
    // hours_per_day: seeded phase rows carry NULL — the input displayed 10 via
    // ?? but the state stayed null and saved null, zeroing all hours. Make the
    // displayed default REAL state so it saves.
    byPhase[ph] = found
      ? { ...found,
          start_date: found.start_date ? String(found.start_date).slice(0, 10) : '',
          end_date: found.end_date ? String(found.end_date).slice(0, 10) : '',
          hours_per_day: found.hours_per_day ?? 10 }
      : { phase: ph, start_date: '', end_date: '', hours_per_day: 10, day_hc: 0, night_hc: 0, work_days: 'all' };
  }
  const [local, setLocal] = useState(byPhase);

  const set = (phase, field, value) => setLocal(prev => ({ ...prev, [phase]: { ...prev[phase], [field]: value } }));
  const totals = phaseHoursPreview(Object.values(local), otRule || 'weekly_over_40', priorWeekHours);

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, color: '#374151' }}>Phases</div>
      <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
        <thead><tr style={{ borderBottom: '1px solid #E5E7EB' }}>
          <th style={{ ...styles.th, fontSize: 11 }}>Phase</th>
          <th style={{ ...styles.th, fontSize: 11 }}>Start</th>
          <th style={{ ...styles.th, fontSize: 11 }}>End</th>
          <th style={{ ...styles.th, fontSize: 11 }} title="Which weekdays count as work days — no hours book on the others">Days</th>
          <th style={{ ...styles.th, fontSize: 11 }}>Hrs/Day</th>
          <th style={{ ...styles.th, fontSize: 11 }}>Day HC</th>
          <th style={{ ...styles.th, fontSize: 11 }}>Night HC</th>
        </tr></thead>
        <tbody>
          {PHASES.map((ph, i) => {
            const row = local[ph];
            // Chain phases: phase N's start picker opens at phase N-1's end so a
            // months-out estimate doesn't make you flip the calendar (min on a
            // future date positions the native picker there). No value is set —
            // dates only auto-fill via the "days" box (George 2026-06-19).
            const prevEnd = i > 0 ? (local[PHASES[i - 1]]?.end_date || '') : '';
            const badRange = row.start_date && row.end_date && row.end_date < row.start_date;
            return (
              <tr key={ph}>
                <td style={{ ...styles.td, fontWeight: 600, color: '#374151' }}>{ph}</td>
                <td style={styles.td}>
                  <input type="date" style={{ ...styles.input, fontSize: 11, padding: '3px 6px' }}
                    min={prevEnd || undefined}
                    value={row.start_date || ''} onChange={e => set(ph, 'start_date', e.target.value)} />
                </td>
                <td style={styles.td}>
                  <input type="date" style={{ ...styles.input, fontSize: 11, padding: '3px 6px' }}
                    min={row.start_date || undefined}
                    value={row.end_date || ''} onChange={e => set(ph, 'end_date', e.target.value)} />
                  <input type="number" min="1" placeholder="days" title="Enter # of days to fill End from Start (inclusive)"
                    style={{ ...styles.input, width: 46, fontSize: 11, padding: '3px 4px', marginLeft: 4 }}
                    value={dayCountISO(row.start_date, row.end_date) || ''}
                    onChange={e => { const end = endFromDays(row.start_date, e.target.value); if (end) set(ph, 'end_date', end); }} />
                  {badRange && <div style={{ color: '#B91C1C', fontSize: 10, fontWeight: 700 }}>ends before start</div>}
                </td>
                <td style={styles.td}>
                  <select style={{ ...styles.input, width: 100, fontSize: 11, padding: '3px 6px' }}
                    value={row.work_days || 'all'} onChange={e => set(ph, 'work_days', e.target.value)}>
                    {WORK_DAYS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  <select style={{ ...styles.input, width: 64, fontSize: 11, padding: '3px 4px' }}
                    title="Quick-set the standard shift lengths; type a custom value alongside"
                    value={HPD_PRESETS.includes(Number(row.hours_per_day)) ? String(Number(row.hours_per_day)) : 'custom'}
                    onChange={e => { if (e.target.value !== 'custom') set(ph, 'hours_per_day', Number(e.target.value)); }}>
                    {HPD_PRESETS.map(h => <option key={h} value={h}>{h}h</option>)}
                    <option value="custom">Custom</option>
                  </select>
                  <input type="number" min="0" max="24" step="0.5" style={{ ...styles.input, width: 48, fontSize: 11, padding: '3px 4px', marginLeft: 3 }}
                    value={row.hours_per_day} onChange={e => set(ph, 'hours_per_day', Number(e.target.value))} />
                </td>
                <td style={styles.td}>
                  <input type="number" min="0" style={{ ...styles.input, width: 50, fontSize: 11, padding: '3px 6px' }}
                    value={row.day_hc || 0} onChange={e => set(ph, 'day_hc', Number(e.target.value))} />
                </td>
                <td style={styles.td}>
                  <input type="number" min="0" style={{ ...styles.input, width: 50, fontSize: 11, padding: '3px 6px' }}
                    value={row.night_hc || 0} onChange={e => set(ph, 'night_hc', Number(e.target.value))} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <button style={{ ...styles.btn, fontSize: 12 }}
          onClick={() => {
            // Popup alert (George 2026-07-19): a dated phase with no headcount
            // books ZERO hours — easy to miss, wrecks the estimate quietly.
            const noHc = Object.values(local)
              .filter(p => p.start_date && p.end_date && (Number(p.day_hc) || 0) + (Number(p.night_hc) || 0) === 0)
              .map(p => p.phase);
            if (noHc.length && !window.confirm(
              `⚠ ${noHc.join(', ')} ${noHc.length === 1 ? 'has' : 'have'} dates but NO headcount (Day HC + Night HC = 0) — no hours will book for ${noHc.length === 1 ? 'that phase' : 'those phases'}.\n\nSave anyway?`
            )) return;
            onSave(Object.values(local));
          }}>
          Save Phases
        </button>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1F2937' }}>
          Billable hours: {totals.st.toLocaleString()} ST / {totals.ot.toLocaleString()} OT
          <span style={{ fontWeight: 400, color: '#9CA3AF' }}> · {totals.workDays} work days · {OT_RULES.find(r => r.value === (otRule || 'weekly_over_40'))?.label}
            {(otRule || 'weekly_over_40') === 'weekly_over_40' && Number(priorWeekHours) > 0 && ` · ${Number(priorWeekHours)} hrs already worked in week 1`}</span>
        </span>
        {isFco && (otRule || 'weekly_over_40') === 'weekly_over_40' && priorWeekHours == null && (
          <span style={{ fontSize: 12, fontWeight: 700, color: '#B91C1C' }}>
            ⚠ FCO: fill in Hrs Already Worked (Wk 1) below, then Save Extras
          </span>
        )}
      </div>
    </div>
  );
}

// Equipment phases — lighter than labor (qty + dates per phase, one rate from the
// line). Leave every qty at 0 to bill the flat line instead. Days are derived.
function EquipPhaseEditor({ phases, rate, onSave }) {
  const byPhase = {};
  for (const ph of PHASES) {
    const found = (phases || []).find(p => p.phase === ph);
    byPhase[ph] = found
      ? { phase: ph,
          start_date: found.start_date ? String(found.start_date).slice(0, 10) : '',
          end_date: found.end_date ? String(found.end_date).slice(0, 10) : '',
          qty: Number(found.qty) || 0 }
      : { phase: ph, start_date: '', end_date: '', qty: 0 };
  }
  const [local, setLocal] = useState(byPhase);
  const set = (phase, field, value) => setLocal(prev => ({ ...prev, [phase]: { ...prev[phase], [field]: value } }));
  const dayCount = (s, e) => { if (!s || !e) return 0; const ms = new Date(e + 'T12:00:00Z') - new Date(s + 'T12:00:00Z'); return ms >= 0 ? Math.round(ms / 86400000) + 1 : 0; };
  const dayRate = Number(rate) || 0;
  const total = Object.values(local).reduce((sum, r) => {
    const q = Number(r.qty) || 0, dc = dayCount(r.start_date, r.end_date);
    return sum + (q > 0 && dc > 0 ? q * dc * dayRate : 0);
  }, 0);
  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, color: '#374151' }}>
        Equipment Phases <span style={{ fontWeight: 400, color: '#9CA3AF' }}>— qty + dates per phase; rate ${dayRate}/day carries from the line</span>
      </div>
      <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
        <thead><tr style={{ borderBottom: '1px solid #E5E7EB' }}>
          <th style={{ ...styles.th, fontSize: 11 }}>Phase</th>
          <th style={{ ...styles.th, fontSize: 11 }}>Start</th>
          <th style={{ ...styles.th, fontSize: 11 }}>End</th>
          <th style={{ ...styles.th, fontSize: 11 }}>Days</th>
          <th style={{ ...styles.th, fontSize: 11 }}>Qty</th>
        </tr></thead>
        <tbody>
          {PHASES.map((ph, i) => {
            const row = local[ph];
            const dc = dayCount(row.start_date, row.end_date);
            const prevEnd = i > 0 ? (local[PHASES[i - 1]]?.end_date || '') : '';
            const badRange = row.start_date && row.end_date && row.end_date < row.start_date;
            return (
              <tr key={ph}>
                <td style={{ ...styles.td, fontWeight: 600, color: '#374151' }}>{ph}</td>
                <td style={styles.td}>
                  <input type="date" style={{ ...styles.input, fontSize: 11, padding: '3px 6px' }}
                    min={prevEnd || undefined}
                    value={row.start_date || ''} onChange={e => set(ph, 'start_date', e.target.value)} />
                </td>
                <td style={styles.td}>
                  <input type="date" style={{ ...styles.input, fontSize: 11, padding: '3px 6px' }}
                    min={row.start_date || undefined}
                    value={row.end_date || ''} onChange={e => set(ph, 'end_date', e.target.value)} />
                  {badRange && <div style={{ color: '#B91C1C', fontSize: 10, fontWeight: 700 }}>ends before start</div>}
                </td>
                <td style={styles.td}>
                  <input type="number" min="1" placeholder="days" title="Enter # of days to fill End from Start (inclusive)"
                    style={{ ...styles.input, width: 52, fontSize: 11, padding: '3px 4px' }}
                    value={dc || ''}
                    onChange={e => { const end = endFromDays(row.start_date, e.target.value); if (end) set(ph, 'end_date', end); }} /></td>
                <td style={styles.td}>
                  <input type="number" min="0" style={{ ...styles.input, width: 60, fontSize: 11, padding: '3px 6px' }}
                    value={row.qty || 0} onChange={e => set(ph, 'qty', Number(e.target.value))} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <button style={{ ...styles.btn, fontSize: 12 }} onClick={() => {
          // Popup alert (George 2026-07-19): dates entered but qty forgotten.
          // ALL qty 0 is the deliberate bill-the-flat-line mode — no alert then.
          const rows = Object.values(local);
          const anyQty = rows.some(p => (Number(p.qty) || 0) > 0);
          const noQty = rows.filter(p => p.start_date && p.end_date && (Number(p.qty) || 0) === 0).map(p => p.phase);
          if (anyQty && noQty.length && !window.confirm(
            `⚠ ${noQty.join(', ')} ${noQty.length === 1 ? 'has' : 'have'} dates but qty 0 — nothing bills for ${noQty.length === 1 ? 'that phase' : 'those phases'}.\n\nSave anyway?`
          )) return;
          onSave(rows);
        }}>Save Phases</button>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1F2937' }}>
          Phase total: {fmtMoney(total)}
          <span style={{ fontWeight: 400, color: '#9CA3AF' }}> · all qty 0 → bills the flat line instead</span>
        </span>
      </div>
    </div>
  );
}

function LaborExtrasEditor({ line, estimateId, otRule, isFco, onSave }) {
  const [local, setLocal] = useState({
    per_diem_eligible: !!line.per_diem_eligible,
    per_diem_bill: line.per_diem_bill || 0,
    per_diem_cost: line.per_diem_cost || 0,
    per_diem_mode: line.per_diem_mode || 'calendar',
    mob_miles_one_way: line.mob_miles_one_way || 0,
    mob_cost_rate: line.mob_cost_rate || 0,
    mob_bill_rate: line.mob_bill_rate || 0,
    travel_days: line.travel_days || 0,
    prior_week_hours: line.prior_week_hours == null ? '' : Number(line.prior_week_hours),
  });
  const set = (k, v) => setLocal(prev => ({ ...prev, [k]: v }));
  // AI mileage (George 2026-07-02): reads the request's Q2 site location and
  // estimates one-way driving miles from Pasadena, TX HQ. Fills the input
  // only — leadership reviews, then Save Extras persists.
  const [aiMiles, setAiMiles] = useState(null);
  const aiEstimateMiles = async () => {
    setAiMiles({ busy: true, text: 'Reading the request location…' });
    try {
      const r = await api.estimateMobMiles(estimateId);
      set('mob_miles_one_way', Math.max(0, Math.round(Number(r.miles_one_way) || 0)));
      setAiMiles({ ok: true, text: `~${r.miles_one_way} mi → ${r.destination_city}, ${r.destination_state} (${r.confidence}) — review, then Save Extras` });
    } catch (e) {
      setAiMiles({ ok: false, text: e.message });
    }
  };
  return (
    <>
      {(otRule || 'weekly_over_40') === 'weekly_over_40' && (() => {
        // Highlighted so nobody misses it; required on an FCO (George
        // 2026-11-03) — the server refuses Submit / Approve while it's blank.
        const blank = local.prior_week_hours === '' || local.prior_week_hours == null;
        const missing = isFco && blank;
        return (
          <div style={{ border: `2px solid ${missing ? '#DC2626' : '#EF6622'}`, background: missing ? '#FEF2F2' : '#FFF7ED',
            borderRadius: 6, padding: '4px 6px' }}>
            <label style={{ ...styles.label, fontSize: 11, color: missing ? '#B91C1C' : '#9A3412', fontWeight: 800 }}
              title="Hours each person already worked onsite that week before this line starts. They count toward the weekly 40, so a mid-week extension prices as OT once the cap is hit. Applies to the first week only; 0 = none.">
              Hrs Already Worked (Wk 1){isFco ? ' *' : ''}
            </label>
            <input type="number" min="0" max="168" step="0.25" placeholder={isFco ? 'Required' : '0'}
              style={{ ...styles.input, fontSize: 11, borderColor: missing ? '#DC2626' : '#EF6622' }}
              value={local.prior_week_hours} onChange={e => set('prior_week_hours', e.target.value === '' ? '' : Number(e.target.value))} />
            {isFco && (
              <div style={{ fontSize: 10, marginTop: 2, fontWeight: 600, color: missing ? '#B91C1C' : '#9A3412' }}>
                {missing ? 'Required on an FCO. 0 if it starts a fresh week.' : 'FCO: counts toward the weekly 40.'}
              </div>
            )}
          </div>
        );
      })()}
      <div>
        <label style={{ ...styles.label, fontSize: 11 }}>Per Diem Eligible</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, paddingTop: 6 }}>
          <input type="checkbox" checked={local.per_diem_eligible} onChange={e => set('per_diem_eligible', e.target.checked)} />
          Eligible
        </label>
      </div>
      {local.per_diem_eligible && (
        <>
          <div>
            <label style={{ ...styles.label, fontSize: 11 }}>PD Bill $/day</label>
            <input type="number" step="0.01" style={{ ...styles.input, fontSize: 11 }}
              value={local.per_diem_bill}
              onChange={e => { const v = Number(e.target.value); setLocal(prev => ({ ...prev, per_diem_bill: v, per_diem_cost: v })); }} />
          </div>
          <div>
            <label style={{ ...styles.label, fontSize: 11 }} title="Auto-matches the bill (a wash); edit to override for a one-off">PD Cost $/day</label>
            <input type="number" step="0.01" style={{ ...styles.input, fontSize: 11 }}
              value={local.per_diem_cost} onChange={e => set('per_diem_cost', Number(e.target.value))} />
          </div>
          <div>
            <label style={{ ...styles.label, fontSize: 11 }}>Travel Days</label>
            <input type="number" min="0" style={{ ...styles.input, fontSize: 11 }}
              value={local.travel_days} onChange={e => set('travel_days', Number(e.target.value))} />
          </div>
          <div>
            <label style={{ ...styles.label, fontSize: 11 }}
              title="7 days/wk (SSS standard): per diem pays every calendar day of the phase span — weekends and rollover days included. Work days only: pays only days hours book.">PD Days Basis</label>
            <select style={{ ...styles.input, fontSize: 11 }}
              value={local.per_diem_mode} onChange={e => set('per_diem_mode', e.target.value)}>
              <option value="calendar">7 days/wk (calendar)</option>
              <option value="work_days">Work days only</option>
            </select>
          </div>
        </>
      )}
      <div>
        <label style={{ ...styles.label, fontSize: 11 }}>Mob Miles (one-way)</label>
        <input type="number" min="0" style={{ ...styles.input, fontSize: 11 }}
          value={local.mob_miles_one_way} onChange={e => set('mob_miles_one_way', Number(e.target.value))} />
        <button type="button" style={{ ...styles.btnSecondary, fontSize: 10, padding: '2px 6px', marginTop: 3 }}
          disabled={aiMiles?.busy}
          title="AI reads the sales request's Customer/Location answer (Q2) and estimates one-way driving miles from Pasadena, TX HQ. Fills the box for your review — Save Extras applies it."
          onClick={aiEstimateMiles}>
          {aiMiles?.busy ? '✨ Estimating…' : '✨ From Pasadena, TX'}
        </button>
        {aiMiles && !aiMiles.busy && (
          <div style={{ fontSize: 10, marginTop: 2, fontWeight: 600, color: aiMiles.ok ? '#065F46' : '#B91C1C' }}>
            {aiMiles.ok ? '✓ ' : '⚠ '}{aiMiles.text}
          </div>
        )}
      </div>
      <div>
        <label style={{ ...styles.label, fontSize: 11 }} title="Auto-matches the bill (pass-through); edit to override for a one-off">Mob Cost $/mi</label>
        <input type="number" step="0.0001" style={{ ...styles.input, fontSize: 11 }}
          value={local.mob_cost_rate} onChange={e => set('mob_cost_rate', Number(e.target.value))} />
      </div>
      <div>
        <label style={{ ...styles.label, fontSize: 11 }}>Mob Bill $/mi</label>
        <input type="number" step="0.0001" style={{ ...styles.input, fontSize: 11 }}
          value={local.mob_bill_rate} onChange={e => { const v = Number(e.target.value); setLocal(prev => ({ ...prev, mob_bill_rate: v, mob_cost_rate: v })); }} />
        <button type="button" style={{ ...styles.btnSecondary, fontSize: 10, padding: '2px 6px', marginTop: 3 }}
          title={`Reset paid & billed mileage to the SSS standard ${STANDARD_MILEAGE_LABEL}/mi (a wash)`}
          onClick={() => setLocal(prev => ({ ...prev, mob_cost_rate: STANDARD_MILEAGE_RATE, mob_bill_rate: STANDARD_MILEAGE_RATE }))}>
          ↺ standard {STANDARD_MILEAGE_LABEL}
        </button>
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <button style={{ ...styles.btn, fontSize: 11 }} onClick={() => {
          // Popup alert (George 2026-07-19): per-diem-eligible crew is
          // out-of-town crew — mileage must be on the line or mob money is
          // silently left off the estimate.
          if (local.per_diem_eligible) {
            const missing = [];
            if (!(Number(local.mob_miles_one_way) > 0)) missing.push('Mob Miles (one-way)');
            if (!(Number(local.mob_cost_rate) > 0)) missing.push('Mob Cost $/mi');
            if (missing.length && !window.confirm(
              `⚠ Per Diem is marked ELIGIBLE but ${missing.join(' and ')} ${missing.length === 1 ? 'is' : 'are'} blank — mobilization mileage won't be in the estimate.\n\nSave anyway?`
            )) return;
          }
          onSave(local);
        }}>Save Extras</button>
      </div>
    </>
  );
}

// ---- Vendor quote files (Third-Party rental + Consumable/Mob lines) ---------
// Compact popover: list / upload / download / delete the vendor's quote files
// for one line. Files are R2-backed rows in the shared attachments table
// (equipment_line_id or consumable_line_id) and follow the chain's current
// revision. `fileApi` swaps the four calls between the equipment and
// consumable endpoints — same UI, different backing table.
const EQUIP_FILE_API = {
  list: api.listEquipLineFiles, upload: api.uploadEquipLineFile,
  download: api.downloadEquipLineFile, remove: api.deleteEquipLineFile,
};
const CONS_FILE_API = {
  list: api.listConsLineFiles, upload: api.uploadConsLineFile,
  download: api.downloadConsLineFile, remove: api.deleteConsLineFile,
};
function VendorQuoteFiles({ lineId, count, fileApi = EQUIP_FILE_API }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const boxRef = useRef(null);
  const load = () => fileApi.list(lineId).then(setFiles).catch(e => { setErr(e.message); setFiles([]); });
  const toggle = () => { const o = !open; setOpen(o); if (o && files == null) load(); };
  // Close on outside click or Esc — otherwise the popover sits open after a drop.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const uploadFiles = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setBusy(true); setErr('');
    try {
      for (const file of list) await fileApi.upload(lineId, file);
      await load();
    } catch (er) { setErr(er.message); }
    finally { setBusy(false); }
  };
  const n = files ? files.length : count;
  return (
    <div ref={boxRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={toggle}
        style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', whiteSpace: 'nowrap' }}
        title="Vendor quote files for this rental">
        📎 {n}
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 30, background: '#fff',
          border: dragOver ? '2px dashed #E36F2C' : '1px solid #E5E7EB', borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,.15)',
          padding: 8, width: 280, marginTop: 2 }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Vendor quotes</div>
          {dragOver && <div style={{ fontSize: 11, color: '#E36F2C', fontWeight: 600, marginBottom: 4 }}>Drop to upload…</div>}
          {err && <div style={{ fontSize: 11, color: '#DC2626', marginBottom: 4 }}>{err}</div>}
          {(files || []).map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '3px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: '#1D4ED8' }}
                title={`Download ${f.original_name}`}
                onClick={() => fileApi.download(lineId, f.id, f.original_name).catch(e => setErr(e.message))}>
                {f.original_name}
              </span>
              <span style={{ color: '#9CA3AF', flexShrink: 0 }}>{Math.round((f.size_bytes || 0) / 1024)} KB</span>
              <button type="button" style={{ border: 'none', background: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 11, padding: 0 }}
                title="Delete file"
                onClick={() => {
                  if (!confirm(`Delete ${f.original_name}?`)) return;
                  fileApi.remove(lineId, f.id).then(load).catch(e => setErr(e.message));
                }}>✕</button>
            </div>
          ))}
          {files && files.length === 0 && !err && (
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4 }}>No files yet.</div>
          )}
          <label style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', display: 'inline-block', marginTop: 6, cursor: busy ? 'wait' : 'pointer' }}>
            {busy ? 'Uploading…' : '⬆ Upload quote'}
            <input type="file" multiple style={{ display: 'none' }} disabled={busy}
              onChange={e => { const fl = Array.from(e.target.files || []); e.target.value = ''; uploadFiles(fl); }} />
          </label>
          <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>or drag &amp; drop files here</div>
        </div>
      )}
    </div>
  );
}

// ---- Quote panel ------------------------------------------------------------
// One quote chain per estimate chain. Create requires an Approved estimate;
// change requests go through Revise (which can pick up a newer approved
// estimate revision). The printable document is rendered into a new window
// from the quote's customer-facing view (bill side only) — browser print
// handles the PDF, no PDF library needed.
const QUOTE_STATUS = ['Draft', 'Sent', 'Accepted', 'Declined'];
const QUOTE_STATUS_COLOR = {
  Draft:    { bg: '#F3F4F6', fg: '#374151' },
  Sent:     { bg: '#DBEAFE', fg: '#1E40AF' },
  Accepted: { bg: '#D1FAE5', fg: '#065F46' },
  Declined: { bg: '#FEE2E2', fg: '#991B1B' },
};

function QuotePanel({ estimate, quotes, saving, doSave, refresh }) {
  const estChainRoot = estimate.root_id || estimate.id;
  const [emailMsg, setEmailMsg] = useState(null); // { id, ok: bool|null, text }

  const emailQuote = (q) => doSave(async () => {
    const qNo = `Q-${q.root_id || q.id}-${q.revision || 0}`;
    // Show WHERE the email is going before sending, so a wrong address can be
    // caught (George's call 2026-06-19). Resolve the recipient first (no send).
    setEmailMsg({ id: q.id, ok: null, text: 'Checking recipient…' });
    let p;
    try { p = await api.emailQuote(q.id, { preview: true }); }
    catch (e) { setEmailMsg({ id: q.id, ok: false, text: e.message || 'Could not check recipient' }); return; }
    if (!p.to) {
      const text = p.reason === 'smtp_unconfigured' ? 'Email is off (SMTP not configured)'
        : p.reason === 'no_recipient' ? 'No account manager email on file for this deal'
        : p.reason === 'not_found' ? 'Quote not found'
        : 'No recipient resolved for this deal';
      setEmailMsg({ id: q.id, ok: false, text });
      return;
    }
    const who = p.rep_name ? `${p.rep_name} <${p.to}>` : p.to;
    let msg = `Send quote ${qNo}${p.customer ? ` (${p.customer})` : ''} to:\n\n    ${who}`;
    if (p.via_notify_list) msg += `\n\n⚠ No account manager resolved for this deal — this is the fallback notify list.`;
    if (p.cc) msg += `\n\nA copy will be CC'd to you (${p.cc}).`;
    if (!confirm(msg)) { setEmailMsg({ id: q.id, ok: null, text: 'Cancelled — not sent' }); return; }

    setEmailMsg({ id: q.id, ok: null, text: 'Sending…' });
    const r = await api.emailQuote(q.id);
    const text = r.sent ? `Emailed to ${r.to}`
      : r.reason === 'smtp_unconfigured' ? 'Email is off (SMTP not configured)'
      : r.reason === 'no_recipient' ? 'No account manager email on file for this deal'
      : r.reason === 'not_found' ? 'Quote not found'
      : 'Could not send — try again';
    setEmailMsg({ id: q.id, ok: !!r.sent, text });
  });

  // A refused create still refreshes: "already exists" means the list on
  // screen is stale, and the existing quote should appear, not just the error.
  const createQuote = () => doSave(async () => {
    try {
      await api.createQuote({ estimate_id: estimate.id });
    } finally {
      await refresh();
    }
  });
  const setStatus = (q, status) => doSave(async () => {
    await api.updateQuote(q.id, { status });
    await refresh();
  });
  const revise = (q) => {
    if (!confirm(`Create quote revision -${Number(q.revision || 0) + 1}? It will price from this chain's current Approved estimate.`)) return;
    doSave(async () => { await api.reviseQuote(q.id); await refresh(); });
  };
  const remove = (q) => {
    if (!confirm(`Delete quote Q-${q.root_id || q.id}-${q.revision || 0}?`)) return;
    doSave(async () => { await api.deleteQuote(q.id); await refresh(); });
  };
  // Print / PDF downloads the SERVER quote PDF (services/quotePdf.js), the same
  // document the AM email carries and the opportunity files (2026-09-28). The
  // hidden-iframe browser print it replaced opened no dialog on George's
  // Chrome; the Budgetary PDF made the same move on 2026-09-26.
  const [pdfBusy, setPdfBusy] = useState(null); // quote id while downloading
  const print = async (q) => {
    setPdfBusy(q.id);
    try { await api.downloadQuotePdf(q.id); }
    catch (e) { alert(e.message || 'Could not build the quote PDF'); }
    finally { setPdfBusy(null); }
  };
  // Budgetary PDF (George 2026-09-24): straight off THIS estimate, any
  // status, no quote record. Sell side only. ONE server renderer
  // (server/services/budgetaryPdf.js) behind Download, Email and the copy
  // filed on the opportunity (2026-09-26). Extra notes print as bullets under
  // Basis & Assumptions; not saved.
  const [budgetNotes, setBudgetNotes] = useState('');
  const [budgetBusy, setBudgetBusy] = useState(false);
  const [budgetMsg, setBudgetMsg] = useState(null);     // { ok: bool|null, text }
  const [budgetMail, setBudgetMail] = useState(null);   // null = closed; { to, cc, me, files } = open
  const downloadBudgetary = async () => {
    setBudgetBusy(true); setBudgetMsg(null);
    try { await api.downloadBudgetaryPdf(estimate.id, budgetNotes); }
    catch (e) { setBudgetMsg({ ok: false, text: e.message || 'Could not build the PDF' }); }
    finally { setBudgetBusy(false); }
  };
  const openBudgetMail = async () => {
    setBudgetBusy(true); setBudgetMsg(null);
    try {
      const r = await api.getBudgetaryRecipients(estimate.id);
      if (!r.smtp) { setBudgetMsg({ ok: false, text: 'Email is off (SMTP not configured)' }); return; }
      setBudgetMail({ to: (r.to || []).join(', '), cc: '', me: r.cc, rep: r.rep_name, files: r.files_to_opportunity });
    } catch (e) { setBudgetMsg({ ok: false, text: e.message || 'Could not load recipients' }); }
    finally { setBudgetBusy(false); }
  };
  const sendBudgetMail = async () => {
    if (!budgetMail.to.trim()) { setBudgetMsg({ ok: false, text: 'Enter at least one To address' }); return; }
    setBudgetBusy(true); setBudgetMsg({ ok: null, text: 'Sending…' });
    try {
      const r = await api.emailBudgetary(estimate.id, { to: budgetMail.to, cc: budgetMail.cc, notes: budgetNotes });
      const filed = r.filed || {};
      const fileText = filed.attached ? (filed.replaced ? ' Opportunity copy replaced.' : ' Filed on the opportunity.')
        : filed.reason === 'no_opportunity' ? ' (No opportunity linked, so nothing was filed.)'
        : filed.reason === 'newer_revision_filed' ? ' A newer revision is already on the opportunity, so its copy was kept.'
        : ' Could not file the copy on the opportunity.';
      setBudgetMsg({ ok: true, text: `Emailed to ${r.to.join(', ')}${r.cc.length ? ` (cc ${r.cc.join(', ')})` : ''}.${fileText}` });
      setBudgetMail(null);
    } catch (e) { setBudgetMsg({ ok: false, text: e.message || 'Could not send' }); }
    finally { setBudgetBusy(false); }
  };

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>Quote — EST-{estChainRoot}-{estimate.revision || 0}{estimate.scope_label ? ` · ${estimate.scope_label}` : ` · ${estimate.project_site}`}</h2>
        <div style={{ textAlign: 'right' }}>
          <button
            style={styles.btn}
            disabled={saving}
            title={estimate.status === 'Approved'
              ? 'Create the customer-facing quote'
              : 'Create a DRAFT quote for viewing — marked "pricing not yet approved" until the estimate is Approved'}
            onClick={createQuote}>
            + Create Quote from EST-{estChainRoot}-{estimate.revision || 0}
          </button>
          {estimate.status !== 'Approved' && (
            <div style={{ fontSize: 11, color: '#B45309', fontWeight: 600, marginTop: 4 }}>
              Creates a DRAFT for viewing — marked "pricing not yet approved" until the estimate is Approved.
            </div>
          )}
        </div>
      </div>
      <div style={{ color: '#9CA3AF', fontSize: 11, marginBottom: 8 }}>
        The quote is the customer-facing output of an Approved estimate — bill rates only, never cost or margin.
        Client change requests go through Revise on the quote (and a new estimate revision when pricing changes).
      </div>
      <div style={{ padding: '10px 12px', marginBottom: 10, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Budgetary Estimate</span>
          <span style={{ color: '#6B7280', fontSize: 11, flex: 1, minWidth: 200 }}>
            Sell-side breakdown of this estimate as it stands now (summary, utilization range, schedule, labor, equipment, consumables). Any status, no quote needed. Never shows cost or margin.
          </span>
          <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }} disabled={budgetBusy}
            onClick={downloadBudgetary}>🖨 Budgetary PDF</button>
          <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }} disabled={budgetBusy || !!budgetMail}
            title="Email the budgetary PDF and file it on the opportunity (replaces this estimate's previous copy)"
            onClick={openBudgetMail}>✉ Email Budgetary</button>
        </div>
        <textarea style={{ ...styles.input, width: '100%', minHeight: 48, fontSize: 12, resize: 'vertical' }}
          placeholder="Extra basis & assumptions for the budgetary PDF, one per line (printed and emailed, not saved)"
          value={budgetNotes} onChange={e => setBudgetNotes(e.target.value)} />
        {budgetMail && (
          <div style={{ marginTop: 8, padding: 10, background: '#fff', border: `1px solid ${ORANGE}`, borderRadius: 6, display: 'grid', gap: 6, fontSize: 12 }}>
            <label style={{ display: 'grid', gap: 2 }}>
              <span style={{ fontWeight: 700 }}>To{budgetMail.rep ? ` (account manager: ${budgetMail.rep})` : ''}</span>
              <input style={{ ...styles.input, fontSize: 12 }} value={budgetMail.to}
                placeholder="name@selectsafetyusa.com, separate several with commas"
                onChange={e => setBudgetMail({ ...budgetMail, to: e.target.value })} />
            </label>
            <label style={{ display: 'grid', gap: 2 }}>
              <span style={{ fontWeight: 700 }}>CC</span>
              <input style={{ ...styles.input, fontSize: 12 }} value={budgetMail.cc}
                placeholder="optional, separate several with commas"
                onChange={e => setBudgetMail({ ...budgetMail, cc: e.target.value })} />
            </label>
            <div style={{ color: '#6B7280', fontSize: 11 }}>
              {budgetMail.me ? `You (${budgetMail.me}) are CC'd automatically. ` : ''}
              {budgetMail.files
                ? 'The PDF is also filed on the opportunity, replacing this estimate’s previous budgetary copy, so Operations and Field Services only see the latest.'
                : 'No opportunity is linked, so the PDF is emailed but not filed.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...styles.btn, fontSize: 12, padding: '4px 12px' }} disabled={budgetBusy} onClick={sendBudgetMail}>Send</button>
              <button style={{ ...styles.btnSecondary, fontSize: 12, padding: '4px 12px' }} disabled={budgetBusy} onClick={() => setBudgetMail(null)}>Cancel</button>
            </div>
          </div>
        )}
        {budgetMsg && (
          <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: budgetMsg.ok === false ? '#B91C1C' : budgetMsg.ok ? '#047857' : '#6B7280' }}>
            {budgetMsg.text}
          </div>
        )}
      </div>
      {quotes.length === 0 && <div style={{ color: '#9CA3AF', fontSize: 13 }}>No quote yet for this estimate. Each estimate on the opportunity carries its own quote.</div>}
      {quotes.map(q => {
        const sc = QUOTE_STATUS_COLOR[q.status] || QUOTE_STATUS_COLOR.Draft;
        return (
          <div key={q.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6,
            background: q.is_current === false ? '#F9FAFB' : '#FFF7ED',
            border: `1px solid ${q.is_current === false ? '#E5E7EB' : ORANGE}`,
            borderRadius: 6, fontSize: 13, flexWrap: 'wrap',
            opacity: q.is_current === false ? 0.65 : 1,
          }}>
            <span style={{ fontWeight: 700 }}>Q-{q.root_id || q.id}-{q.revision || 0}</span>
            <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.fg }}>
              {q.status}
            </span>
            {q.estimate_change_order_of != null && (
              <span title={`Prices a CHANGE ORDER — the estimate is a change order of the EST-${q.estimate_change_order_of} chain. This quote bills ON TOP of the base job's quote, not instead of it.`}
                style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#EDE9FE', color: '#5B21B6', whiteSpace: 'nowrap' }}>
                CHANGE ORDER · of EST-{q.estimate_change_order_of}
              </span>
            )}
            {q.estimate_status && q.estimate_status !== 'Approved' && (
              <span title={`The linked estimate is ${q.estimate_status}. This quote is a draft preview of unapproved pricing.`}
                style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#FEF3C7', color: '#92400E' }}>
                DRAFT · unapproved pricing
              </span>
            )}
            <span style={{ color: '#6B7280', fontSize: 11 }}>
              from EST-{q.estimate_root_id || q.estimate_id}-{q.estimate_revision || 0}
              {q.sent_at ? ` · sent ${fmtDate(q.sent_at)}` : ''}
              {q.responded_at ? ` · responded ${fmtDate(q.responded_at)}` : ''}
              {q.is_current === false ? ' · Superseded' : ''}
            </span>
            <span style={{ flex: 1 }} />
            {q.is_current !== false && (
              <>
                <select style={{ ...styles.input, width: 116, fontSize: 12, fontWeight: 800,
                    background: sc.bg, color: sc.fg, textTransform: 'uppercase',
                    letterSpacing: '0.03em', cursor: 'pointer' }}
                  value={q.status}
                  onChange={e => setStatus(q, e.target.value)}>
                  {QUOTE_STATUS.map(s => <option key={s}>{s}</option>)}
                </select>
                <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }}
                  onClick={() => revise(q)}>📝 Revise</button>
              </>
            )}
            <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }}
              disabled={pdfBusy === q.id}
              title="Download this quote as a PDF (the same file the email sends)"
              onClick={() => print(q)}>{pdfBusy === q.id ? 'Building…' : '🖨 Print / PDF'}</button>
            {q.is_current !== false && (
              <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px' }}
                title="Email this quote PDF to the account manager who owns the deal"
                onClick={() => emailQuote(q)}>📧 Email to Sales</button>
            )}
            {q.status === 'Draft' && (
              <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 8px' }}
                onClick={() => remove(q)}>Del</button>
            )}
            {emailMsg && emailMsg.id === q.id && (
              <div style={{ flexBasis: '100%', fontSize: 11, fontWeight: 600, marginTop: 2,
                color: emailMsg.ok === false ? '#B91C1C' : emailMsg.ok ? '#065F46' : '#6B7280' }}>
                {emailMsg.ok === false ? '⚠ ' : emailMsg.ok ? '✓ ' : ''}{emailMsg.text}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Personnel tab — submittal manpower roster. Slots are seeded from each labor
// line's headcount (Day/Night × positions); you fill Name/Email/Phone. The
// export's timeline comes from the craft phases. Save upserts; blanks delete.
function PersonnelPanel({ current, onSave, onExport, onExportOrg }) {
  // Slot derivation EXTRACTED to components/CrewEditor.jsx (2026-07-30):
  // buildCrewSlots is the position model + schedule/sort + saved-row fallback
  // (unnamed slots print TBD server side), shared with the ops-facing Crew
  // editor on the Operations Allocations drill-in so the two surfaces can
  // never disagree on which slots exist. Server mirror stays in
  // estimatePersonnelGantt.js — keep all three in lockstep.
  // MEMOIZED + RE-SYNCED (2026-07-31). This was `buildCrewSlots(current)` with
  // a bare `useState(init)` and no effect, so `vals` froze at whatever the FIRST
  // render produced. Switching estimates does not remount this panel, so opening
  // Cenovus Lima (25 slots) after Formosa (3) left `vals` holding Formosa's three
  // keys — none of which match Cenovus's slots — and every name box rendered
  // blank over a fully populated database. It looked exactly like data loss and
  // it was not: nothing had been written. The ops twin CrewEditorCard already
  // had this effect; the two are meant to behave identically.
  const { slots, init } = useMemo(() => buildCrewSlots(current), [current]);
  const ORDER = SECTION_ORDER;
  const md = mdDate;
  const [vals, setVals] = useState(init);
  useEffect(() => { setVals(init); }, [init]);
  // Unsaved names must not vanish silently on a tab close / reload either.
  const dirty = useMemo(() => JSON.stringify(vals) !== JSON.stringify(init), [vals, init]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const set = (key, field, value) => setVals(prev => ({ ...prev, [key]: { ...(prev[key] || { name: '', email: '', phone: '' }), [field]: value } }));

  // Roster suggestions: active employees feed the Name datalist; picking one
  // fills in a blank Email/Phone. New names entered here are added back to the
  // roster on save (server-side in PUT /personnel), so reload after each save.
  const [roster, setRoster] = useState([]);
  const loadRoster = useCallback(() => {
    api.listEmployees({ active: true })
      .then(rows => setRoster((rows || []).map(e => ({ name: e.full_name || '', email: e.email || '', phone: e.phone || '' })).filter(e => e.name)))
      .catch(() => {});
  }, []);
  useEffect(() => { loadRoster(); }, [loadRoster]);
  const rosterByName = useMemo(() => {
    const m = {};
    for (const e of roster) m[e.name.trim().toLowerCase()] = e;
    return m;
  }, [roster]);
  // The contact detail belongs to the PERSON in the slot, not to the slot
  // (George 2026-07-31: "if name is deleted, it should also delete the other
  // info and if name is added it should delete and replace info"). Before this,
  // clearing a name left the previous person's address and number sitting in the
  // row — which is how a blank slot ended up carrying macario.guevara@ and his
  // phone, and how a re-staffed slot could email the wrong man.
  const setName = (key, value) => setVals(prev => {
    const cur = prev[key] || { name: '', email: '', phone: '' };
    if (!value.trim()) return { ...prev, [key]: { name: '', email: '', phone: '' } };
    const emp = rosterByName[value.trim().toLowerCase()];
    // A known person REPLACES the contact detail outright.
    if (emp) return { ...prev, [key]: { name: value, email: emp.email || '', phone: emp.phone || '' } };
    // Editing AWAY from a known person drops their detail immediately, so a
    // re-staffed slot can never email the man who used to hold it. Gated on the
    // previous name being a roster match: while someone hand-types a new hire
    // and their address, every keystroke would otherwise wipe what they typed.
    const prevWasKnown = !!rosterByName[(cur.name || '').trim().toLowerCase()];
    if (prevWasKnown) return { ...prev, [key]: { name: value, email: '', phone: '' } };
    return { ...prev, [key]: { ...cur, name: value } };
  });

  const save = async () => {
    await onSave(slots.map(s => ({
      labor_line_id: s.labor_line_id, shift: s.shift, slot_index: s.slot_index,
      name: (vals[s.key] || {}).name || '', email: (vals[s.key] || {}).email || '', phone: (vals[s.key] || {}).phone || '',
    })));
    loadRoster();   // pick up anyone newly added to the roster
  };

  const present = [...new Set(slots.map(s => s.section))];
  const ordered = [...ORDER.filter(s => present.includes(s)), ...present.filter(s => !ORDER.includes(s)).sort()];
  // A slot is filled when it has a PERSON in it. Counting a stray email as
  // "filled" is what let a nameless row read as staffed.
  const filled = slots.filter(s => ((vals[s.key] || {}).name || '').trim()).length;
  const [payrollOpen, setPayrollOpen] = useState(false);

  if (!slots.length) return (
    <div style={styles.card}><p style={{ color: '#6B7280', margin: 0 }}>No staffed labor yet — add crews with headcount on the Labor tab and their name slots appear here.</p></div>
  );
  return (
    <div style={styles.card}>
      <datalist id="personnel-roster-names">
        {roster.map((e, i) => <option key={i} value={e.name} />)}
      </datalist>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <h2 style={{ ...styles.h2, margin: 0 }}>Personnel</h2>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{filled} of {slots.length} slots filled · seeded from labor headcount · names suggest from the roster (new ones saved back)</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...styles.btn, fontSize: 12 }} onClick={save}>Save</button>
          {/* Change orders never sync crew — the server 400s it (the removal
              list would be the entire base crew). */}
          {current.project_id && !current.change_order_of && (
            <button style={{ ...styles.btnSecondary, fontSize: 12 }}
              title="Reconcile the Operations Allocations with THIS crew list — adds new people, updates crafts/shifts/dates, and offers to remove crew no longer on the estimate. Nothing is removed without your confirmation."
              onClick={async () => {
                try {
                  const d = await api.syncOpsCrew(current.id, { dry: true });
                  if (!d.adds.length && !d.updates.length && !d.removals.length) { alert('Operations crew already matches this Personnel list.'); return; }
                  const summary = [
                    d.adds.length ? `ADD ${d.adds.length}: ${d.adds.map(a => a.name).join(', ')}` : '',
                    d.updates.length ? `UPDATE ${d.updates.length}: ${d.updates.map(u => `${u.name} (${u.changes.join('; ')})`).join(' · ')}` : '',
                  ].filter(Boolean).join('\n\n');
                  if (summary && !window.confirm(`Sync Operations crew for this job?\n\n${summary}`)) return;
                  let removeIds = [];
                  if (d.removals.length) {
                    const names = d.removals.map(r => `${r.name} (${r.craft}${r.shift ? ' · ' + r.shift : ''})`).join('\n');
                    if (window.confirm(`These ${d.removals.length} assignment(s) on the job are NOT on this estimate's personnel (removed by a revision, or ops-added):\n\n${names}\n\nRemove them from the job too?\n\nOK = remove · Cancel = keep them`)) {
                      removeIds = d.removals.map(r => r.allocation_id);
                    }
                  }
                  const r = await api.syncOpsCrew(current.id, { remove_ids: removeIds });
                  alert(`Synced: +${r.added} added, ${r.updated} updated, ${r.removed} removed.${(r.conflicts || []).length ? '\n\nConflicts:\n' + r.conflicts.join('\n') : ''}`);
                } catch (e) { alert('Sync failed: ' + e.message); }
              }}>
              ⇄ Sync Ops Crew
            </button>
          )}
          <button style={{ ...styles.btnSecondary, fontSize: 12 }}
            title="Review each person's PAY rate (pre-filled from the estimate, editable) then email the mobilization report PDF to payroll. Uses the last SAVED personnel — hit Save first if you just typed names."
            onClick={async () => { await save(); setPayrollOpen(true); }}>
            ✉ Payroll Report
          </button>
          <button style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={onExport}>⬇ Personnel Gantt</button>
          <button style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={onExportOrg}>⬇ Org Chart</button>
        </div>
      </div>
      {payrollOpen && <PayrollReportModal estimateId={current.id} onClose={() => setPayrollOpen(false)} />}
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Craft / Role</th><th style={styles.th}>Shift</th>
            <th style={styles.th} title="This slot's phase windows from the Labor tab — dates, hours/day, and work-day pattern. Ramp phases staffed for fewer people only show on the slots they cover.">Schedule (phases)</th>
            <th style={styles.th}>Name</th><th style={styles.th}>Email</th><th style={styles.th}>Phone</th>
          </tr></thead>
          <tbody>
            {ordered.map(sec => (
              <Fragment key={sec}>
                <tr><td colSpan={6} style={{ ...styles.td, background: '#F3F4F6', fontWeight: 700, color: '#374151' }}>{sec.toUpperCase()}</td></tr>
                {slots.filter(s => s.section === sec).map(s => {
                  const v = vals[s.key] || {};
                  return (
                    <tr key={s.key}>
                      <td style={styles.td}>
                        <span title={`Labor tab line L${s.line_no} drives this slot — edit rates/phases there.`}
                          style={{ display: 'inline-block', background: '#EEF2FF', color: '#4338CA', fontWeight: 800, fontSize: 11, borderRadius: 4, padding: '1px 6px', marginRight: 6 }}>
                          L{s.line_no}
                        </span>
                        {s.craft}
                      </td>
                      <td style={styles.td}>{s.shift}{s.rotates && <span title="Rotates shifts across phases — see the schedule column" style={{ color: '#B45309', fontWeight: 700 }}> ↔</span>}</td>
                      <td style={{ ...styles.td, fontSize: 11, color: '#374151' }}>
                        {s.phases.length === 0
                          ? <span style={{ color: '#B45309' }} title="Give this line's phases dates + headcount on the Labor tab">no dated phases</span>
                          : s.phases.map((ph, j) => (
                            <div key={j} style={{ whiteSpace: 'nowrap' }}>
                              <strong>{ph.phase}</strong> {md(ph.start_date)} → {md(ph.end_date)}
                              <span style={{ color: '#6B7280' }}> · {Number(ph.hours_per_day) || 0}h/day · {WD_LABEL[ph.work_days || 'all'] || ph.work_days}</span>
                              {s.rotates && <span style={{ color: ph.slotShift === 'Night' ? '#7C3AED' : '#B45309', fontWeight: 700 }}> · {ph.slotShift}</span>}
                            </div>
                          ))}
                      </td>
                      <td style={styles.td}><input list="personnel-roster-names" style={{ ...styles.input, width: 180, fontSize: 12 }} value={v.name || ''} onChange={e => setName(s.key, e.target.value)} placeholder="Full name" /></td>
                      <td style={styles.td}><input style={{ ...styles.input, width: 220, fontSize: 12 }} value={v.email || ''} onChange={e => set(s.key, 'email', e.target.value)} placeholder="email@…" /></td>
                      <td style={styles.td}><input style={{ ...styles.input, width: 140, fontSize: 12 }} value={v.phone || ''} onChange={e => set(s.key, 'phone', e.target.value)} placeholder="phone" /></td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Payroll mobilization report review modal (2026-07-17 — replaces the
// standalone Payroll app's roster intake). Pay rates arrive pre-filled from
// the estimate's labor lines but are EDITABLE here: final pay can differ from
// what was used to estimate (George), so the sender confirms every rate
// before the PDF goes to payroll. Send-time pay is persisted onto the job's
// Allocations rows server-side.
function PayrollReportModal({ estimateId, onClose }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [mobDate, setMobDate] = useState('');
  const [notes, setNotes] = useState('');
  const [cc, setCc] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const d10 = (d) => (d ? String(d).slice(0, 10) : '');

  useEffect(() => {
    api.getPersonnelReport(estimateId)
      .then(d => {
        setData(d);
        setRows((d.rows || []).map(r => ({ ...r, start_date: d10(r.start_date), included: true })));
        setMobDate(d10(d.header && d.header.mob_date));
        setCc(d.sender_email || '');
      })
      .catch(e => setErr(e.message));
  }, [estimateId]);

  const set = (i, field, value) => setRows(prev => prev.map((r, j) => (j === i ? { ...r, [field]: value } : r)));

  // Group sends (George 2026-07-17): crews mobilize in waves and late names
  // change, so only CHECKED rows go in this email — send the advance group
  // now, re-open later and send the bulk once their names firm up.
  const includedRows = rows.filter(r => r.included);
  const allIncluded = rows.length > 0 && includedRows.length === rows.length;
  const toggleAll = () => setRows(prev => prev.map(r => ({ ...r, included: !allIncluded })));

  const doSend = async () => {
    setErr('');
    setSending(true);
    try {
      const r = await api.sendPersonnelReport(estimateId, { rows: includedRows.map(({ included, ...rest }) => rest), mob_date: mobDate || null, notes, cc: cc.trim() || null });
      alert(`Personnel report sent to ${r.sent_to}${r.cc ? ` (cc ${r.cc})` : ''} — ${r.people} employee(s).`
        // A person staffed on more than one labor line is one human to payroll.
        + `${r.merged_duplicates ? `\n${r.merged_duplicates} extra position row(s) merged — payroll gets one row per person.` : ''}`
        + `${r.allocations_updated ? `\nConfirmed pay saved to ${r.allocations_updated} ops crew row(s).` : ''}`);
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSending(false);
    }
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  };
  const h = (data && data.header) || {};
  const num = { ...styles.input, width: 76, fontSize: 12, textAlign: 'right' };
  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...styles.card, width: 980, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ ...styles.h2, marginTop: 0 }}>Personnel Report → Payroll</h2>
        {!data && !err && <p style={{ color: '#6B7280' }}>Loading…</p>}
        {data && (
          <>
            <div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
              <strong>{h.job_number ? `Job ${h.job_number}` : 'No job number yet'}</strong>
              {h.customer ? ` · ${h.customer}` : ''}{h.location ? ` · ${h.location}` : ''}
              <span style={{ color: '#6B7280' }}> — goes to <strong>{data.payroll_email}</strong>, CC <strong>{data.always_cc}</strong>{cc.trim() && cc.trim().toLowerCase() !== (data.always_cc || '').toLowerCase() ? <> + <strong>{cc.trim()}</strong></> : null}</span>
            </div>
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#92400E', marginBottom: 10 }}>
              Confirm each person's <strong>pay and bill rates</strong> before sending — pre-filled from the estimate, and final rates may differ from what was used to bid. Payroll uses these to pay the onsite billable rate while the employee is on this job. What you send here is what payroll gets (and it's saved back onto the ops crew rows). <strong>Only checked rows are included</strong> — crews mobilizing later can be unchecked now and sent as their own report once their names firm up. Rows are ordered by start date onsite.
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12, color: '#374151' }}>Mobilization date{' '}
                <input type="date" style={{ ...styles.input, fontSize: 12 }} value={mobDate} onChange={e => setMobDate(e.target.value)} />
              </label>
              <label style={{ fontSize: 12, color: '#374151' }}>Also CC (optional){' '}
                <input style={{ ...styles.input, fontSize: 12, width: 240 }} value={cc} onChange={e => setCc(e.target.value)} placeholder="you@selectsafetyusa.com" />
              </label>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead><tr>
                  <th style={{ ...styles.th, width: 34 }} title="Only checked people go in this email — uncheck later waves and send them separately when their names firm up">
                    <input type="checkbox" checked={allIncluded} onChange={toggleAll} />
                  </th>
                  <th style={styles.th}>Employee</th><th style={styles.th}>Craft</th><th style={styles.th}>Shift</th>
                  <th style={styles.th}>Start</th><th style={styles.th}>Pay ST</th><th style={styles.th}>Pay OT</th>
                  <th style={styles.th}>Bill ST</th><th style={styles.th}>Bill OT</th>
                  <th style={styles.th}>Per Diem</th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} style={r.included ? {} : { opacity: 0.45 }}>
                      <td style={styles.td}><input type="checkbox" checked={!!r.included} onChange={e => set(i, 'included', e.target.checked)} /></td>
                      <td style={styles.td}>{r.name}</td>
                      <td style={styles.td}>{r.craft}</td>
                      <td style={styles.td}>{r.shift}</td>
                      <td style={styles.td}><input type="date" style={{ ...styles.input, fontSize: 12 }} value={r.start_date || ''} onChange={e => set(i, 'start_date', e.target.value)} /></td>
                      <td style={styles.td}><input style={num} value={r.pay_st ?? ''} onChange={e => set(i, 'pay_st', e.target.value)} placeholder="—" /></td>
                      <td style={styles.td}><input style={num} value={r.pay_ot ?? ''} onChange={e => set(i, 'pay_ot', e.target.value)} placeholder="—" /></td>
                      <td style={styles.td}><input style={num} value={r.bill_st ?? ''} onChange={e => set(i, 'bill_st', e.target.value)} placeholder="—" /></td>
                      <td style={styles.td}><input style={num} value={r.bill_ot ?? ''} onChange={e => set(i, 'bill_ot', e.target.value)} placeholder="—" /></td>
                      <td style={styles.td}><input style={num} value={r.per_diem ?? ''} onChange={e => set(i, 'per_diem', e.target.value)} placeholder="—" /></td>
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan={10} style={{ ...styles.td, color: '#6B7280' }}>No saved personnel with names yet — fill and Save the Personnel tab first.</td></tr>}
                </tbody>
              </table>
            </div>
            <label style={{ display: 'block', fontSize: 12, color: '#374151', marginTop: 10 }}>Notes for payroll (optional)
              <textarea style={{ ...styles.input, width: '100%', minHeight: 50, fontSize: 12, marginTop: 4 }} value={notes} onChange={e => setNotes(e.target.value)} />
            </label>
          </>
        )}
        {err && <p style={{ color: '#B91C1C', fontSize: 13 }}>{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button style={styles.btnSecondary} onClick={onClose} disabled={sending}>Cancel</button>
          <button style={styles.btnSecondary} disabled={sending || !includedRows.length}
            title="Opens the exact PDF payroll would receive — nothing is sent"
            onClick={async () => {
              setErr('');
              try {
                const res = await api.previewPersonnelReport(estimateId, { rows: includedRows.map(({ included, ...rest }) => rest), mob_date: mobDate || null, notes });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Preview failed'); }
                const url = window.URL.createObjectURL(await res.blob());
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 60000);
              } catch (e) { setErr(e.message); }
            }}>
            👁 Preview PDF
          </button>
          <button style={styles.btn} onClick={doSend} disabled={sending || !includedRows.length}>
            {sending ? 'Sending…' : `✉ Send to Payroll (${includedRows.length}${includedRows.length !== rows.length ? ` of ${rows.length}` : ''})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryPanel({ computed, tier }) {
  const rollup = computed.rollupSelected;
  if (!rollup) return null;
  const econ = computed.economics;
  const laborRow = rollup.rows.find(r => r.category === 'Labor');

  return (
    <div style={styles.card}>
      <h2 style={{ ...styles.h2, marginBottom: 12 }}>
        Category Summary — {tier}% Utilization Tier
        {computed.hasBelow && <span style={{ marginLeft: 8, color: ORANGE, fontSize: 14 }}>⚠ Below threshold</span>}
      </h2>

      {/* Deal economics — owner view */}
      {econ && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Man-Hours', value: `${econ.totalHours.toLocaleString()}`,
              sub: `${econ.stHours.toLocaleString()} ST / ${econ.otHours.toLocaleString()} OT`,
              help: 'Total labor hours across every phase at the selected tier.' },
            { label: 'Gross Profit', value: fmtMoney(rollup.total.grossProfit),
              sub: `${pct(rollup.total.margin)} blended margin`,
              help: 'Total GP dollars across the whole deal at the selected tier.' },
            { label: 'Blended Labor Markup', value: laborRow && laborRow.markup != null ? pct(laborRow.markup) : '—',
              sub: laborRow
                ? `${pct(laborRow.margin)} labor margin${laborRow.rawMarkup != null ? ` · unburdened ${pct(laborRow.rawMarkup)}` : ''}`
                : 'no labor lines',
              color: laborRow && laborRow.status === 'OK' ? colors.green : laborRow && laborRow.status === 'BELOW' ? ORANGE : '#1F2937',
              help: 'Markup across all labor: labor Gross Profit ÷ labor (fully-burdened) cost. Profit on top of true cost. Per diem and mobilization are excluded. "Unburdened" is the same revenue measured against the pay/sub rates before our burden — the wage multiplier, display only.' },
            { label: 'Self-Perform', value: econ.selfPerform.margin != null ? pct(econ.selfPerform.margin) : '—',
              sub: `${fmtMoney(econ.selfPerform.grossProfit)} GP on ${fmtMoney(econ.selfPerform.revenue)}`,
              color: econ.selfPerform.margin != null && econ.selfPerform.margin >= (computed.margin_threshold || 0) ? colors.green : ORANGE,
              help: 'Labor + own equipment only — the margin on work SSS actually performs.' },
            { label: 'Pass-Through', value: econ.passThrough.margin != null ? pct(econ.passThrough.margin) : '—',
              sub: `${fmtMoney(econ.passThrough.grossProfit)} GP on ${fmtMoney(econ.passThrough.revenue)}`,
              help: 'Consumables/services, third-party, per diem, and mobilization — resold dollars and reimbursables at thin margin. High pass-through revenue dilutes the blended margin without changing the labor economics.' },
          ].map(card => (
            <div key={card.label} title={card.help} style={{
              flex: 1, minWidth: 150, padding: '10px 14px', borderRadius: 8,
              background: '#F9FAFB', border: '1px solid #E5E7EB', cursor: 'help' }}>
              <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <span>{card.label}</span>
                <span title={card.help} aria-label={`Help: ${card.label}`} style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 16, height: 16, borderRadius: '50%',
                  border: '1px solid #9CA3AF', color: '#6B7280',
                  fontSize: 10, fontWeight: 700, cursor: 'help', flexShrink: 0 }}>?</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: card.color || '#1F2937' }}>{card.value}</div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>{card.sub}</div>
            </div>
          ))}
        </div>
      )}

      <table style={styles.table}>
        <thead><tr>
          <th style={styles.th}>Category</th>
          <th style={{ ...styles.th, textAlign: 'right' }}>Revenue</th>
          <th style={{ ...styles.th, textAlign: 'right' }}>Cost</th>
          <th style={{ ...styles.th, textAlign: 'right' }}>Gross Profit</th>
          <th style={{ ...styles.th, textAlign: 'right' }}>Margin<HelpGlyph help="Margin = Gross Profit ÷ Revenue. Same profit as Markup — Gross Profit is Revenue minus fully-burdened cost; margin divides it by revenue, markup divides it by cost. That denominator is the only difference. Per diem & mobilization are their own rows, so the TOTAL includes everything." /></th>
          <th style={{ ...styles.th, textAlign: 'right' }}>Markup<HelpGlyph help="Markup = Gross Profit ÷ Cost. Same profit as Margin — markup divides it by cost, so it always reads higher than margin. '—' for categories with no cost basis (e.g. owned equipment). The Labor row is the blended labor markup." /></th>
          <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
        </tr></thead>
        <tbody>
          {rollup.rows.filter(row => !row.empty).map(row => (
            <tr key={row.category}>
              <td style={styles.td}>{row.category}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{row.status === 'EMPTY' ? '—' : fmtMoney(row.revenue)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{row.status === 'EMPTY' ? '—' : fmtMoney(row.cost)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{row.status === 'EMPTY' ? '—' : fmtMoney(row.grossProfit)}</td>
              <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: row.status === 'OK' ? colors.green : row.status === 'EMPTY' ? '#9CA3AF' : ORANGE }}>
                {row.status === 'EMPTY' ? '—' : pct(row.margin)}
                {row.rawMargin != null && (
                  <div style={{ fontWeight: 400, fontSize: 10, color: '#9CA3AF' }}
                    title="Unburdened — revenue vs pay/sub rates, before our burden. Display only; the threshold grades the burdened figure above.">
                    unburdened {pct(row.rawMargin)}
                  </div>
                )}
              </td>
              <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: '#6B7280' }}
                title={row.category === 'Labor' ? 'Blended labor markup — labor Gross Profit ÷ labor (burdened) cost.' : 'Gross Profit ÷ Cost'}>
                {row.status === 'EMPTY' || row.markup == null ? '—' : pct(row.markup)}
                {row.rawMarkup != null && (
                  <div style={{ fontWeight: 400, fontSize: 10, color: '#9CA3AF' }}
                    title="Unburdened — revenue vs pay/sub rates, before our burden. The wage multiplier; its numerator still carries the burden we pay out, so it always reads higher.">
                    unburdened {pct(row.rawMarkup)}
                  </div>
                )}
              </td>
              <td style={{ ...styles.td, textAlign: 'center' }}>
                <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                  background: row.status === 'OK' ? '#D1FAE5' : row.status === 'EMPTY' ? '#F3F4F6' : '#FEF3C7',
                  color: row.status === 'OK' ? '#065F46' : row.status === 'EMPTY' ? '#6B7280' : '#92400E' }}>
                  {row.status === 'EMPTY' ? 'No lines' : row.status}
                </span>
              </td>
            </tr>
          ))}
          <tr style={{ background: '#F9FAFB', fontWeight: 700 }}>
            <td style={styles.td}>TOTAL</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoney(rollup.total.revenue)}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoney(rollup.total.cost)}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoney(rollup.total.grossProfit)}</td>
            <td style={{ ...styles.td, textAlign: 'right', color: rollup.total.status === 'OK' ? colors.green : ORANGE }}>
              {pct(rollup.total.margin)}
            </td>
            <td style={{ ...styles.td, textAlign: 'right', color: '#6B7280' }} title="Gross Profit ÷ Cost across the whole deal (burdened).">
              {rollup.total.markup == null ? '—' : pct(rollup.total.markup)}
            </td>
            <td style={{ ...styles.td, textAlign: 'center' }}>
              <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                background: rollup.total.status === 'OK' ? '#D1FAE5' : '#FEF3C7',
                color: rollup.total.status === 'OK' ? '#065F46' : '#92400E' }}>
                {rollup.total.status}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Tier comparison */}
      <h3 style={{ fontSize: 13, fontWeight: 700, marginTop: 20, marginBottom: 8 }}>Revenue at All Tiers</h3>
      <div style={{ display: 'flex', gap: 12 }}>
        {[50, 70, 100].map(t => {
          const r = t === 50 ? computed.rollup50 : t === 100 ? computed.rollup100 : computed.rollup70;
          if (!r) return null;
          const sameAllTiers = computed.rollup50 && computed.rollup100
            && computed.rollup50.total.revenue === computed.rollup100.total.revenue;
          const tierHelp = `Deal totals if utilization equipment bills at ${t}% of its on-rent days. Fixed charges (labor, non-consignment equipment, consumables/services, third-party, per diem, mobilization) bill in full at every tier.${sameAllTiers ? ' No utilization equipment on this estimate, so all three tiers match.' : ''}${t === tier ? ' This is the selected tier used by the Summary and quote.' : ''}`;
          return (
            <div key={t} title={tierHelp} style={{
              flex: 1, padding: 12, borderRadius: 8, textAlign: 'center',
              background: t === tier ? '#FFF7ED' : '#F9FAFB',
              border: `2px solid ${t === tier ? ORANGE : '#E5E7EB'}`, cursor: 'help',
            }}>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <span>{t}% Tier</span>
                <span title={tierHelp} aria-label={`Help: ${t}% tier`} style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 16, height: 16, borderRadius: '50%',
                  border: '1px solid #9CA3AF', color: '#6B7280',
                  fontSize: 10, fontWeight: 700, cursor: 'help', flexShrink: 0 }}>?</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtMoney(r.total.revenue)}</div>
              <div style={{ fontSize: 13, color: r.total.status === 'OK' ? colors.green : ORANGE, fontWeight: 600 }}>
                {fmtMoney(r.total.grossProfit)} GP · {pct(r.total.margin)} margin
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
